// HeadMarkerDashboard.jsx - Dashboard for head marker to review and manage submissions  
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {HelpCircle, ChevronDown, ChevronUp, Plus, Trash2, Edit3, MessageSquare, Send, Check, ThumbsUp, Inbox, FileText, Users, Upload, LifeBuoy, Image as ImageIcon, Bell, Lock, AlertTriangle, Table, RotateCcw} from 'lucide-react';
import { useAuth, changePassword } from './firebase/auth';
import { getSubmissionsAwaitingReview, getMarkedSubmissions, getAllPendingSubmissions, claimSubmission, getPendingSubmissions, getAllClaimedSubmissions, permanentlyDeleteSubmission, resetSubmissionForResubmit, reopenExamSubmission, changeSubmissionHomeworkType, getFlaggedSubmissions, returnFlaggedToMarker, releaseFlaggedToPool, flagSubmissionForReview, getSubmissionsWithPendingAdditions, releasePendingAdditions } from './firebase/submissions';
import { getReviewSettings, updateReviewSettings, getSolutionReleaseSettings, updateSolutionReleaseSettings, getHomeworkNames, updateHomeworkNames, getMarkerCaps, updateMarkerCaps, getMaxMarks, updateMaxMarks, getWhatsAppTemplates, updateWhatsAppTemplates, fillWhatsAppTemplate, getSectionStructures, updateSectionStructures, getDefaultSectionStructures, getEmailVerificationRequired, updateEmailVerificationRequired, getStudentApprovalMode, updateStudentApprovalMode, getQuestionStructures, updateQuestionStructures, getStudentDataWebhookUrl, updateStudentDataWebhookUrl, getSubmissionWebhookUrl, updateSubmissionWebhookUrl, getSubmissionModes, updateSubmissionModes, getExamDurations, updateExamDurations, getExamUnlimitedDurations, updateExamUnlimitedDurations, getExamShowBreakdownScreen, updateExamShowBreakdownScreen, getExamInteractiveSpreadsheetMarking, updateExamInteractiveSpreadsheetMarking, getPdfSpreadsheetAttachmentEnabled, updatePdfSpreadsheetAttachmentEnabled, getExamHelpDocument, updateExamHelpDocument, getExamHelpButtonVisible, updateExamHelpButtonVisible, getSupportAccess, updateSupportAccess, getAiSummaryEnabled, updateAiSummaryEnabled, sortHomeworkTypes, updateHomeworkOrder, getSettingsReadFailureCount } from './firebase/settings';
import { uploadExamMaterial, deleteExamMaterialFile, uploadTaxTablesDocument } from './firebase/examMaterials';
import { getAllSolutions, uploadSolution, deleteSolution } from './firebase/solutions';
import { getAllQuestionPapers, uploadQuestionPaper, deleteQuestionPaper } from './firebase/questionPapers';
import { getAllMarkers, createMarkerAccount, deactivateMarker, reactivateMarker, deleteMarker, sendMarkerPasswordReset, getMarkerStats as getMarkerAccountStats, getMarker } from './firebase/users';
import { getMarkerStats as getMarkerHomeworkStats, getAllMarkerStats } from './firebase/stats';
import { getAllCommentBanks, getPendingSuggestionsCount } from './firebase/comments';
import FirebaseMarker from './FirebaseMarker';
import CohortManagement from './components/CohortManagement';
import CommentBankManager from './components/CommentBankManager';

// Import new view components
import ReviewQueueView from './components/ReviewQueueView';
import AvailableSubmissionsView from './components/AvailableSubmissionsView';
import ClaimedSubmissionsView from './components/ClaimedSubmissionsView';
import FlaggedSubmissionsView from './components/FlaggedSubmissionsView';
import PendingAdditionsView from './components/PendingAdditionsView';
import AddHomeworkModal from './components/AddHomeworkModal';
import RejectSubmissionModal from './components/RejectSubmissionModal';
import FlagForReviewModal from './components/FlagForReviewModal';
import ChangeHomeworkTypeModal from './components/ChangeHomeworkTypeModal';
import UploadOnBehalfModal from './components/UploadOnBehalfModal';
import NotificationSettings from './NotificationSettings';
import useIsMobile from './hooks/useIsMobile';
import ReminderSettingsPanel from './components/ReminderSettingsPanel';
import { goToCourseHosting } from './firebase/crossApp';
import HelpPanel from './components/HelpPanel';
import { CLOUD_FUNCTION_URL, notificationRequest } from './firebase/headMarkerNotifications';
import { DEFAULT_ORG_ID } from './firebase/orgs';
import { getOrgBranding, uploadOrgLogo, removeOrgLogo, updateOrgHeaderColor } from './firebase/orgBranding';
import { getContrastTextColor } from './utils/color';
import { spreadsheetMarkingMode } from './utils/spreadsheetMarkingMode';
import { openExamPopup } from './utils/examPopup';
import ExamRichTextEditor from './components/ExamRichTextEditor';
import ExamSpreadsheetGrid from './components/ExamSpreadsheetGrid';

// Computes a homework's total achievable marks (technical + professional)
// from its section structure, instead of a tutor typing a number that can
// drift out of sync with what the sections/groups actually add up to.
// Mirrors calculateMaxMarks() in FirebaseMarker.jsx (the "out of X" a marker
// sees live while marking) so the configured total can never disagree with
// what markers/students actually see.
function calculateHomeworkTotalMarks(structure) {
  const sections = structure?.sections || [];
  const profGroups = structure?.professionalMarkGroups || [];
  const profGroupedIds = new Set(profGroups.flatMap(g => g.sections));
  const techGroups = structure?.technicalMarkGroups || [];
  const techGroupedIds = new Set(techGroups.flatMap(g => g.sections));
  // An additional-reasons pool only counts once - either on its own, or
  // folded into whichever professional group has includeAdditional set.
  const additionalCountedInGroup = profGroups.some(g => g.includeAdditional);
  const additionalProfessional = additionalCountedInGroup ? 0 : (structure?.additionalProfessionalReasons?.length ?? 0);

  return sections.reduce((sum, s) =>
    sum + (techGroupedIds.has(s.id) ? 0 : (s.maxTechnical ?? s.maxMark ?? 0)) + (profGroupedIds.has(s.id) ? 0 : (s.maxProfessional ?? 1)), 0) +
    techGroups.reduce((sum, g) => sum + g.totalMarks, 0) +
    profGroups.reduce((sum, g) => sum + g.totalMarks, 0) +
    additionalProfessional;
}

// Settings > Homework: one collapsible heading within a homework, closed
// by default, with a short summary so you can see what's set up without
// opening it. The body only renders while open - its state all lives in the
// dashboard, so closing a panel never loses unsaved edits.
function SettingsPanel({ title, description, summary, isOpen, onToggle, nested = false, children }) {
  return (
    <div style={{
      background: nested ? 'white' : '#f8f9fa',
      border: nested ? '2px solid #e2e8f0' : 'none',
      borderRadius: '8px',
      marginBottom: nested ? '12px' : '16px'
    }}>
      <div
        onClick={onToggle}
        role="button"
        aria-expanded={isOpen}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          padding: nested ? '12px 14px' : '16px 20px',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: nested ? '14px' : '16px', fontWeight: nested ? '700' : '600', color: '#2c3e50' }}>{title}</div>
          {description && <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '2px' }}>{description}</div>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          {summary && <span style={{ fontSize: '12px', color: '#7f8c8d', fontWeight: '600', whiteSpace: 'nowrap' }}>{summary}</span>}
          <ChevronDown size={18} style={{ color: '#667eea', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </div>
      </div>
      {isOpen && (
        <div style={{ padding: nested ? '0 14px 14px 14px' : '0 20px 20px 20px' }}>
          {children}
        </div>
      )}
    </div>
  );
}

const countLabel = (items, noun) => `${items.length} ${noun}${items.length === 1 ? '' : 's'}`;

// Rich text editors leave tags like <p></p> behind when emptied
const hasHtmlText = (html) => !!(html || '').replace(/<[^>]*>/g, '').trim();

const examQuestionSummary = (section) => {
  const parts = [];
  if ((section.exhibits || []).length) parts.push(countLabel(section.exhibits, 'exhibit'));
  if ((section.requirements || []).length) parts.push(countLabel(section.requirements, 'requirement'));
  if (section.startingGridJson) parts.push('starting spreadsheet');
  if (hasHtmlText(section.startingHtml)) parts.push('starting text');
  return parts.length ? parts.join(', ') : 'No content yet';
};

// Helper component for managing a list of professional mark reasons
function ProfessionalReasonsEditor({ label, reasons, borderColor, bgColor, onAdd, onRemove }) {
  const [newReason, setNewReason] = React.useState('');
  const [showBulk, setShowBulk] = React.useState(false);
  const [bulkText, setBulkText] = React.useState('');

  const handleBulkAdd = () => {
    const lines = bulkText.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    lines.forEach(line => onAdd(line));
    setBulkText('');
    setShowBulk(false);
  };

  return (
    <div style={{ marginBottom: '16px', padding: '14px', background: 'white', borderRadius: '8px', border: `2px solid ${borderColor}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ fontSize: '13px', fontWeight: '700', color: '#2c3e50' }}>{label}</div>
        <button
          onClick={() => { setShowBulk(!showBulk); setBulkText(''); }}
          style={{ fontSize: '12px', padding: '4px 10px', background: showBulk ? '#e2e8f0' : borderColor, color: showBulk ? '#4a5568' : 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}>
          {showBulk ? 'Cancel Bulk' : 'Bulk Add'}
        </button>
      </div>

      {showBulk && (
        <div style={{ marginBottom: '12px', padding: '12px', background: '#f8f9fa', borderRadius: '8px', border: `1px dashed ${borderColor}` }}>
          <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '8px' }}>
            Paste your list below — one item per line. All lines will be added at once.
          </div>
          <textarea
            autoFocus
            value={bulkText}
            onChange={e => setBulkText(e.target.value)}
            placeholder={'Reason one\nReason two\nReason three'}
            rows={6}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit', resize: 'vertical', marginBottom: '8px' }}
          />
          <button
            onClick={handleBulkAdd}
            disabled={!bulkText.trim()}
            style={{ padding: '7px 16px', background: bulkText.trim() ? borderColor : '#e2e8f0', color: bulkText.trim() ? 'white' : '#95a5a6', border: 'none', borderRadius: '6px', cursor: bulkText.trim() ? 'pointer' : 'not-allowed', fontWeight: '600', fontSize: '13px' }}>
            Add {bulkText.trim() ? bulkText.split('\n').filter(l => l.trim()).length : 0} Items
          </button>
        </div>
      )}

      {reasons.length === 0 && !showBulk && (
        <div style={{ fontSize: '12px', color: '#95a5a6', marginBottom: '8px', fontStyle: 'italic' }}>No reasons added yet.</div>
      )}
      {reasons.map((reason, rIdx) => (
        <div key={rIdx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <div style={{ flex: 1, fontSize: '13px', padding: '6px 10px', background: bgColor, borderRadius: '6px', border: `1px solid ${borderColor}` }}>
            {reason}
          </div>
          <button onClick={() => onRemove(rIdx)}
            style={{ background: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
        <input
          type="text"
          placeholder="Type a reason and press Enter or click +"
          value={newReason}
          onChange={e => setNewReason(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && newReason.trim()) { onAdd(newReason); setNewReason(''); } }}
          style={{ flex: 1, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}
        />
        <button
          onClick={() => { if (newReason.trim()) { onAdd(newReason); setNewReason(''); } }}
          disabled={!newReason.trim()}
          style={{ padding: '7px 12px', background: newReason.trim() ? borderColor : '#e2e8f0', color: newReason.trim() ? 'white' : '#95a5a6', border: 'none', borderRadius: '6px', cursor: newReason.trim() ? 'pointer' : 'not-allowed', fontWeight: '700', fontSize: '16px', lineHeight: 1 }}>
          +
        </button>
      </div>
    </div>
  );
}

// Helper component for managing a list of uploaded Exhibit/Requirement
// documents for one question, or the single global Tax Tables document
function ExamMaterialList({ label, items, onUpload, onDelete, onAddText, uploading }) {
  const inputRef = React.useRef(null);
  const [showTextForm, setShowTextForm] = useState(false);
  const [textTitle, setTextTitle] = useState('');
  const [textContent, setTextContent] = useState('');

  const submitText = () => {
    if (!textContent.trim()) return;
    onAddText(textTitle.trim() || 'Untitled', textContent);
    setTextTitle('');
    setTextContent('');
    setShowTextForm(false);
  };

  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ fontSize: '12px', fontWeight: '700', color: '#7f8c8d', marginBottom: '6px' }}>{label}</div>
      {items.length === 0 && !showTextForm && (
        <div style={{ fontSize: '12px', color: '#95a5a6', marginBottom: '6px', fontStyle: 'italic' }}>None added yet.</div>
      )}
      {items.map(item => (
        <div key={item.id || item.fileName} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <div style={{ flex: 1, fontSize: '13px', padding: '6px 10px', background: '#f0f4ff', borderRadius: '6px', border: '1px solid #c7d2fe', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.title || item.fileName}
            {item.type === 'text' && <span style={{ color: '#95a5a6', fontSize: '11px', marginLeft: '6px' }}>(text)</span>}
          </div>
          <button
            onClick={() => onDelete(item.id)}
            style={{ background: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <Trash2 size={12} />
          </button>
        </div>
      ))}

      {showTextForm ? (
        <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #e2e8f0', borderRadius: '6px', marginTop: '4px' }}>
          <input
            type="text"
            placeholder="Title"
            value={textTitle}
            onChange={(e) => setTextTitle(e.target.value)}
            style={{ width: '100%', marginBottom: '6px', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box' }}
          />
          <textarea
            placeholder="Content shown to the student in a new window..."
            value={textContent}
            onChange={(e) => setTextContent(e.target.value)}
            rows={4}
            style={{ width: '100%', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '13px', fontFamily: 'inherit', resize: 'vertical', marginBottom: '8px', boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={submitText}
              disabled={!textContent.trim()}
              style={{ padding: '6px 12px', background: textContent.trim() ? '#667eea' : '#e2e8f0', color: textContent.trim() ? 'white' : '#95a5a6', border: 'none', borderRadius: '6px', cursor: textContent.trim() ? 'pointer' : 'not-allowed', fontWeight: '600', fontSize: '12px' }}>
              Save
            </button>
            <button
              onClick={() => { setShowTextForm(false); setTextTitle(''); setTextContent(''); }}
              style={{ padding: '6px 12px', background: 'white', color: '#4a5568', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '12px' }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            ref={inputRef}
            type="file"
            accept=".pdf"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onUpload(file);
              e.target.value = '';
            }}
          />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            style={{ padding: '6px 12px', background: uploading ? '#e2e8f0' : 'white', color: '#667eea', border: '1px solid #667eea', borderRadius: '6px', cursor: uploading ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '12px' }}>
            {uploading ? 'Uploading...' : '+ Upload PDF'}
          </button>
          <button
            onClick={() => setShowTextForm(true)}
            style={{ padding: '6px 12px', background: 'white', color: '#1b998b', border: '1px solid #1b998b', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '12px' }}>
            + Add Text
          </button>
        </div>
      )}
    </div>
  );
}

function HeadMarkerDashboard() {
  const { user, logout } = useAuth();
  // Sync to Student Data Sheet (and the per-submission webhook) is Aileen's
  // own Apps Script tracking setup, not general platform tooling - no other
  // org has a matching spreadsheet integration to receive it. CSV export
  // stays available to everyone.
  const isAileenOrg = user && (user.orgId || DEFAULT_ORG_ID) === DEFAULT_ORG_ID;

  // Aileen's org keeps the static logo files below regardless - no need to
  // fetch anything for her.
  useEffect(() => {
    if (!user || isAileenOrg) return;
    getOrgBranding(user.orgId || DEFAULT_ORG_ID).then(b => {
      setOrgLogoUrl(b?.logos?.headMarker?.url || b?.logoUrl || null);
      setOrgHeaderColor(b?.headerColors?.headMarker || null);
      setBranding(b);
    });
  }, [user, isAileenOrg]);
  const [switchingToCourseHosting, setSwitchingToCourseHosting] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const handleSwitchToCourseHosting = async () => {
    setSwitchingToCourseHosting(true);
    try {
      await goToCourseHosting();
    } catch (error) {
      alert(error.message || 'Could not switch to Course Content.');
      setSwitchingToCourseHosting(false);
    }
  };
  const [currentView, setCurrentView] = useState('review');
  const [markersSubView, setMarkersSubView] = useState('activity'); // 'activity' | 'manage'
  const isMobile = useIsMobile();
  const [settingsSubView, setSettingsSubView] = useState('homework'); // homework, comments, whatsapp
  // Which Settings > Homework panels are open, keyed `${homeworkType}:${panelId}`.
  // All closed to start; kept while the dashboard is open, so saving or
  // switching homework doesn't snap them shut
  const [openSettingsPanels, setOpenSettingsPanels] = useState(() => new Set());
  // The database's copy (as JSON) of each setting "Save" writes, to spot
  // unsaved changes. maxMarks isn't tracked - it's derived from
  // sectionStructures, so it only changes when they do
  const [savedSettingsJson, setSavedSettingsJson] = useState({});
  const markSettingsSaved = useCallback((values) => {
    setSavedSettingsJson(prev => {
      const next = { ...prev };
      Object.entries(values).forEach(([key, value]) => { next[key] = JSON.stringify(value); });
      return next;
    });
  }, []);
  const settingsPanelProps = (key) => ({
    isOpen: openSettingsPanels.has(key),
    onToggle: () => setOpenSettingsPanels(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    })
  });
  const [homeworkTypes, setHomeworkTypes] = useState([]);
  const [showAddHomeworkModal, setShowAddHomeworkModal] = useState(false);
  const [newHomeworkCode, setNewHomeworkCode] = useState('');
  const [showUploadOnBehalfModal, setShowUploadOnBehalfModal] = useState(false);
  const [awaitingReview, setAwaitingReview] = useState([]);
  const [flaggedSubmissions, setFlaggedSubmissions] = useState([]);
  const [pendingAdditions, setPendingAdditions] = useState([]);
  const [availableSubmissions, setAvailableSubmissions] = useState([]);
  const [myClaimedSubmissions, setMyClaimedSubmissions] = useState([]);
  const [allClaimedSubmissions, setAllClaimedSubmissions] = useState([]); // All markers' claimed work
  const [activityMarkerFilter, setActivityMarkerFilter] = useState('all');
  const [reviewSettings, setReviewSettings] = useState({});
  const [solutionReleaseSettings, setSolutionReleaseSettings] = useState({}); // Per-homework: wait for deadline (true/unset) vs release as soon as marked (false)
  const [homeworkNames, setHomeworkNames] = useState({});
  const [markerCaps, setMarkerCaps] = useState({});
  const [maxMarks, setMaxMarks] = useState({}); // Item 12: Custom total marks
  const [submissionModes, setSubmissionModes] = useState({}); // Global default: 'pdf' | 'exam' per homework type
  const [examDurations, setExamDurations] = useState({}); // Exam duration (minutes) per homework type
  const [examUnlimitedDurations, setExamUnlimitedDurations] = useState({}); // Homework types with no time limit
  const [examShowBreakdownScreen, setExamShowBreakdownScreen] = useState({}); // Homework types showing the standalone breakdown screen
  const [examInteractiveSpreadsheetMarking, setExamInteractiveSpreadsheetMarking] = useState({}); // Homework types capturing interactive spreadsheet marking data
  const [pdfSpreadsheetAttachmentEnabled, setPdfSpreadsheetAttachmentEnabled] = useState({}); // Homework types offering an optional spreadsheet attachment alongside PDF upload
  const [practiceHomeworkType, setPracticeHomeworkType] = useState(''); // Selected homework for the Practice/Demo Run launcher
  const [examHelpDocument, setExamHelpDocument] = useState(null); // Global Tax Tables / Help document
  const [examHelpButtonVisible, setExamHelpButtonVisible] = useState(true);
  const [savingHelpButtonVisible, setSavingHelpButtonVisible] = useState(false);
  const [uploadingMaterialKey, setUploadingMaterialKey] = useState(null); // Tracks which exhibit/requirement/tax-tables upload is in flight
  const [uploadingSolutionKey, setUploadingSolutionKey] = useState(null); // Tracks which homework type's model solution upload is in flight
  const [uploadingQuestionPaperKey, setUploadingQuestionPaperKey] = useState(null); // Tracks which homework type's question paper upload is in flight
  const [uploadingTaxTables, setUploadingTaxTables] = useState(false);
  const [whatsappTemplates, setWhatsappTemplates] = useState({}); // Item 5: Custom WhatsApp templates
  const [sectionStructures, setSectionStructures] = useState({}); // Item 10: Custom sections
  const [questionStructures, setQuestionStructures] = useState({});
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
  const [studentApprovalMode, setStudentApprovalMode] = useState('none');
  const [aiSummaryEnabled, setAiSummaryEnabled] = useState(false);
  const [savingAiSummary, setSavingAiSummary] = useState(false);
  // Set when a settings read failed and the page is holding fallback
  // defaults instead of the real values - saving is blocked until a reload
  const [mainSettingsLoadFailed, setMainSettingsLoadFailed] = useState(false);
  const [extraSettingsLoadFailed, setExtraSettingsLoadFailed] = useState(false);
  const [supportAccess, setSupportAccess] = useState({ mode: 'always', expiresAt: null });
  const [savingSupportAccess, setSavingSupportAccess] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null); // { type: 'error' | 'success', text }
  const [orgLogoUrl, setOrgLogoUrl] = useState(null);
  const [orgHeaderColor, setOrgHeaderColor] = useState(null);
  // Full branding doc (all three roles) - used by the Branding settings
  // sub-view below, which edits every role's logo/colour, not just this
  // dashboard's own (headMarker's).
  const [branding, setBranding] = useState({ logoUrl: null, logos: {}, headerColors: {} });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [expandedHomework, setExpandedHomework] = useState(null); // Item 10: Accordion state
  const [commentBanks, setCommentBanks] = useState({}); // Item 10: For accordion display
  const [solutions, setSolutions] = useState({}); // Model solution per homework type, for accordion display
  const [questionPapers, setQuestionPapers] = useState({}); // Question paper per homework type, for accordion display
  const [markers, setMarkers] = useState([]); // Item 6: All markers
  const [markerStats, setMarkerStats] = useState({}); // Item 6: Marker statistics
  const [selectedMarker, setSelectedMarker] = useState(null); // Track which marker is selected for detail view
  const [individualMarkerStats, setIndividualMarkerStats] = useState({}); // Individual marker homework counts
  const [markerCohortCounts, setMarkerCohortCounts] = useState(null); // Selected marker's marked scripts per cohort (null = loading)
  const [pendingSuggestions, setPendingSuggestions] = useState(0);
  const [showAddMarkerForm, setShowAddMarkerForm] = useState(false); // Item 6: Add marker form
  const [newMarkerData, setNewMarkerData] = useState({ // Item 6: New marker form data
    name: '',
    email: '',
    role: 'marker'
  });
  const [loading, setLoading] = useState(true);
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [flagComment, setFlagComment] = useState('');
  const [showFlagModal, setShowFlagModal] = useState(null);
  const [showChangeTypeModal, setShowChangeTypeModal] = useState(null);
  const [reviewingSubmission, setReviewingSubmission] = useState(null); // For editing mode
  const [copiedSubmissionId, setCopiedSubmissionId] = useState(null);


  // Student Data Sheet webhook
  const [webhookUrl, setWebhookUrl] = useState('');
  const [submissionWebhookUrl, setSubmissionWebhookUrl] = useState('');
  const [syncing, setSyncing] = useState(false);
const [cohorts, setCohorts] = useState([]);
  const [selectedSyncCohort, setSelectedSyncCohort] = useState('all');

  const loadData = useCallback(async () => {
  setLoading(true);
  try {
    const readFailuresBefore = getSettingsReadFailureCount();
    const [submissions, available, claimed, allClaimed, settings, solutionReleaseData, names, caps, marks, templates, structures, suggestionsCount, modes, durations, unlimitedDurations, showBreakdownScreen, interactiveSpreadsheetMarking, spreadsheetAttachmentEnabled, flagged, additions] = await Promise.all([
  getSubmissionsAwaitingReview(),
  getAllPendingSubmissions(),
  getPendingSubmissions(user.uid),
  getAllClaimedSubmissions(),
  getReviewSettings(),
  getSolutionReleaseSettings(),
  getHomeworkNames(),
  getMarkerCaps(),
  getMaxMarks(),
  getWhatsAppTemplates(),
  getSectionStructures(),
  getPendingSuggestionsCount(),
  getSubmissionModes(),
  getExamDurations(),
  getExamUnlimitedDurations(),
  getExamShowBreakdownScreen(),
  getExamInteractiveSpreadsheetMarking(),
  getPdfSpreadsheetAttachmentEnabled(),
  getFlaggedSubmissions(),
  getSubmissionsWithPendingAdditions()
]);
const settingsReadOk = getSettingsReadFailureCount() === readFailuresBefore;
setMainSettingsLoadFailed(!settingsReadOk);
setAwaitingReview(submissions);
setFlaggedSubmissions(flagged);
setPendingAdditions(additions);
setAvailableSubmissions(available);
setMyClaimedSubmissions(claimed);
setAllClaimedSubmissions(allClaimed);
setReviewSettings(settings);
setSolutionReleaseSettings(solutionReleaseData);
setHomeworkNames(names);
// The tutor's own order if they've set one (Settings > Homework, move
// up/down), otherwise TH1-THn, RH1-RHn, then alphabetical
const sortedTypes = sortHomeworkTypes(Object.keys(names));
setHomeworkTypes(sortedTypes);
setMarkerCaps(caps);
setMaxMarks(marks);
setWhatsappTemplates(templates);
setSectionStructures(structures);
setSubmissionModes(modes);
setExamDurations(durations);
setExamUnlimitedDurations(unlimitedDurations);
setExamShowBreakdownScreen(showBreakdownScreen);
setExamInteractiveSpreadsheetMarking(interactiveSpreadsheetMarking);
setPdfSpreadsheetAttachmentEnabled(spreadsheetAttachmentEnabled);
markSettingsSaved({
  reviewSettings: settings,
  solutionReleaseSettings: solutionReleaseData,
  homeworkNames: names,
  markerCaps: caps,
  whatsappTemplates: templates,
  sectionStructures: structures,
  submissionModes: modes,
  examDurations: durations,
  examUnlimitedDurations: unlimitedDurations,
  examShowBreakdownScreen: showBreakdownScreen,
  examInteractiveSpreadsheetMarking: interactiveSpreadsheetMarking,
  pdfSpreadsheetAttachmentEnabled: spreadsheetAttachmentEnabled
});

// ✅ DATA MIGRATION: Ensure all sections have professionalReasons arrays (once per session)
    // Skipped if the read failed - structures would be the fallback defaults
    if (settingsReadOk && !sessionStorage.getItem('structuresMigrated')) {
      let needsUpdate = false;
      const migratedStructures = {};

      Object.keys(structures).forEach(hwType => {
        const structure = structures[hwType];
        const migratedStructure = { ...structure };

        if (!Array.isArray(migratedStructure.additionalProfessionalReasons)) {
          migratedStructure.additionalProfessionalReasons = [];
          needsUpdate = true;
        }

        if (migratedStructure.sections) {
          migratedStructure.sections = migratedStructure.sections.map(section => {
            if (!Array.isArray(section.professionalReasons)) {
              needsUpdate = true;
              return { ...section, professionalReasons: [] };
            }
            return section;
          });
        }

        migratedStructures[hwType] = migratedStructure;
      });

      if (needsUpdate) {
        await updateSectionStructures(migratedStructures);
        setSectionStructures(migratedStructures);
        markSettingsSaved({ sectionStructures: migratedStructures });
      }
      sessionStorage.setItem('structuresMigrated', '1');
    }

setPendingSuggestions(suggestionsCount);
    } catch (error) {
      console.error('Error loading data:', error);
      setMainSettingsLoadFailed(true);
      alert('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [user, markSettingsSaved]);

  useEffect(() => {
    // Only load data when user is available
    if (user?.uid) {
      loadData();
    }
    
    // Load webhook URL from Firestore so it's shared across devices/browsers.
    // One-time migration: if this browser has an older localStorage value but
    // Firestore has none yet, push it up so it becomes the shared value.
    (async () => {
      try {
        const firestoreUrl = await getStudentDataWebhookUrl();
        if (firestoreUrl) {
          setWebhookUrl(firestoreUrl);
        } else {
          const legacyUrl = localStorage.getItem('studentDataWebhookUrl');
          if (legacyUrl) {
            setWebhookUrl(legacyUrl);
            await updateStudentDataWebhookUrl(legacyUrl);
          }
        }
      } catch (err) {
        console.error('Error loading student data webhook URL:', err);
      }
    })();

    (async () => {
      try {
        setSubmissionWebhookUrl(await getSubmissionWebhookUrl());
      } catch (err) {
        console.error('Error loading submission webhook URL:', err);
      }
    })();
  }, [user, loadData]);

  // Item 6: Load markers when markers view is opened
  useEffect(() => {
    if (currentView === 'markers') {
      loadMarkers();
    }
  }, [currentView]);

  // Lazy-load settings-only data when settings tab is first opened
  const [settingsDataLoaded, setSettingsDataLoaded] = useState(false);
  useEffect(() => {
    if (currentView === 'settings' && !settingsDataLoaded) {
      (async () => {
        try {
          const readFailuresBefore = getSettingsReadFailureCount();
          const [banks, emailVerifRequired, questionStructuresData, helpDocument, supportAccessData, aiSummaryEnabledData, solutionsData, questionPapersData, approvalMode, helpButtonVisible] = await Promise.all([
            getAllCommentBanks(),
            getEmailVerificationRequired(),
            getQuestionStructures(),
            getExamHelpDocument(),
            getSupportAccess(),
            getAiSummaryEnabled(),
            getAllSolutions(),
            getAllQuestionPapers(),
            getStudentApprovalMode(),
            getExamHelpButtonVisible()
          ]);
          setCommentBanks(banks);
          setEmailVerificationRequired(emailVerifRequired);
          setStudentApprovalMode(approvalMode);
          setQuestionStructures(questionStructuresData);
          setExamHelpDocument(helpDocument);
          setExamHelpButtonVisible(helpButtonVisible);
          setSupportAccess(supportAccessData);
          setAiSummaryEnabled(aiSummaryEnabledData);
          setSolutions(solutionsData);
          setQuestionPapers(questionPapersData);
          // Left unloaded on a failed read, so reopening Settings tries again
          const settingsReadOk = getSettingsReadFailureCount() === readFailuresBefore;
          setExtraSettingsLoadFailed(!settingsReadOk);
          if (settingsReadOk) {
            markSettingsSaved({
              emailVerificationRequired: emailVerifRequired,
              studentApprovalMode: approvalMode,
              questionStructures: questionStructuresData
            });
            setSettingsDataLoaded(true);
          }
        } catch (e) {
          console.error('Error loading settings data:', e);
          setExtraSettingsLoadFailed(true);
        }
      })();
    }
  }, [currentView, settingsDataLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Total Marks is no longer typed in manually - it's derived from each
  // homework's section structure (technical + professional marks, groups,
  // additional reasons). Keep maxMarks in lockstep with sectionStructures so
  // "Save All Settings" always persists the calculated value, matching what
  // markers see live while marking (FirebaseMarker.jsx's calculateMaxMarks).
  useEffect(() => {
    setMaxMarks(prev => {
      let changed = false;
      const updated = { ...prev };
      homeworkTypes.forEach(type => {
        const computed = calculateHomeworkTotalMarks(sectionStructures[type]);
        if (updated[type] !== computed) {
          updated[type] = computed;
          changed = true;
        }
      });
      return changed ? updated : prev;
    });
  }, [homeworkTypes, sectionStructures]);

// Load cohorts for sync dropdown
  useEffect(() => {
    const loadCohorts = async () => {
      try {
        console.log('Attempting to load cohorts...');
        const { getAllCohorts } = await import('./firebase/cohorts');
        console.log('Import succeeded, calling getAllCohorts...');
        const allCohorts = await getAllCohorts();
        console.log('Cohorts loaded:', allCohorts);
        setCohorts(allCohorts);
      } catch (err) {
        console.error('Failed to load cohorts for sync:', err);
      }
    };
    loadCohorts();
  }, []);


  const handleReject = async (submissionId) => {
    if (!rejectionNotes.trim()) {
      alert('Please provide feedback for the marker');
      return;
    }

    try {
      const submission = awaitingReview.find(s => s.id === submissionId);
      const { rejectSubmission } = await import('./firebase/submissions');
      await rejectSubmission(submissionId, rejectionNotes);

      // Send rejection email to the marker
      if (submission?.assignedTo) {
        try {
          const markerProfile = await getMarker(submission.assignedTo);
          if (markerProfile?.email) {
            await fetch(CLOUD_FUNCTION_URL, await notificationRequest({
                type: 'submission_rejected',
                markerEmail: markerProfile.email,
                markerName: submission.markerName || markerProfile.name,
                submissionData: {
                  studentName: submission.studentName,
                  homeworkType: homeworkNames[submission.homeworkType] || submission.homeworkType,
                  rejectionNotes
                }
              }));
          }
        } catch (emailErr) {
          console.error('Failed to send rejection email:', emailErr);
        }
      }

      alert('Marking returned to the marker with your feedback. An email has been sent to notify them.');
      setShowRejectModal(null);
      setRejectionNotes('');
      setAwaitingReview(prev => prev.filter(s => s.id !== submissionId));
    } catch (error) {
      console.error('Reject error:', error);
      alert('Failed to reject submission');
    }
  };

  const handleReturnFlaggedToMarker = async (submissionId) => {
    try {
      const submission = flaggedSubmissions.find(s => s.id === submissionId);
      await returnFlaggedToMarker(submissionId);

      // Notify the marker their flagged submission is back with them
      if (submission?.flaggedBy) {
        try {
          const markerProfile = await getMarker(submission.flaggedBy);
          if (markerProfile?.email) {
            await fetch(CLOUD_FUNCTION_URL, await notificationRequest({
                type: 'submission_flag_returned',
                markerEmail: markerProfile.email,
                markerName: submission.flaggedByName || markerProfile.name,
                submissionData: {
                  studentName: submission.studentName,
                  homeworkType: homeworkNames[submission.homeworkType] || submission.homeworkType
                }
              }));
          }
        } catch (emailErr) {
          console.error('Failed to send flag-returned email:', emailErr);
        }
      }

      setFlaggedSubmissions(prev => prev.filter(s => s.id !== submissionId));
    } catch (error) {
      console.error('Return flagged submission error:', error);
      alert('Failed to return submission to the marker');
    }
  };

  const handleReleaseFlaggedToPool = async (submissionId) => {
    if (!window.confirm('Release this submission to the general available pool? Any marker will be able to claim it.')) return;
    try {
      await releaseFlaggedToPool(submissionId);
      setFlaggedSubmissions(prev => prev.filter(s => s.id !== submissionId));
      const released = flaggedSubmissions.find(s => s.id === submissionId);
      if (released) {
        setAvailableSubmissions(prev => [{ ...released, status: 'pending', assignedTo: null, markerName: null }, ...prev]);
      }
    } catch (error) {
      console.error('Release flagged submission error:', error);
      alert('Failed to release submission to the pool');
    }
  };

  const handleReleasePendingAdditions = async (submissionId) => {
    try {
      await releasePendingAdditions(submissionId);
      setPendingAdditions(prev => prev.filter(s => s.id !== submissionId));
      // releasePendingAdditions sets status to 'flagged_for_review' - refresh
      // that list so it shows up there immediately for the return-to-marker step
      const flagged = await getFlaggedSubmissions();
      setFlaggedSubmissions(flagged);
    } catch (error) {
      console.error('Release pending additions error:', error);
      alert('Failed to release pending additions');
    }
  };

  // Save writes every setting on the page back, so it must only run once
  // they've all loaded - otherwise placeholder defaults overwrite real values
  const settingsLoadFailed = mainSettingsLoadFailed || extraSettingsLoadFailed;
  const canSaveSettings = settingsDataLoaded && !settingsLoadFailed;

  // Everything "Save" writes, for spotting unsaved changes
  const saveAllSettings = useMemo(() => ({
    reviewSettings, solutionReleaseSettings, homeworkNames, markerCaps, whatsappTemplates,
    sectionStructures, emailVerificationRequired, studentApprovalMode, questionStructures,
    submissionModes, examDurations, examUnlimitedDurations, examShowBreakdownScreen,
    examInteractiveSpreadsheetMarking, pdfSpreadsheetAttachmentEnabled
  }), [reviewSettings, solutionReleaseSettings, homeworkNames, markerCaps, whatsappTemplates,
    sectionStructures, emailVerificationRequired, studentApprovalMode, questionStructures,
    submissionModes, examDurations, examUnlimitedDurations, examShowBreakdownScreen,
    examInteractiveSpreadsheetMarking, pdfSpreadsheetAttachmentEnabled]);
  const hasUnsavedSettings = useMemo(() =>
    Object.entries(saveAllSettings).some(([key, value]) =>
      key in savedSettingsJson && JSON.stringify(value) !== savedSettingsJson[key]),
  [saveAllSettings, savedSettingsJson]);

  // Browser's own "Leave site? Changes you made may not be saved" prompt
  useEffect(() => {
    if (!hasUnsavedSettings) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedSettings]);

  const handleUpdateSettings = async () => {
    if (!canSaveSettings) {
      alert(settingsLoadFailed
        ? 'Your settings could not be loaded, so saving now could overwrite them. Please refresh the page and try again.'
        : 'Your settings are still loading - please wait a moment and try again.');
      return;
    }
    try {
      await Promise.all([
        updateReviewSettings(reviewSettings),
        updateSolutionReleaseSettings(solutionReleaseSettings),
        updateHomeworkNames(homeworkNames),
        updateMarkerCaps(markerCaps),
        updateMaxMarks(maxMarks),
        updateWhatsAppTemplates(whatsappTemplates),
        updateSectionStructures(sectionStructures),
        updateEmailVerificationRequired(emailVerificationRequired),
        updateStudentApprovalMode(studentApprovalMode),
        updateQuestionStructures(questionStructures),
        updateSubmissionModes(submissionModes),
        updateExamDurations(examDurations),
        updateExamUnlimitedDurations(examUnlimitedDurations),
        updateExamShowBreakdownScreen(examShowBreakdownScreen),
        updateExamInteractiveSpreadsheetMarking(examInteractiveSpreadsheetMarking),
        updatePdfSpreadsheetAttachmentEnabled(pdfSpreadsheetAttachmentEnabled)
      ]);
      markSettingsSaved(saveAllSettings);
      alert('Settings updated successfully!');
    } catch (error) {
      console.error('Update settings error:', error);
      alert('Failed to update settings');
    }
  };

  // Saves immediately on click rather than batching into "Save All Settings"
  // above - this is a security-relevant toggle, so it shouldn't be possible
  // to change it and then leave the tab without realizing it wasn't saved.
  const handleSetSupportAccess = async (mode) => {
    setSavingSupportAccess(true);
    try {
      const expiresAt = mode === 'temporary' ? new Date(Date.now() + 24 * 60 * 60 * 1000) : null;
      await updateSupportAccess({ mode, expiresAt });
      setSupportAccess({ mode, expiresAt });
    } catch (error) {
      console.error('Update support access error:', error);
      alert('Failed to update support access');
    } finally {
      setSavingSupportAccess(false);
    }
  };

  // Also saves immediately - it used to wait for "Save All Settings", so
  // flipping it and leaving the page silently left it as it was
  const handleToggleAiSummary = async () => {
    const next = !aiSummaryEnabled;
    setAiSummaryEnabled(next);
    setSavingAiSummary(true);
    try {
      await updateAiSummaryEnabled(next);
    } catch (error) {
      console.error('Update AI summary setting error:', error);
      setAiSummaryEnabled(!next);
      alert('Failed to update the AI summary setting');
    } finally {
      setSavingAiSummary(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage(null);

    if (!passwordForm.current || !passwordForm.next || !passwordForm.confirm) {
      setPasswordMessage({ type: 'error', text: 'Please fill in all fields.' });
      return;
    }
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    if (passwordForm.next.length < 6) {
      setPasswordMessage({ type: 'error', text: 'New password must be at least 6 characters long.' });
      return;
    }

    setChangingPassword(true);
    try {
      await changePassword(passwordForm.current, passwordForm.next);
      setPasswordForm({ current: '', next: '', confirm: '' });
      setPasswordMessage({ type: 'success', text: 'Password changed successfully.' });
    } catch (error) {
      setPasswordMessage({ type: 'error', text: error.message });
    } finally {
      setChangingPassword(false);
    }
  };

  // This dashboard's own header shows the headMarker-role logo, falling back
  // to the legacy/default one - same fallback chain as the initial fetch
  // above. Re-derive it from what the edit just changed, rather than
  // re-fetching, so the header updates instantly.
  const handleUploadLogo = async (file, role = 'default') => {
    if (!file) return;
    setLogoError('');
    setUploadingLogo(true);
    try {
      const url = await uploadOrgLogo(user.orgId || DEFAULT_ORG_ID, file, role);
      if (role === 'default') {
        setBranding(prev => ({ ...prev, logoUrl: url }));
        if (!branding.logos?.headMarker?.url) setOrgLogoUrl(url);
      } else {
        setBranding(prev => ({ ...prev, logos: { ...prev.logos, [role]: { url } } }));
        if (role === 'headMarker') setOrgLogoUrl(url);
      }
    } catch (error) {
      console.error('Upload logo error:', error);
      setLogoError(error.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async (role = 'default') => {
    setUploadingLogo(true);
    try {
      await removeOrgLogo(user.orgId || DEFAULT_ORG_ID, role);
      if (role === 'default') {
        setBranding(prev => ({ ...prev, logoUrl: null }));
        if (!branding.logos?.headMarker?.url) setOrgLogoUrl(null);
      } else {
        setBranding(prev => ({ ...prev, logos: { ...prev.logos, [role]: { url: null } } }));
        if (role === 'headMarker') setOrgLogoUrl(branding.logoUrl || null);
      }
    } catch (error) {
      console.error('Remove logo error:', error);
      setLogoError(error.message || 'Failed to remove logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleUpdateHeaderColor = async (role, color) => {
    setLogoError('');
    setBranding(prev => ({ ...prev, headerColors: { ...prev.headerColors, [role]: color || null } }));
    if (role === 'headMarker') setOrgHeaderColor(color || null);
    try {
      await updateOrgHeaderColor(user.orgId || DEFAULT_ORG_ID, role, color || null);
    } catch (error) {
      console.error('Update header colour error:', error);
      setLogoError(error.message || 'Failed to update header colour');
    }
  };

  const toggleReviewSetting = (homeworkType) => {
    setReviewSettings({
      ...reviewSettings,
      [homeworkType]: !reviewSettings[homeworkType]
    });
  };

  // solutionReleaseSettings[homeworkType] === false means release as soon as
  // marked; true or unset (the original behavior) means wait for the
  // cohort's deadline too - see StudentDashboard.jsx's gating logic.
  const setSolutionWaitsForDeadline = (homeworkType, waitsForDeadline) => {
    setSolutionReleaseSettings({
      ...solutionReleaseSettings,
      [homeworkType]: waitsForDeadline
    });
  };

  const updateHomeworkName = (homeworkType, newName) => {
    setHomeworkNames({
      ...homeworkNames,
      [homeworkType]: newName
    });
  };

  const updateMarkerCap = (homeworkType, cap) => {
    setMarkerCaps({
      ...markerCaps,
      [homeworkType]: cap === '' ? null : parseInt(cap, 10)
    });
  };

  const updateSubmissionMode = (homeworkType, mode) => {
    setSubmissionModes({
      ...submissionModes,
      [homeworkType]: mode
    });
  };

  const updateExamDuration = (homeworkType, minutes) => {
    setExamDurations({
      ...examDurations,
      [homeworkType]: minutes === '' ? 90 : parseInt(minutes, 10)
    });
  };

  const toggleExamUnlimited = (homeworkType) => {
    setExamUnlimitedDurations({
      ...examUnlimitedDurations,
      [homeworkType]: !examUnlimitedDurations[homeworkType]
    });
  };

  const toggleExamShowBreakdownScreen = (homeworkType) => {
    setExamShowBreakdownScreen({
      ...examShowBreakdownScreen,
      [homeworkType]: !examShowBreakdownScreen[homeworkType]
    });
  };

  const setExamSpreadsheetMarkingMode = (homeworkType, mode) => {
    setExamInteractiveSpreadsheetMarking({
      ...examInteractiveSpreadsheetMarking,
      [homeworkType]: mode === 'pdf' ? false : mode
    });
  };

  const togglePdfSpreadsheetAttachmentEnabled = (homeworkType) => {
    setPdfSpreadsheetAttachmentEnabled({
      ...pdfSpreadsheetAttachmentEnabled,
      [homeworkType]: !pdfSpreadsheetAttachmentEnabled[homeworkType]
    });
  };

  const updateExamSummary = (homeworkType, text) => {
    setSectionStructures(prev => ({
      ...prev,
      [homeworkType]: { ...(prev[homeworkType] || { sections: [] }), examSummary: text }
    }));
  };

  const updateSectionBreakdown = (homeworkType, sectionId, text) => {
    setSectionStructures(prev => {
      const structure = prev[homeworkType] || { sections: [] };
      return {
        ...prev,
        [homeworkType]: {
          ...structure,
          sections: structure.sections.map(s => s.id === sectionId ? { ...s, breakdown: text } : s)
        }
      };
    });
  };

  // Separate, optional text for the standalone Breakdown Screen (only shown
  // when examShowBreakdownScreen is on for this homework type) - independent
  // of updateSectionBreakdown above, which feeds the split screen's QUESTION
  // INFORMATION pane. Leaving this blank falls back to that same text - see
  // ExamQuestionBreakdown.jsx.
  const updateSectionBreakdownScreenText = (homeworkType, sectionId, text) => {
    setSectionStructures(prev => {
      const structure = prev[homeworkType] || { sections: [] };
      return {
        ...prev,
        [homeworkType]: {
          ...structure,
          sections: structure.sections.map(s => s.id === sectionId ? { ...s, breakdownScreenText: text } : s)
        }
      };
    });
  };

  // Same batched-into-"Save Settings" pattern as updateSectionBreakdown above
  // (not an immediate persist like the exhibits/requirements uploads - this
  // is plain Firestore data, not a Storage file that could otherwise end up
  // orphaned). Consumed at exam-session creation time by
  // functions/index.js's createOrResumeExamSession/createPracticeExamSession
  // to seed a spreadsheet question's starting grid instead of a blank one -
  // passing null/undefined here (via the "Clear" button) reverts to blank.
  const updateSectionStartingGrid = (homeworkType, sectionId, gridJson) => {
    setSectionStructures(prev => {
      const structure = prev[homeworkType] || { sections: [] };
      return {
        ...prev,
        [homeworkType]: {
          ...structure,
          // Clearing the starting grid (gridJson falsy, via the "Clear"
          // button) also turns off "include blank as well" - that toggle
          // only makes sense alongside a prepopulated grid, so it shouldn't
          // silently survive and reappear pre-checked if data is added back.
          sections: structure.sections.map(s => s.id === sectionId
            ? { ...s, startingGridJson: gridJson, ...(gridJson ? {} : { includeBlankSpreadsheet: false }) }
            : s)
        }
      };
    });
  };

  // "Include blank spreadsheet as well" - when on, students get a second,
  // independent, always-blank spreadsheet alongside the prepopulated one
  // (see functions/index.js's initialAnswers seeding). Only meaningful once
  // a starting grid exists, so the checkbox itself is only shown then.
  const updateSectionIncludeBlankSpreadsheet = (homeworkType, sectionId, value) => {
    setSectionStructures(prev => {
      const structure = prev[homeworkType] || { sections: [] };
      return {
        ...prev,
        [homeworkType]: {
          ...structure,
          sections: structure.sections.map(s => s.id === sectionId ? { ...s, includeBlankSpreadsheet: value } : s)
        }
      };
    });
  };

  // Same pattern as updateSectionStartingGrid above, for the Word Processor
  // panel's starting text instead of the Spreadsheet panel's starting grid.
  const updateSectionStartingHtml = (homeworkType, sectionId, html) => {
    setSectionStructures(prev => {
      const structure = prev[homeworkType] || { sections: [] };
      return {
        ...prev,
        [homeworkType]: {
          ...structure,
          sections: structure.sections.map(s => s.id === sectionId
            ? { ...s, startingHtml: html, ...(html ? {} : { includeBlankWordProcessor: false }) }
            : s)
        }
      };
    });
  };

  const updateSectionIncludeBlankWordProcessor = (homeworkType, sectionId, value) => {
    setSectionStructures(prev => {
      const structure = prev[homeworkType] || { sections: [] };
      return {
        ...prev,
        [homeworkType]: {
          ...structure,
          sections: structure.sections.map(s => s.id === sectionId ? { ...s, includeBlankWordProcessor: value } : s)
        }
      };
    });
  };

  // Purely a UI convenience (which sections have their starting-grid editor
  // open) - unrelated to whether that section actually has data, so opening
  // it never implicitly creates a starting grid.
  const [expandedStartingGrids, setExpandedStartingGrids] = useState(() => new Set());
  const toggleStartingGridExpanded = (key) => {
    setExpandedStartingGrids(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // A combined question's own starting content, same rules as the section
  // updaters above: clearing starting data also turns off its "include blank
  // as well" toggle
  const updateQuestionGroupContent = (homeworkType, groupIndex, changes) => {
    setSectionStructures(prev => {
      const structure = prev[homeworkType] || { sections: [] };
      return {
        ...prev,
        [homeworkType]: {
          ...structure,
          groups: (structure.groups || []).map((g, i) => i === groupIndex ? { ...g, ...changes } : g)
        }
      };
    });
  };
  const groupStartingContentSetters = (homeworkType, groupIndex) => ({
    setGrid: (gridJson) => updateQuestionGroupContent(homeworkType, groupIndex, { startingGridJson: gridJson || null, ...(gridJson ? {} : { includeBlankSpreadsheet: false }) }),
    setHtml: (html) => updateQuestionGroupContent(homeworkType, groupIndex, { startingHtml: html, ...(html ? {} : { includeBlankWordProcessor: false }) }),
    setIncludeBlankSpreadsheet: (value) => updateQuestionGroupContent(homeworkType, groupIndex, { includeBlankSpreadsheet: value }),
    setIncludeBlankWordProcessor: (value) => updateQuestionGroupContent(homeworkType, groupIndex, { includeBlankWordProcessor: value })
  });

  // Same UI-only convenience as expandedStartingGrids above, for the Word
  // Processor panel's starting text editor.
  const [expandedStartingTexts, setExpandedStartingTexts] = useState(() => new Set());
  const toggleStartingTextExpanded = (key) => {
    setExpandedStartingTexts(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Starting Spreadsheet Data / Starting Word Processor Text editors, for an
  // individual question (a section) or a combined question (a group).
  // setters: { setGrid, setHtml, setIncludeBlankSpreadsheet, setIncludeBlankWordProcessor }
  const renderStartingContentEditors = (item, expandKey, setters) => (
    <>
      {/* Starting Spreadsheet Data - every question's Responses sidebar always offers a Spreadsheet panel, so this applies to all of them, not just some "spreadsheet mode" subset */}
      {(() => {
        const gridKey = expandKey;
        const isGridExpanded = expandedStartingGrids.has(gridKey);
        return (
          <div style={{ marginTop: '14px' }}>
            <div
              onClick={() => toggleStartingGridExpanded(gridKey)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', padding: '8px 10px', background: '#f8f9fa', borderRadius: '6px' }}
            >
              <div>
                <div style={{ fontWeight: '600', fontSize: '13px', color: '#2c3e50' }}>Starting Spreadsheet Data</div>
                <div style={{ fontSize: '11px', color: '#7f8c8d' }}>Optional. Students see this pre-entered in their Spreadsheet panel instead of a blank grid.</div>
              </div>
              <span style={{ fontSize: '12px', color: item.startingGridJson ? '#1b998b' : '#7f8c8d', fontWeight: '600', whiteSpace: 'nowrap', marginLeft: '12px' }}>
                {item.startingGridJson ? 'Data added' : 'Blank'} · {isGridExpanded ? 'Hide' : 'Edit'}
              </span>
            </div>
            {isGridExpanded && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ height: '480px', border: '2px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  <ExamSpreadsheetGrid
                    value={item.startingGridJson || ''}
                    onChange={(gridJson) => setters.setGrid(gridJson)}
                  />
                </div>
                {item.startingGridJson && (
                  <>
                    <button
                      onClick={() => setters.setGrid(null)}
                      style={{ marginTop: '8px', padding: '8px 14px', background: 'white', color: '#e74c3c', border: '2px solid #e74c3c', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '12px' }}
                    >
                      Clear Starting Data (revert to blank)
                    </button>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px', padding: '10px', background: '#f8f9fa', border: '2px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        checked={!!item.includeBlankSpreadsheet}
                        onChange={(e) => setters.setIncludeBlankSpreadsheet(e.target.checked)}
                      />
                      <span>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#2c3e50' }}>Include blank spreadsheet as well</span>
                        <div style={{ fontSize: '11px', color: '#7f8c8d' }}>
                          Student gets both this prepopulated spreadsheet AND a separate blank one to use if they choose - both are included in the PDF sent for marking.
                        </div>
                      </span>
                    </label>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Starting Word Processor Text - same pattern as Starting Spreadsheet Data above, for the Word Processor panel */}
      {(() => {
        const textKey = expandKey;
        const isTextExpanded = expandedStartingTexts.has(textKey);
        return (
          <div style={{ marginTop: '14px' }}>
            <div
              onClick={() => toggleStartingTextExpanded(textKey)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', padding: '8px 10px', background: '#f8f9fa', borderRadius: '6px' }}
            >
              <div>
                <div style={{ fontWeight: '600', fontSize: '13px', color: '#2c3e50' }}>Starting Word Processor Text</div>
                <div style={{ fontSize: '11px', color: '#7f8c8d' }}>Optional. Students see this pre-entered in their Word Processor panel instead of a blank page.</div>
              </div>
              <span style={{ fontSize: '12px', color: item.startingHtml ? '#1b998b' : '#7f8c8d', fontWeight: '600', whiteSpace: 'nowrap', marginLeft: '12px' }}>
                {item.startingHtml ? 'Text added' : 'Blank'} · {isTextExpanded ? 'Hide' : 'Edit'}
              </span>
            </div>
            {isTextExpanded && (
              <div style={{ marginTop: '8px' }}>
                <ExamRichTextEditor
                  value={item.startingHtml || ''}
                  onChange={(html) => setters.setHtml(html)}
                  placeholder="Text the student will see already entered..."
                />
                {item.startingHtml && (
                  <>
                    <button
                      onClick={() => setters.setHtml('')}
                      style={{ marginTop: '8px', padding: '8px 14px', background: 'white', color: '#e74c3c', border: '2px solid #e74c3c', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '12px' }}
                    >
                      Clear Starting Text (revert to blank)
                    </button>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px', padding: '10px', background: '#f8f9fa', border: '2px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        checked={!!item.includeBlankWordProcessor}
                        onChange={(e) => setters.setIncludeBlankWordProcessor(e.target.checked)}
                      />
                      <span>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#2c3e50' }}>Include blank word processor as well</span>
                        <div style={{ fontSize: '11px', color: '#7f8c8d' }}>
                          Student gets both this prepopulated text AND a separate blank word processor to use if they choose - both are included in the PDF sent for marking.
                        </div>
                      </span>
                    </label>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })()}
    </>
  );


  // Exhibits/Requirements upload directly persists (not deferred to the main
  // Save Settings button) so a head marker never ends up with a file sitting
  // in Storage without its Firestore reference saved. listKey 'groups' puts
  // the item on a combined question instead of a section (sectionId is then
  // the group's id - also used as that file's Storage folder)
  const handleUploadExamMaterial = async (homeworkType, sectionId, kind, file, listKey = 'sections') => {
    const key = `${homeworkType}-${sectionId}-${kind}`;
    setUploadingMaterialKey(key);
    try {
      const item = await uploadExamMaterial(homeworkType, sectionId, kind, file);
      const structure = sectionStructures[homeworkType] || { sections: [] };
      const updatedStructure = {
        ...structure,
        [listKey]: (structure[listKey] || []).map(s => s.id === sectionId
          ? { ...s, [kind]: [...(s[kind] || []), item] }
          : s)
      };
      const updatedAll = { ...sectionStructures, [homeworkType]: updatedStructure };
      setSectionStructures(updatedAll);
      await updateSectionStructures(updatedAll);
      markSettingsSaved({ sectionStructures: updatedAll });
    } catch (error) {
      console.error('Upload exam material error:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingMaterialKey(null);
    }
  };

  const handleDeleteExamMaterial = async (homeworkType, sectionId, kind, itemId, listKey = 'sections') => {
    const structure = sectionStructures[homeworkType] || { sections: [] };
    const section = (structure[listKey] || []).find(s => s.id === sectionId);
    const item = (section?.[kind] || []).find(i => i.id === itemId);
    if (!item) return;
    if (!window.confirm(`Delete "${item.title}"?`)) return;

    try {
      if (item.filePath) {
        await deleteExamMaterialFile(item.filePath);
      }
      const updatedStructure = {
        ...structure,
        [listKey]: (structure[listKey] || []).map(s => s.id === sectionId
          ? { ...s, [kind]: (s[kind] || []).filter(i => i.id !== itemId) }
          : s)
      };
      const updatedAll = { ...sectionStructures, [homeworkType]: updatedStructure };
      setSectionStructures(updatedAll);
      await updateSectionStructures(updatedAll);
      markSettingsSaved({ sectionStructures: updatedAll });
    } catch (error) {
      console.error('Delete exam material error:', error);
      alert('Failed to delete file. Please try again.');
    }
  };

  const handleAddExamMaterialText = async (homeworkType, sectionId, kind, title, content, listKey = 'sections') => {
    const item = { id: String(Date.now()), title: title || 'Untitled', type: 'text', content };
    const structure = sectionStructures[homeworkType] || { sections: [] };
    const updatedStructure = {
      ...structure,
      [listKey]: (structure[listKey] || []).map(s => s.id === sectionId
        ? { ...s, [kind]: [...(s[kind] || []), item] }
        : s)
    };
    const updatedAll = { ...sectionStructures, [homeworkType]: updatedStructure };
    setSectionStructures(updatedAll);
    try {
      await updateSectionStructures(updatedAll);
      markSettingsSaved({ sectionStructures: updatedAll });
    } catch (error) {
      console.error('Add exam material text error:', error);
      alert('Failed to save. Please try again.');
    }
  };

  // Same immediate-persist pattern as the exhibits/requirements uploads above.
  const handleUploadSolution = async (homeworkType, file) => {
    setUploadingSolutionKey(homeworkType);
    try {
      const solution = await uploadSolution(homeworkType, file);
      setSolutions(prev => ({ ...prev, [homeworkType]: solution }));
    } catch (error) {
      console.error('Upload solution error:', error);
      alert('Failed to upload solution. Please try again.');
    } finally {
      setUploadingSolutionKey(null);
    }
  };

  const handleDeleteSolution = async (homeworkType) => {
    const solution = solutions[homeworkType];
    if (!solution) return;
    if (!window.confirm(`Delete the model solution for ${homeworkType}?`)) return;
    try {
      await deleteSolution(homeworkType);
      setSolutions(prev => ({ ...prev, [homeworkType]: null }));
    } catch (error) {
      console.error('Delete solution error:', error);
      alert('Failed to delete solution. Please try again.');
    }
  };

  // Same immediate-persist pattern as the model solution upload above.
  const handleUploadQuestionPaper = async (homeworkType, file) => {
    setUploadingQuestionPaperKey(homeworkType);
    try {
      const questionPaper = await uploadQuestionPaper(homeworkType, file);
      setQuestionPapers(prev => ({ ...prev, [homeworkType]: questionPaper }));
    } catch (error) {
      console.error('Upload question paper error:', error);
      alert('Failed to upload question paper. Please try again.');
    } finally {
      setUploadingQuestionPaperKey(null);
    }
  };

  const handleDeleteQuestionPaper = async (homeworkType) => {
    const questionPaper = questionPapers[homeworkType];
    if (!questionPaper) return;
    if (!window.confirm(`Delete the question paper for ${homeworkType}?`)) return;
    try {
      await deleteQuestionPaper(homeworkType);
      setQuestionPapers(prev => ({ ...prev, [homeworkType]: null }));
    } catch (error) {
      console.error('Delete question paper error:', error);
      alert('Failed to delete question paper. Please try again.');
    }
  };

  // Practice / Demo Run launcher. fresh: discard any unfinished practice run
  // of this homework and build a new one from its current content (see
  // createPracticeExamSession).
  const openPracticeRun = (fresh) => {
    const params = new URLSearchParams({
      homeworkType: practiceHomeworkType,
      homeworkName: homeworkNames[practiceHomeworkType] || practiceHomeworkType,
      practice: '1'
    });
    if (fresh) params.set('fresh', '1');
    openExamPopup(`/exam?${params.toString()}`, 'examPractice', { widthPct: 0.95, heightPct: 0.92 });
  };

  const handleUploadTaxTables = async (file) => {
    setUploadingTaxTables(true);
    try {
      const document = await uploadTaxTablesDocument(file);
      await updateExamHelpDocument(document);
      setExamHelpDocument(document);
    } catch (error) {
      console.error('Upload tax tables error:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingTaxTables(false);
    }
  };

  // Saves immediately, like support access
  const handleToggleHelpButtonVisible = async () => {
    const visible = !examHelpButtonVisible;
    setSavingHelpButtonVisible(true);
    try {
      await updateExamHelpButtonVisible(visible);
      setExamHelpButtonVisible(visible);
    } catch (error) {
      console.error('Update help button visibility error:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setSavingHelpButtonVisible(false);
    }
  };

  const handleDeleteTaxTables = async () => {
    if (!examHelpDocument) return;
    if (!window.confirm('Delete the global Tax Tables / Help document? This will remove it for every exam-mode homework.')) return;
    try {
      await deleteExamMaterialFile(examHelpDocument.filePath);
      await updateExamHelpDocument(null);
      setExamHelpDocument(null);
    } catch (error) {
      console.error('Delete tax tables error:', error);
      alert('Failed to delete file. Please try again.');
    }
  };

  const updateWhatsAppTemplate = (homeworkType, template) => {
    setWhatsappTemplates({
      ...whatsappTemplates,
      [homeworkType]: template
    });
  };

  const addQuestion = (homeworkType) => {
    const questions = questionStructures[homeworkType] || [];
    setQuestionStructures({
      ...questionStructures,
      [homeworkType]: [...questions, { id: `q_${Date.now()}`, label: '' }]
    });
  };

  const removeQuestion = (homeworkType, questionId) => {
    const questions = questionStructures[homeworkType] || [];
    setQuestionStructures({
      ...questionStructures,
      [homeworkType]: questions.filter(q => q.id !== questionId)
    });
  };

  const updateQuestionLabel = (homeworkType, questionId, label) => {
    const questions = questionStructures[homeworkType] || [];
    setQuestionStructures({
      ...questionStructures,
      [homeworkType]: questions.map(q => q.id === questionId ? { ...q, label } : q)
    });
  };

  // Sections added from 2026-10-11 get a final-comment question of the same
  // name, linked by sectionId. The link follows the section's renames (while
  // the question still has the section's name - edit it and it stops), id
  // changes and removal. Questions set up before this have no sectionId, so
  // nothing here ever touches them.
  const questionForSection = (section, index = 0) => ({
    id: `q_${Date.now()}_${index}`,
    label: section.name,
    sectionId: section.id
  });

  const updateLinkedQuestions = (homeworkType, update) => {
    setQuestionStructures(prev => ({
      ...prev,
      [homeworkType]: (prev[homeworkType] || []).flatMap(q => (q.sectionId === undefined ? [q] : update(q)))
    }));
  };

  // Item 10: Section Structure Management Functions
  const updateSectionId = (homeworkType, sectionIndex, newId) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType])); // Deep clone
    const oldId = structure.sections[sectionIndex].id;
    structure.sections[sectionIndex].id = newId;
    setSectionStructures({
      ...sectionStructures,
      [homeworkType]: structure
    });
    updateLinkedQuestions(homeworkType, q => [q.sectionId === oldId ? { ...q, sectionId: newId } : q]);
  };

  const updateSectionName = (homeworkType, sectionIndex, newName) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType])); // Deep clone
    const { id, name: oldName } = structure.sections[sectionIndex];
    structure.sections[sectionIndex].name = newName;
    setSectionStructures({
      ...sectionStructures,
      [homeworkType]: structure
    });
    updateLinkedQuestions(homeworkType, q => [q.sectionId === id && q.label === oldName ? { ...q, label: newName } : q]);
  };

  const updateSectionMaxTechnical = (homeworkType, sectionIndex, newVal) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    structure.sections[sectionIndex].maxTechnical = Math.round((parseFloat(newVal) || 0) * 2) / 2;
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  const updateSectionMaxProfessional = (homeworkType, sectionIndex, newVal) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    structure.sections[sectionIndex].maxProfessional = parseInt(newVal, 10) || 0;
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  const updateSectionMaxMark = (homeworkType, sectionIndex, newMaxMark) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    structure.sections[sectionIndex].maxMark = parseInt(newMaxMark, 10) || 0;
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  const addProfessionalReason = (homeworkType, sectionIndex, reason) => {
    if (!reason.trim()) return;
    setSectionStructures(prev => {
      const structure = JSON.parse(JSON.stringify(prev[homeworkType]));
      if (!structure.sections[sectionIndex].professionalReasons) {
        structure.sections[sectionIndex].professionalReasons = [];
      }
      structure.sections[sectionIndex].professionalReasons.push(reason.trim());
      return { ...prev, [homeworkType]: structure };
    });
  };

  const removeProfessionalReason = (homeworkType, sectionIndex, reasonIndex) => {
    setSectionStructures(prev => {
      const structure = JSON.parse(JSON.stringify(prev[homeworkType]));
      structure.sections[sectionIndex].professionalReasons.splice(reasonIndex, 1);
      return { ...prev, [homeworkType]: structure };
    });
  };

  const addAdditionalProfessionalReason = (homeworkType, reason) => {
    if (!reason.trim()) return;
    setSectionStructures(prev => {
      const structure = JSON.parse(JSON.stringify(prev[homeworkType]));
      if (!structure.additionalProfessionalReasons) structure.additionalProfessionalReasons = [];
      structure.additionalProfessionalReasons.push(reason.trim());
      return { ...prev, [homeworkType]: structure };
    });
  };

  const removeAdditionalProfessionalReason = (homeworkType, reasonIndex) => {
    setSectionStructures(prev => {
      const structure = JSON.parse(JSON.stringify(prev[homeworkType]));
      structure.additionalProfessionalReasons.splice(reasonIndex, 1);
      return { ...prev, [homeworkType]: structure };
    });
  };

  // Combine sections into one question for the student (exam mode only) -
  // separate from professional/technical mark groups above, which only
  // affect how markers cap marks. This affects what the student sees: a
  // combined screen with one shared Word Processor/Spreadsheet response.
  // Marking itself is unaffected either way - see src/utils/examGroups.js.
  const addQuestionGroup = (homeworkType) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    if (!structure.groups) structure.groups = [];
    structure.groups.push({ id: `qg_${Date.now()}`, name: '', sectionIds: [] });
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  const updateQuestionGroupName = (homeworkType, groupIndex, value) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    structure.groups[groupIndex].name = value;
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  // A combined question can carry its own single breakdown covering the
  // whole question. When set, it replaces the per-section breakdowns in the
  // split screen's QUESTION INFORMATION pane entirely (see ExamSplitScreen.jsx) -
  // the per-section breakdowns below are left untouched either way, so
  // clearing this text reverts to showing them again.
  const updateQuestionGroupBreakdown = (homeworkType, groupIndex, value) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    structure.groups[groupIndex].breakdown = value;
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  // Same idea, but for the standalone Breakdown Screen only (see
  // ExamQuestionBreakdown.jsx) - independent of updateQuestionGroupBreakdown
  // above. Leaving this blank falls back to the whole-question breakdown
  // above, or each part's own Breakdown Screen text.
  const updateQuestionGroupBreakdownScreenText = (homeworkType, groupIndex, value) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    structure.groups[groupIndex].breakdownScreenText = value;
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  // Per-question opt-out of the Breakdown Screen (see
  // ExamWindowApp.jsx's screenForGroup) - lets one question skip its own
  // intro screen (e.g. it shares content with the question before it)
  // while other questions in the same homework type still get one.
  // Undefined counts as shown, so this flips the effective value on click.
  const toggleQuestionGroupShowBreakdownScreen = (homeworkType, groupIndex) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    const group = structure.groups[groupIndex];
    group.showBreakdownScreen = !(group.showBreakdownScreen !== false);
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  const toggleSectionInQuestionGroup = (homeworkType, groupIndex, sectionId) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    const group = structure.groups[groupIndex];
    if (group.sectionIds.includes(sectionId)) {
      group.sectionIds = group.sectionIds.filter(s => s !== sectionId);
    } else {
      group.sectionIds.push(sectionId);
    }
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  const removeQuestionGroup = (homeworkType, groupIndex) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    structure.groups.splice(groupIndex, 1);
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  const addProfessionalMarkGroup = (homeworkType) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    if (!structure.professionalMarkGroups) structure.professionalMarkGroups = [];
    structure.professionalMarkGroups.push({ name: '', totalMarks: 1, sections: [] });
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  const updateGroupName = (homeworkType, groupIndex, value) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    structure.professionalMarkGroups[groupIndex].name = value;
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  const updateGroupTotalMarks = (homeworkType, groupIndex, value) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    structure.professionalMarkGroups[groupIndex].totalMarks = parseInt(value, 10) || 1;
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  const toggleSectionInGroup = (homeworkType, groupIndex, sectionId) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    const group = structure.professionalMarkGroups[groupIndex];
    if (group.sections.includes(sectionId)) {
      group.sections = group.sections.filter(s => s !== sectionId);
    } else {
      group.sections.push(sectionId);
    }
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  const removeProfessionalMarkGroup = (homeworkType, groupIndex) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    structure.professionalMarkGroups.splice(groupIndex, 1);
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  const toggleGroupIncludesAdditional = (homeworkType, groupIndex) => {
    setSectionStructures(prev => {
      const structure = JSON.parse(JSON.stringify(prev[homeworkType]));
      const groups = structure.professionalMarkGroups;
      const enabling = !groups[groupIndex].includeAdditional;
      // Only one group may include the additional professional marks pool at a time
      groups.forEach((g, i) => { g.includeAdditional = i === groupIndex ? enabling : false; });
      return { ...prev, [homeworkType]: structure };
    });
  };

  const addTechnicalMarkGroup = (homeworkType) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    if (!structure.technicalMarkGroups) structure.technicalMarkGroups = [];
    structure.technicalMarkGroups.push({ name: '', totalMarks: 1, sections: [] });
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  const updateTechnicalGroupName = (homeworkType, groupIndex, value) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    structure.technicalMarkGroups[groupIndex].name = value;
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  const updateTechnicalGroupTotalMarks = (homeworkType, groupIndex, value) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    structure.technicalMarkGroups[groupIndex].totalMarks = Math.round((parseFloat(value) || 0) * 2) / 2 || 0.5;
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  const toggleSectionInTechnicalGroup = (homeworkType, groupIndex, sectionId) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    const group = structure.technicalMarkGroups[groupIndex];
    if (group.sections.includes(sectionId)) {
      group.sections = group.sections.filter(s => s !== sectionId);
    } else {
      group.sections.push(sectionId);
    }
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  const removeTechnicalMarkGroup = (homeworkType, groupIndex) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType]));
    structure.technicalMarkGroups.splice(groupIndex, 1);
    setSectionStructures({ ...sectionStructures, [homeworkType]: structure });
  };

  const addSection = (homeworkType) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType])); // Deep clone
    // First letter not already in use - counting sections reused a letter
    // after one was removed (A, C left after removing B gave a second C)
    const usedIds = new Set(structure.sections.map(s => s.id));
    const nextLetter = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'].find(letter => !usedIds.has(letter)) || String.fromCharCode(65 + structure.sections.length);
    const newSection = {
      id: nextLetter,
      name: `Section ${nextLetter}`,
      maxTechnical: 5,
      maxProfessional: 1,
      professionalReasons: []
    };
    structure.sections.push(newSection);
    setSectionStructures({
      ...sectionStructures,
      [homeworkType]: structure
    });
    setQuestionStructures(prev => ({
      ...prev,
      [homeworkType]: [...(prev[homeworkType] || []), questionForSection(newSection)]
    }));
  };

  const removeSection = (homeworkType, sectionIndex) => {
    const structure = JSON.parse(JSON.stringify(sectionStructures[homeworkType])); // Deep clone
    if (structure.sections.length <= 1) {
      alert('Cannot remove the last section');
      return;
    }
    const [removed] = structure.sections.splice(sectionIndex, 1);
    setSectionStructures({
      ...sectionStructures,
      [homeworkType]: structure
    });
    updateLinkedQuestions(homeworkType, q => (q.sectionId === removed.id ? [] : [q]));
  };

  const resetToDefaultSections = (homeworkType) => {
    if (!window.confirm(`Reset ${homeworkType} to default structure (A, B, C)?`)) {
      return;
    }
    const defaults = getDefaultSectionStructures();
    setSectionStructures({
      ...sectionStructures,
      [homeworkType]: defaults[homeworkType]
    });
  };

  // Add new homework type
  const handleAddHomework = () => {
    if (!newHomeworkCode.trim()) {
      alert('Please enter a homework code');
      return;
    }
    
    const code = newHomeworkCode.trim().toUpperCase();
    
    if (homeworkTypes.includes(code)) {
      alert('This homework code already exists');
      return;
    }
    
    setHomeworkTypes([...homeworkTypes, code]);
    
    // Initialize default settings for new homework
    const defaults = getDefaultSectionStructures();
    setSectionStructures({
      ...sectionStructures,
      [code]: defaults['TH1'] // Use TH1 as default template
    });
    setQuestionStructures(prev => ({
      ...prev,
      [code]: prev[code] || defaults['TH1'].sections.map(questionForSection)
    }));
    
    setShowAddHomeworkModal(false);
    setNewHomeworkCode('');
    alert(`Homework ${code} added successfully!`);
  };

  // Move a homework up or down the list - saved straight away, and used for
  // the order everywhere homework is listed (including students' dropdown)
  const handleMoveHomework = async (homeworkType, direction) => {
    const from = homeworkTypes.indexOf(homeworkType);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= homeworkTypes.length) return;
    const next = [...homeworkTypes];
    [next[from], next[to]] = [next[to], next[from]];
    setHomeworkTypes(next);
    try {
      await updateHomeworkOrder(next);
    } catch (error) {
      console.error('Save homework order error:', error);
      setHomeworkTypes(homeworkTypes);
      alert('Could not save the new order. Please try again.');
    }
  };

  // Delete homework type
  const handleDeleteHomework = async (homeworkType) => {
    if (!window.confirm(`Are you sure you want to delete ${homeworkType}?\n\nThis will remove all settings for this homework but will NOT delete any student submissions.`)) {
      return;
    }

    setHomeworkTypes(homeworkTypes.filter(ht => ht !== homeworkType));
    setExpandedHomework(null);

    // Also remove from homeworkNames and save to Firestore immediately
    const newNames = { ...homeworkNames };
    delete newNames[homeworkType];
    setHomeworkNames(newNames);
    await updateHomeworkNames(newNames);
    markSettingsSaved({ homeworkNames: newNames });

    alert(`${homeworkType} has been removed.`);
  };

  // Claim submission for marking
  const handleClaimSubmission = async (submission) => {
    try {
      await claimSubmission(
        submission.id,
        user.uid,
        user.name || user.email,
        submission.homeworkType,
        null // No cap for head markers
      );
      
      alert('Submission claimed! You can now mark it.');
      setAvailableSubmissions(prev => prev.filter(s => s.id !== submission.id));
      setMyClaimedSubmissions(prev => [...prev, { ...submission, status: 'marking', assignedTo: user.uid, markerName: user.name || user.email }]);
    } catch (error) {
      console.error('Claim error:', error);
      alert(error.message || 'Failed to claim submission');
    }
  };

  // Format date helper
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Item 6: Marker Management Functions
const loadMarkers = async () => {
    try {
      console.log('=== LOADING MARKERS ===');
      const [markersData, accountStats, homeworkStats] = await Promise.all([
        getAllMarkers(),
        getMarkerAccountStats(),
        getAllMarkerStats()
      ]);
      console.log('Markers loaded:', markersData.length, markersData);
      console.log('Marker account stats loaded:', accountStats);
      console.log('Marker homework stats loaded:', homeworkStats);
      setMarkers(markersData);
      setMarkerStats({ ...accountStats, homeworkStats });
      console.log('======================');
    } catch (error) {
      console.error('Failed to load markers:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      alert(`Failed to load markers: ${error.message}\n\nCheck browser console for details.`);
    }
  };

  const handleMarkerClick = async (marker) => {
    if (selectedMarker?.id === marker.id) {
      // Click on same marker - deselect
      setSelectedMarker(null);
      setIndividualMarkerStats({});
      setMarkerCohortCounts(null);
    } else {
      // Load stats for this marker
      setSelectedMarker(marker);
      loadMarkerCohortCounts(marker.id);
      try {
        // Import the new functions
        const { getHeadMarkerViewStats, getMarkerResetAuditLog } = await import('./firebase/stats');
        
        // Load HEAD MARKER'S independent view of stats (not affected by marker resets)
        const stats = await getHeadMarkerViewStats(marker.id);
        
        // Load permanent audit log showing all resets
        const auditLog = await getMarkerResetAuditLog(marker.id);
        
        // Combine stats with audit log
        setIndividualMarkerStats({ ...stats, auditLog });
        
        console.log('Loaded head marker view stats and audit log:', marker.name);
      } catch (error) {
        console.error('Failed to load marker stats:', error);
        alert('Failed to load marker statistics');
      }
    }
  };

  const handleDeleteResetLogEntry = async (entryId) => {
    if (!selectedMarker) return;
    if (!window.confirm('Delete this reset history entry? This cannot be undone.')) return;

    try {
      const { deleteResetLogEntry } = await import('./firebase/stats');
      await deleteResetLogEntry(selectedMarker.id, entryId);
      setIndividualMarkerStats(prev => ({
        ...prev,
        auditLog: (prev.auditLog || []).filter(entry => entry.id !== entryId)
      }));
    } catch (error) {
      console.error('Failed to delete reset log entry:', error);
      alert('Failed to delete entry. Please try again.');
    }
  };

  const loadMarkerCohortCounts = async (markerId) => {
    setMarkerCohortCounts(null);
    try {
      const { getMarkerCountsByCohort } = await import('./firebase/stats');
      setMarkerCohortCounts(await getMarkerCountsByCohort(markerId));
    } catch (error) {
      console.error('Failed to load marker counts by cohort:', error);
      setMarkerCohortCounts([]);
    }
  };

  const handleRefreshMarkerStats = async () => {
    if (!selectedMarker) return;
    loadMarkerCohortCounts(selectedMarker.id);
    
    try {
      const { getHeadMarkerViewStats, getMarkerResetAuditLog } = await import('./firebase/stats');
      const stats = await getHeadMarkerViewStats(selectedMarker.id);
      const auditLog = await getMarkerResetAuditLog(selectedMarker.id);
      setIndividualMarkerStats({ ...stats, auditLog });
      alert('Statistics refreshed!');
    } catch (error) {
      console.error('Failed to refresh marker stats:', error);
      alert('Failed to refresh statistics');
    }
  };

const handleResetHeadMarkerView = async () => {
    if (!selectedMarker) return;
    
    const confirmReset = window.confirm(
      `Reset YOUR view of ${selectedMarker.name}'s statistics?\n\nThis will:\ Save current counts to permanent audit log\ Reset YOUR view to zero\ NOT affect ${selectedMarker.name}'s own view\ Keep a permanent record in the audit log\n\nAre you sure?`
    );
    
    if (!confirmReset) return;
    
    try {
      const { resetHeadMarkerViewStats, getHeadMarkerViewStats, getMarkerResetAuditLog } = await import('./firebase/stats');
      await resetHeadMarkerViewStats(selectedMarker.id, user.uid);
      
      // Reload stats to show updated view (handleMarkerClick would close the
      // panel, since this marker is already selected)
      const [stats, auditLog] = await Promise.all([
        getHeadMarkerViewStats(selectedMarker.id),
        getMarkerResetAuditLog(selectedMarker.id)
      ]);
      setIndividualMarkerStats({ ...stats, auditLog });
      
      alert('Your view has been reset and logged in audit history.\n\nThe marker\'s own view is unchanged.');
    } catch (error) {
      console.error('Failed to reset head marker view:', error);
      alert('Failed to reset statistics');
    }
  };

  const handleAddMarker = async (e) => {
    e.preventDefault();

    if (!newMarkerData.name || !newMarkerData.email) {
      alert('Please fill in all fields');
      return;
    }

    try {
      // Created server-side (Cloud Function) so this browser's own session
      // - including a Support Access session - is never touched or signed out.
      // No password is chosen here - the account is created password-less and
      // the Cloud Function returns a one-time "set your password" link for it.
      const { resetLink } = await createMarkerAccount(newMarkerData);

      // Send that link directly to the new marker
      let emailSent = false;
      try {
        const emailResponse = await fetch(CLOUD_FUNCTION_URL, await notificationRequest({
            type: 'new_marker_credentials',
            markerEmail: newMarkerData.email,
            markerName: newMarkerData.name,
            resetLink
          }));
        emailSent = emailResponse.ok;
      } catch (emailError) {
        console.error('Failed to send credentials email:', emailError);
      }

      alert(
        `Marker account created for ${newMarkerData.name}!\n\n` +
        (emailSent
          ? `An email has been sent to ${newMarkerData.email} with a link to set their password and log in.`
          : `Note: The credentials email could not be sent.\n\nEmail: ${newMarkerData.email}\nSet-password link: ${resetLink}\n\nPlease share this link manually - it's single-use.`)
      );
      setShowAddMarkerForm(false);
      setNewMarkerData({ name: '', email: '', role: 'marker' });
      loadMarkers();
    } catch (error) {
      alert(`Failed to create marker account:\n\n${error.message}`);
    }
  };

  const handleToggleMarkerStatus = async (marker) => {
    try {
      if (marker.active) {
        await deactivateMarker(marker.id);
        alert(`${marker.name} has been deactivated`);
      } else {
        await reactivateMarker(marker.id);
        alert(`${marker.name} has been reactivated`);
      }
      loadMarkers();
    } catch (error) {
      alert('Failed to update marker status');
    }
  };

  const handleDeleteMarker = async (marker) => {
    const typed = window.prompt(
      `This will PERMANENTLY delete ${marker.name}'s account, statistics and personal comment banks. This cannot be undone.\n\nTheir past marking is kept, but will show as "Former marker" instead of their name.\n\nType the marker's name to confirm:`
    );
    if (typed === null) return; // cancelled
    if (typed.trim() !== (marker.name || '').trim()) {
      alert('Name did not match. Deletion cancelled.');
      return;
    }
    try {
      await deleteMarker(marker.id);
      alert(`${marker.name} has been deleted`);
      loadMarkers();
    } catch (error) {
      alert(`Failed to delete marker:\n\n${error.message}`);
    }
  };

  const handleSendPasswordReset = async (marker) => {
    if (!window.confirm(`Send password reset email to ${marker.name}?\n\nEmail: ${marker.email}`)) {
      return;
    }
    
    try {
      await sendMarkerPasswordReset(marker.email);
      alert(`Password reset email sent to ${marker.email}`);
    } catch (error) {
      alert(`Failed to send password reset email:\n\n${error.message}`);
    }
  };

  // Shared by both "Sync to Student Data Sheet" and "Download as CSV" -
  // fetches the selected cohort's students + marked submissions, matches,
  // deduplicates (highest score wins), and returns the same row shape both
  // destinations need. Throws on error - callers handle their own try/catch.
  const buildStudentDataRows = async (cohortChoice = selectedSyncCohort) => {
    const { getAllCohorts, getCohortStudents } = await import('./firebase/cohorts');
    const allCohorts = await getAllCohorts();
    const cohortMap = {};
    allCohorts.forEach(c => { cohortMap[c.id] = c.name; });

    // Step 1: Get students based on selected cohort(s) - uses CURRENT cohort membership
    let studentsToSync = []; // { id, name, cohortId, cohortName }

    if (cohortChoice === 'all') {
      // Get students from all active cohorts
      for (const cohort of allCohorts.filter(c => c.active !== false)) {
        const students = await getCohortStudents(cohort.id);
        students.forEach(s => {
          studentsToSync.push({
            id: s.id,
            name: s.name,
            cohortId: cohort.id,
            cohortName: cohort.name
          });
        });
      }
    } else {
      // Get students from selected cohort only
      const cohortName = cohortMap[cohortChoice] || cohortChoice;
      const students = await getCohortStudents(cohortChoice);
      students.forEach(s => {
        studentsToSync.push({
          id: s.id,
          name: s.name,
          cohortId: cohortChoice,
          cohortName: cohortName
        });
      });
    }

    console.log(`Found ${studentsToSync.length} students in selected cohort(s)`);

    if (studentsToSync.length === 0) {
      throw new Error('NO_STUDENTS');
    }

    // Build a set of student IDs for quick lookup
    const studentIdSet = new Set(studentsToSync.map(s => s.id));
    // Map studentId -> { name, cohortName } for current cohort info
    const studentMap = {};
    studentsToSync.forEach(s => {
      studentMap[s.id] = { name: s.name, cohortName: s.cohortName, cohortId: s.cohortId };
    });

    // Step 2: Get all marked submissions and match by studentId
    const markedSubmissions = await getMarkedSubmissions();
    console.log(`Total marked submissions: ${markedSubmissions.length}`);

    // Filter submissions to only those from students currently in selected cohort(s)
    const matchedSubmissions = markedSubmissions
      .filter(sub => sub.studentId && studentIdSet.has(sub.studentId) && sub.cohortId === studentMap[sub.studentId].cohortId)
      .map(sub => {
        const student = studentMap[sub.studentId];
        return {
          name: student.name,
          cohort: student.cohortName, // Use CURRENT cohort, not submission's cohortId
          homework: sub.homeworkType,
          score: sub.score?.total?.toFixed(1) || '0',
          scoreNum: sub.score?.total || 0,
          daysLate: (sub.minutesLate || 0) / 1440
        };
      });

    console.log(`Matched ${matchedSubmissions.length} submissions to current cohort students`);

    // Step 3: Deduplicate - keep highest score per student/homework
    const deduplicatedSubmissions = [];
    const seenKeys = new Map();
    let duplicatesFound = 0;

    matchedSubmissions.forEach(submission => {
      const key = `${submission.name}|${submission.cohort}|${submission.homework}`;

      if (seenKeys.has(key)) {
        const existingIndex = seenKeys.get(key);
        const existingScore = deduplicatedSubmissions[existingIndex].scoreNum;
        const newScore = submission.scoreNum;

        if (newScore > existingScore) {
          console.log(`Duplicate: ${submission.name} - ${submission.homework}: Keeping ${newScore} (was ${existingScore})`);
          deduplicatedSubmissions[existingIndex] = submission;
        }
        duplicatesFound++;
      } else {
        seenKeys.set(key, deduplicatedSubmissions.length);
        deduplicatedSubmissions.push(submission);
      }
    });

    if (duplicatesFound > 0) {
      console.log(`Found ${duplicatesFound} duplicate(s) - kept highest scores`);
    }

    // Step 4: Build final list - include ALL students, even those with no submissions
    const studentsWithSubmissions = new Set(deduplicatedSubmissions.map(s => `${s.name}|${s.cohort}`));
    const finalSubmissions = deduplicatedSubmissions.map(sub => ({
      name: sub.name,
      cohort: sub.cohort,
      homework: sub.homework,
      score: sub.score,
      daysLate: sub.daysLate
    }));

    // Add placeholder entries for students with no submissions so they appear in the sheet
    let studentsWithoutHomework = 0;
    studentsToSync.forEach(student => {
      const key = `${student.name}|${student.cohortName}`;
      if (!studentsWithSubmissions.has(key)) {
        finalSubmissions.push({
          name: student.name,
          cohort: student.cohortName,
          homework: '',
          score: ''
        });
        studentsWithoutHomework++;
      }
    });

    if (studentsWithoutHomework > 0) {
      console.log(`Added ${studentsWithoutHomework} students with no submissions`);
    }

    const cohortLabel = cohortChoice === 'all' ? 'all cohorts' : cohortMap[cohortChoice];

    return { finalSubmissions, studentsToSync, duplicatesFound, studentsWithoutHomework, cohortLabel };
  };

  const handleSyncToStudentData = async (cohortChoice = selectedSyncCohort) => {
    if (!webhookUrl.trim()) {
      alert('Please add the Student Data Sheet webhook URL first, in Settings > Integrations.');
      setCurrentView('settings');
      setSettingsSubView('integrations');
      return;
    }

    setSyncing(true);
    try {
      const { finalSubmissions, studentsToSync, duplicatesFound, studentsWithoutHomework, cohortLabel } = await buildStudentDataRows(cohortChoice);

      // Send to Google Sheet
      await fetch(webhookUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ students: finalSubmissions })
      });

      console.log(`Synced ${finalSubmissions.length} entries for ${cohortLabel}`);

      alert(`Successfully synced to Student Data Sheet!\n\n` +
        `Cohort: ${cohortLabel}\n` +
        `Students: ${studentsToSync.length}\n` +
        `Submissions: ${finalSubmissions.length - studentsWithoutHomework}\n` +
        `Students with no homework: ${studentsWithoutHomework}\n` +
        (duplicatesFound > 0 ? `Duplicates resolved: ${duplicatesFound}\n` : '') +
        `\nYour engagement sheet can now pull the data.`);

    } catch (error) {
      console.error('Sync error:', error);
      if (error.message === 'NO_STUDENTS') {
        alert('No students found in the selected cohort(s).');
      } else {
        alert('Sync failed. Please check:\n1. Webhook URL is correct\n2. Apps Script is deployed\n3. Internet connection');
      }
    } finally {
      setSyncing(false);
    }
  };

const handleUnclaimSubmission = async (submissionId, studentName) => {
    if (!window.confirm(`Send "${studentName}" back to available submissions?\n\nThis will remove it from the marker's queue.`)) {
      return;
    }
    try {
      const { doc, updateDoc } = await import('firebase/firestore');
      const { db } = await import('./firebase/config');
      await updateDoc(doc(db, 'submissions', submissionId), {
        status: 'pending',
        assignedTo: null,
        markerName: null,
        claimedAt: null
      });
      
      // Notify head marker (non-blocking)
      try {
        const { sendHeadMarkerNotification, NOTIFICATION_TYPES } = await import('./firebase/headMarkerNotifications');
        sendHeadMarkerNotification(NOTIFICATION_TYPES.SUBMISSION_UNCLAIMED, {
          studentName,
          homeworkType: '',
          markerName: 'Head Marker'
        });
      } catch (e) { console.error('Notification error:', e); }
      
      const unclaimed = myClaimedSubmissions.find(s => s.id === submissionId) || allClaimedSubmissions.find(s => s.id === submissionId);
      if (unclaimed) {
        const pendingSub = { ...unclaimed, status: 'pending', assignedTo: null, markerName: null, claimedAt: null };
        setMyClaimedSubmissions(prev => prev.filter(s => s.id !== submissionId));
        setAllClaimedSubmissions(prev => prev.filter(s => s.id !== submissionId));
        setAvailableSubmissions(prev => [pendingSub, ...prev]);
      }
      alert('Submission returned to available pool');
    } catch (error) {
      console.error('Error unclaiming submission:', error);
      alert('Failed to unclaim submission: ' + error.message);
    }
  };

  const handleFlagFromClaimed = async (submissionId) => {
    if (!flagComment.trim()) return;
    const submission = myClaimedSubmissions.find(s => s.id === submissionId);

    try {
      await flagSubmissionForReview(submissionId, user.uid, user.name, flagComment);

      try {
        const { sendHeadMarkerNotification, NOTIFICATION_TYPES } = await import('./firebase/headMarkerNotifications');
        sendHeadMarkerNotification(NOTIFICATION_TYPES.SUBMISSION_FLAGGED_FOR_REVIEW, {
          studentName: submission?.studentName,
          homeworkType: homeworkNames[submission?.homeworkType] || submission?.homeworkType,
          markerName: user.name,
          flagComment
        });
      } catch (e) { console.error('Notification error:', e); }

      setMyClaimedSubmissions(prev => prev.filter(s => s.id !== submissionId));
      setAllClaimedSubmissions(prev => prev.filter(s => s.id !== submissionId));
      if (submission) {
        setFlaggedSubmissions(prev => [{ ...submission, status: 'flagged_for_review', flaggedByName: user.name, flagComment }, ...prev]);
      }
      setShowFlagModal(null);
      setFlagComment('');
    } catch (error) {
      console.error('Flag submission error:', error);
      alert('Failed to flag submission. Please try again.');
    }
  };

  const handleForceDeleteSubmission = async (submission) => {
    const label = `${homeworkNames[submission.homeworkType] || submission.homeworkType} — ${submission.studentName}`;
    if (!window.confirm(`Permanently delete this submission?\n\n${label}\n\nThis removes the submission record and any files entirely. This cannot be undone. Only use this for broken/corrupted submissions.`)) {
      return;
    }
    try {
      await permanentlyDeleteSubmission(submission.id);
      setAvailableSubmissions(prev => prev.filter(s => s.id !== submission.id));
      setMyClaimedSubmissions(prev => prev.filter(s => s.id !== submission.id));
      setAllClaimedSubmissions(prev => prev.filter(s => s.id !== submission.id));
      alert('Submission permanently deleted.');
    } catch (error) {
      console.error('Force delete submission error:', error);
      alert('Failed to delete submission: ' + error.message);
    }
  };

  const handleReopenExamSubmission = async (submission) => {
    const label = `${homeworkNames[submission.homeworkType] || submission.homeworkType} - ${submission.studentName}`;
    if (!window.confirm(`Reopen this homework so ${submission.studentName} can keep editing it?\n\n${label}\n\nEverything they typed stays in place. This submission leaves the queue (it is kept, not deleted) and they submit again when they are done. Let the student know it is open again.`)) {
      return;
    }
    try {
      await reopenExamSubmission(submission.id);
      setAvailableSubmissions(prev => prev.filter(s => s.id !== submission.id));
      alert(`Reopened. ${submission.studentName} can now open ${homeworkNames[submission.homeworkType] || submission.homeworkType} from their dashboard and carry on where they left off.`);
    } catch (error) {
      console.error('Reopen exam submission error:', error);
      alert('Could not reopen: ' + error.message);
    }
  };

  const handleResetSubmission = async (submission) => {
    const label = `${homeworkNames[submission.homeworkType] || submission.homeworkType} — ${submission.studentName}`;
    const reason = window.prompt(`Reset this submission so ${submission.studentName} can re-upload?\n\n${label}\n\nOptional: reason (included in the email to the student). Leave blank to skip, or Cancel to abort.`);
    if (reason === null) return;

    try {
      await resetSubmissionForResubmit(submission.id, reason, user.name || user.email);
      setAvailableSubmissions(prev => prev.filter(s => s.id !== submission.id));
      setMyClaimedSubmissions(prev => prev.filter(s => s.id !== submission.id));
      setAllClaimedSubmissions(prev => prev.filter(s => s.id !== submission.id));

      try {
        await fetch(CLOUD_FUNCTION_URL, await notificationRequest({
            type: 'submission_reset',
            studentEmail: submission.studentEmail,
            studentName: submission.studentName,
            homeworkType: homeworkNames[submission.homeworkType] || submission.homeworkType,
            reason
          }));
      } catch (emailError) {
        console.error('Reset notification email failed:', emailError);
      }

      alert('Submission reset. The student has been notified to re-upload.');
    } catch (error) {
      console.error('Reset submission error:', error);
      alert('Failed to reset submission: ' + error.message);
    }
  };

  const handleChangeHomeworkType = async (submissionId, newHomeworkType) => {
    try {
      await changeSubmissionHomeworkType(submissionId, newHomeworkType, user.name || user.email);
      setShowChangeTypeModal(null);
      setAvailableSubmissions(prev => prev.map(s =>
        s.id === submissionId ? { ...s, homeworkType: newHomeworkType } : s
      ));
      alert(`Reclassified as ${homeworkNames[newHomeworkType] || newHomeworkType}.`);
    } catch (error) {
      console.error('Change homework type error:', error);
      alert('Failed to change homework type: ' + error.message);
    }
  };

  const buildWhatsAppMessage = (submission) => {
    const score = submission.score || {};
    const homeworkName = homeworkNames[submission.homeworkType] || submission.homeworkType;
    const template = whatsappTemplates[submission.homeworkType];
    const storedSections = sectionStructures[submission.homeworkType]?.sections;
    const hwSections = (storedSections && storedSections.length > 0)
      ? storedSections
      : (getDefaultSectionStructures()[submission.homeworkType]?.sections || null);
    const sectionScores = hwSections
      ? Object.fromEntries(hwSections.map(s => [s.id, score[`section${s.id}`] ?? 0]))
      : null;
    const technicalScores = hwSections
      ? Object.fromEntries(hwSections.map(s => [s.id, score[`technical${s.id}`] ?? 0]))
      : null;
    const professionalScores = hwSections
      ? Object.fromEntries(hwSections.map(s => [s.id, score[`professional${s.id}`] ?? 0]))
      : null;
    const professionalGroups = score.professionalGroups || null;
    // Also pass per-section scores directly so template scanning works even if hwSections is null
    const directSectionScores = Object.fromEntries(
      Object.entries(score).filter(([k]) => /^(technical|professional)[A-Z]/.test(k) && typeof score[k] === 'number')
    );

    if (template) {
      return fillWhatsAppTemplate(template, {
        studentName: submission.studentName,
        sectionA: score.sectionA,
        sectionB: score.sectionB,
        sectionC: score.sectionC,
        sections: hwSections,
        sectionScores,
        technicalScores,
        professionalScores,
        professionalGroups,
        total: score.total,
        maxMark: maxMarks[submission.homeworkType] || 20,
        technical: score.technical,
        professional: score.professional,
        finalComment: submission.finalComment || '',
        homeworkType: submission.homeworkType,
        homeworkName: homeworkName,
        ...directSectionScores
      });
    }
    const sectionLines = hwSections
      ? hwSections.map((s, i) => {
          const legacyKey = i === 0 ? 'sectionA' : i === 1 ? 'sectionB' : 'sectionC';
          return `${s.name}: ${(score[legacyKey] ?? 0).toFixed(1)}`;
        }).join('\n')
      : `Section A: ${score.sectionA?.toFixed(1) || '0.0'}\nSection B: ${score.sectionB?.toFixed(1) || '0.0'}\nSection C: ${score.sectionC?.toFixed(1) || '0.0'}`;
    return `Hi ${submission.studentName}, your homework has been marked.\n\n${sectionLines}\n\nTotal: ${score.total?.toFixed(1) || '0.0'}/${maxMarks[submission.homeworkType] || 20}\n\n${submission.finalComment || ''}`;
  };

  const sendWhatsAppMessage = (submission) => {
    if (!submission.studentPhone) {
      alert('No phone number for this student!\n\nPhone number is required to send WhatsApp messages.');
      return;
    }
    const message = buildWhatsAppMessage(submission);
    const phone = submission.studentPhone.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const copyWhatsAppMessage = (submission) => {
    const message = buildWhatsAppMessage(submission);
    navigator.clipboard.writeText(message);
    setCopiedSubmissionId(submission.id);
    setTimeout(() => setCopiedSubmissionId(null), 2500);
  };

  if (loading) {
    return (
      <div style={{
        fontFamily: "'Crimson Pro', 'Georgia', serif",
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f1e8 0%, #e8e0d5 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '40px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  const headerBg = orgHeaderColor || '#c33329';
  const headerText = getContrastTextColor(headerBg);
  const issuesCount = flaggedSubmissions.length + pendingAdditions.length;

  // Main navigation. Flagged + Pending Additions live together under Issues;
  // Results, Deadlines and Sync/Export live in Cohorts; Notifications in
  // Settings; Marker Activity in Markers.
  const navTabs = [
    { view: 'review', label: 'Review Queue', count: awaitingReview.length },
    { view: 'available', label: 'Available', count: availableSubmissions.length, icon: FileText },
    { view: 'claimed', label: 'Claimed', count: myClaimedSubmissions.length, icon: Inbox },
    { view: 'issues', label: 'Issues', count: issuesCount, icon: AlertTriangle, alert: issuesCount > 0 },
    { view: 'cohorts', label: 'Cohorts', icon: Table },
    { view: 'settings', label: 'Settings', count: pendingSuggestions || null, alert: pendingSuggestions > 0 },
    { view: 'markers', label: 'Markers', icon: Users }
  ];

  const openTab = (view) => {
    setCurrentView(view);
    if (view === 'settings') setSettingsSubView('homework');
    if (view === 'markers') setMarkersSubView('activity');
  };

  return (
    <div style={{
      fontFamily: "'Crimson Pro', 'Georgia', serif",
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f1e8 0%, #e8e0d5 100%)',
      padding: isMobile ? '8px' : '20px'
    }}>
      {/* Header - logo, welcome, account buttons */}
      <div style={{
        background: headerBg,
        color: headerText,
        padding: isMobile ? '10px 12px' : '10px 20px',
        borderRadius: '12px 12px 0 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0 }}>
          {isAileenOrg ? (
            <img
              src="/aileen-edgar-logo-red.png"
              alt="Aileen Edgar"
              style={{ height: isMobile ? '34px' : '44px', width: 'auto', maxWidth: isMobile ? '40vw' : 'none', objectFit: 'contain' }}
            />
          ) : orgLogoUrl ? (
            <img
              src={orgLogoUrl}
              alt="Logo"
              style={{ height: isMobile ? '34px' : '44px', width: 'auto', maxWidth: isMobile ? '40vw' : 'none', objectFit: 'contain' }}
            />
          ) : (
            <span style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '700' }}>
              Homework Solutions
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '10px', flexShrink: 0 }}>
          {/* Help / FAQs - see HelpPanel.jsx (fixed overlay, so it can render here) */}
          <button
            type="button"
            onClick={() => setShowHelp(true)}
            title="Help and frequently asked questions"
            style={{ padding: isMobile ? '8px 10px' : '9px 14px', background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: isMobile ? '13px' : '14px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <HelpCircle size={16} />
            {!isMobile && 'Help'}
          </button>
          {showHelp && <HelpPanel role="head_marker" onClose={() => setShowHelp(false)} />}
          <button
            onClick={handleSwitchToCourseHosting}
            disabled={switchingToCourseHosting}
            style={{
              padding: isMobile ? '8px 10px' : '9px 18px',
              background: 'rgba(255,255,255,0.15)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: switchingToCourseHosting ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: isMobile ? '13px' : '14px'
            }}
          >
            {switchingToCourseHosting ? 'Switching...' : (isMobile ? 'Course' : 'Course Content')}
          </button>
          <button
            onClick={logout}
            style={{
              padding: isMobile ? '8px 10px' : '9px 18px',
              background: '#e74c3c',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: isMobile ? '13px' : '14px'
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Welcome + homeworks waiting for review */}
      <div style={{
        background: 'white',
        padding: isMobile ? '14px 16px' : '20px 24px',
        borderRadius: '0 0 12px 12px',
        marginBottom: isMobile ? '12px' : '16px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '10px 24px'
      }}>
        <h1 style={{ margin: 0, fontSize: isMobile ? '20px' : '26px', color: '#2c3e50', fontWeight: '700' }}>
          Welcome, {user?.name || user?.email}
        </h1>
        <button
          onClick={() => openTab('review')}
          title="Open the Review Queue"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: isMobile ? '6px 12px' : '8px 16px',
            background: awaitingReview.length > 0 ? '#fff7e6' : '#f8f9fa',
            border: `2px solid ${awaitingReview.length > 0 ? '#f39c12' : '#e2e8f0'}`,
            borderRadius: '10px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            color: '#2c3e50'
          }}
        >
          <span style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: '700' }}>{awaitingReview.length}</span>
          <span style={{ fontSize: '14px', color: '#4a5568', textAlign: 'left' }}>
            {awaitingReview.length === 1 ? 'homework' : 'homeworks'} waiting<br />for review
          </span>
        </button>
      </div>

      {/* Tabs - one row on desktop, swipeable strip on phones */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: isMobile ? '8px' : '10px 12px',
        marginBottom: isMobile ? '12px' : '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        display: 'flex',
        gap: isMobile ? '6px' : '8px',
        flexWrap: isMobile ? 'nowrap' : 'wrap',
        overflowX: isMobile ? 'auto' : 'visible',
        WebkitOverflowScrolling: 'touch'
      }}>
        {navTabs.map(({ view, label, count, icon: Icon, alert: needsAttention }) => {
          const active = currentView === view;
          return (
            <button
              key={view}
              onClick={() => openTab(view)}
              style={{
                padding: isMobile ? '8px 12px' : '9px 16px',
                background: active ? '#c33329' : '#ecf0f1',
                color: active ? 'white' : '#2c3e50',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
            >
              {Icon && <Icon size={15} />}
              {label}
              {count !== undefined && count !== null && (
                <span style={{
                  background: active ? 'rgba(255,255,255,0.25)' : (needsAttention ? '#e74c3c' : '#d5dbdf'),
                  color: active || needsAttention ? 'white' : '#2c3e50',
                  padding: '1px 7px',
                  borderRadius: '10px',
                  fontSize: '12px',
                  fontWeight: '700'
                }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Review Queue View */}
      {currentView === 'review' && !reviewingSubmission && (
        <ReviewQueueView
          awaitingReview={awaitingReview}
          homeworkNames={homeworkNames}
          maxMarks={maxMarks}
          sectionStructures={sectionStructures}
          onEditReview={(submission) => setReviewingSubmission(submission)}
          onSendWhatsApp={sendWhatsAppMessage}
          onCopyWhatsApp={copyWhatsAppMessage}
          copiedSubmissionId={copiedSubmissionId}
          onReject={(submissionId) => {
            setShowRejectModal(submissionId);
            setRejectionNotes('');
          }}
        />
      )}

      {/* Issues - Flagged for Review + Pending Additions (late uploads held
          for release, see releasePendingAdditions). An empty section shrinks
          to one line so the other is easy to see. */}
      {currentView === 'issues' && (
        <div style={{ display: 'grid', gap: isMobile ? '12px' : '20px' }}>
          {[
            { key: 'flagged', title: 'Flagged for Review', items: flaggedSubmissions, empty: 'Nothing flagged right now' },
            { key: 'additions', title: 'Pending Additions', items: pendingAdditions, empty: 'No pending additions' }
          ].map(section => section.items.length === 0 ? (
            <div key={section.key} style={{
              background: 'white',
              borderRadius: '12px',
              padding: '14px 20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              color: '#7f8c8d'
            }}>
              <Check size={16} color="#1b998b" />
              <span style={{ fontWeight: '600', color: '#2c3e50' }}>{section.title}</span>
              <span>- {section.empty}</span>
            </div>
          ) : section.key === 'flagged' ? (
            <FlaggedSubmissionsView
              key={section.key}
              flaggedSubmissions={flaggedSubmissions}
              homeworkNames={homeworkNames}
              onReturnToMarker={handleReturnFlaggedToMarker}
              onReleaseToPool={handleReleaseFlaggedToPool}
              onReviewMarking={(submission) => setReviewingSubmission(submission)}
            />
          ) : (
            <PendingAdditionsView
              key={section.key}
              pendingAdditions={pendingAdditions}
              homeworkNames={homeworkNames}
              onRelease={handleReleasePendingAdditions}
            />
          ))}
        </div>
      )}

      {/* Review/Edit Mode - Opens FirebaseMarker.
          isHeadMarkerReview only applies to genuine review-queue approvals
          (status 'awaiting_review') - Aileen's own fresh marking (claimed
          from 'available') and flagged submissions she's inspecting open in
          normal marker mode, so she gets Save Progress/Flag/Complete instead
          of being dropped straight into "Approve & Send to Student". */}
      {reviewingSubmission && (
        <FirebaseMarker
          submission={reviewingSubmission}
          onBack={async () => {
            setReviewingSubmission(null);
            const [claimed, allClaimed, updated, flagged, additions] = await Promise.all([
              getPendingSubmissions(user.uid),
              getAllClaimedSubmissions(),
              getSubmissionsAwaitingReview(),
              getFlaggedSubmissions(),
              getSubmissionsWithPendingAdditions()
            ]);
            setMyClaimedSubmissions(claimed);
            setAllClaimedSubmissions(allClaimed);
            setAwaitingReview(updated);
            setFlaggedSubmissions(flagged);
            setPendingAdditions(additions);
          }}
          isHeadMarkerReview={reviewingSubmission.status === 'awaiting_review'}
          user={user}
        />
      )}

      {/* Available Submissions View */}
      {currentView === 'available' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <button
              onClick={() => setShowUploadOnBehalfModal(true)}
              style={{
                padding: '10px 20px',
                background: 'white',
                color: '#c33329',
                border: '2px solid #c33329',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Upload size={16} />
              Upload Submission for Student
            </button>
          </div>
          <AvailableSubmissionsView
            availableSubmissions={availableSubmissions}
            homeworkNames={homeworkNames}
            onClaim={handleClaimSubmission}
            onForceDelete={handleForceDeleteSubmission}
            onReset={handleResetSubmission}
            onReopen={handleReopenExamSubmission}
            onChangeType={(submission) => setShowChangeTypeModal(submission)}
          />
        </>
      )}

      {/* My Claimed Submissions View */}
      {currentView === 'claimed' && (
        <ClaimedSubmissionsView
          myClaimedSubmissions={myClaimedSubmissions}
          homeworkNames={homeworkNames}
          onStartMarking={(submission) => setReviewingSubmission(submission)}
          onUnclaim={(submission) => handleUnclaimSubmission(submission.id, submission.studentName)}
          onForceDelete={handleForceDeleteSubmission}
          onReset={handleResetSubmission}
          onFlag={(submission) => { setShowFlagModal(submission.id); setFlagComment(''); }}
        />
      )}
      
{/* Markers tab: Activity (what each marker is working on) | Manage Markers */}
{currentView === 'markers' && (
  <div style={{ display: 'flex', gap: '8px', marginBottom: isMobile ? '12px' : '16px', flexWrap: 'wrap' }}>
    {[
      { key: 'activity', label: `Activity (${allClaimedSubmissions.length})` },
      { key: 'manage', label: 'Manage Markers' }
    ].map(({ key, label }) => (
      <button
        key={key}
        onClick={() => setMarkersSubView(key)}
        style={{
          padding: '8px 16px',
          background: markersSubView === key ? '#f39c12' : 'white',
          color: markersSubView === key ? 'white' : '#2c3e50',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontWeight: '600',
          fontSize: '14px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
        }}
      >
        {label}
      </button>
    ))}
  </div>
)}
{currentView === 'markers' && markersSubView === 'activity' && (
  <div style={{
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
  }}>
    <h2 style={{ margin: '0 0 16px 0', fontSize: '20px' }}>
      Marker Activity
    </h2>
    <p style={{ color: '#7f8c8d', marginBottom: '16px', fontSize: '14px' }}>
      View all submissions currently being marked by your team.
    </p>

    {allClaimedSubmissions.length > 0 && (() => {
      const uniqueMarkers = [...new Set(allClaimedSubmissions.map(s => s.markerName || s.assignedTo || 'Unknown'))];
      return uniqueMarkers.length > 1 ? (
        <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '14px', color: '#4a5568', fontWeight: '600' }}>Filter by marker:</span>
          <select
            value={activityMarkerFilter}
            onChange={e => setActivityMarkerFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#2c3e50',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Markers ({allClaimedSubmissions.length})</option>
            {uniqueMarkers.map(name => (
              <option key={name} value={name}>
                {name} ({allClaimedSubmissions.filter(s => (s.markerName || s.assignedTo || 'Unknown') === name).length})
              </option>
            ))}
          </select>
        </div>
      ) : null;
    })()}

    {allClaimedSubmissions.length === 0 ? (
      <div style={{
        textAlign: 'center',
        padding: '60px 20px',
        color: '#7f8c8d'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
          <FileText size={48} color="#bdc3c7" />
        </div>
        <div style={{ fontSize: '18px' }}>No claimed submissions</div>
      </div>
    ) : (
      <div style={{ display: 'grid', gap: '16px' }}>
        {allClaimedSubmissions.filter(s => activityMarkerFilter === 'all' || (s.markerName || s.assignedTo || 'Unknown') === activityMarkerFilter).map(submission => (
          <div
            key={submission.id}
            style={{
              border: '1px solid #e1e8ed',
              borderRadius: '8px',
              padding: '20px',
              background: '#fafafa'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#2c3e50',
                  marginBottom: '8px'
                }}>
                  {submission.studentName}
                  {submission.cohortName && (
                    <span style={{ 
                      marginLeft: '12px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#667eea',
                      background: '#eef2ff',
                      padding: '4px 12px',
                      borderRadius: '6px'
                    }}>
                      {submission.cohortName}
                    </span>
                  )}
                </div>
                
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  fontSize: '14px',
                  color: '#7f8c8d',
                  marginBottom: '12px'
                }}>
                  <span>{homeworkNames[submission.homeworkType] || submission.homeworkType}</span>
                  <span>Submitted {submission.submittedAt ? new Date(submission.submittedAt.toDate()).toLocaleDateString() : 'Unknown'}</span>
                  <span>Claimed {submission.claimedAt ? new Date(submission.claimedAt.toDate()).toLocaleDateString() : 'Unknown'}</span>
                </div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  background: '#e3f2fd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#1976d2',
                  width: 'fit-content'
                }}>
                 <span>Marker: {submission.markerName || submission.assignedTo || 'Unknown'}</span>
                </div>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                <div style={{
                  padding: '6px 16px',
                  background: submission.status === 'marking' ? '#fff3cd' : '#d1ecf1',
                  color: submission.status === 'marking' ? '#856404' : '#0c5460',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600'
                }}>
                  {submission.status === 'marking' ? 'IN PROGRESS' : 'ASSIGNED'}
                </div>
                <button
                  onClick={() => handleUnclaimSubmission(submission.id, submission.studentName)}
                  style={{
                    padding: '6px 14px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '12px'
                  }}
                >
                  Send Back to Available
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
)}

      {/* Cohorts View */}
      {currentView === 'cohorts' && (
        <CohortManagement
          orgId={user.orgId || DEFAULT_ORG_ID}
          renderDeadlinesExtra={() => <ReminderSettingsPanel />}
          renderCohortActions={isAileenOrg ? (cohort) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <select
                value={selectedSyncCohort === 'all' ? 'all' : 'this'}
                onChange={(e) => setSelectedSyncCohort(e.target.value === 'all' ? 'all' : 'this')}
                aria-label="Which cohorts to sync"
                style={{ padding: '9px 8px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
              >
                <option value="this">This cohort</option>
                <option value="all">All active cohorts</option>
              </select>
              <button
                onClick={() => handleSyncToStudentData(selectedSyncCohort === 'all' ? 'all' : cohort.id)}
                disabled={syncing}
                style={{ padding: '10px 16px', background: '#1b998b', color: 'white', border: 'none', borderRadius: '8px', cursor: syncing ? 'not-allowed' : 'pointer', opacity: syncing ? 0.6 : 1, fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}
              >
                <Send size={16} />
                {syncing ? 'Syncing...' : 'Sync to Sheets'}
              </button>
            </div>
          ) : undefined}
        />
      )}

      {/* Settings View */}
      {currentView === 'settings' && (
        <div>
          {/* Unsaved-changes bar - stays on screen so Save is never a long
              scroll away */}
          {hasUnsavedSettings && (
            <div style={{
              position: 'fixed',
              bottom: '16px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              padding: '12px 16px 12px 20px',
              background: '#2c3e50',
              color: 'white',
              borderRadius: '10px',
              boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
              maxWidth: 'calc(100vw - 32px)',
              boxSizing: 'border-box'
            }}>
              <span style={{ fontSize: '14px', fontWeight: '600' }}>You have unsaved changes</span>
              <button
                onClick={handleUpdateSettings}
                disabled={!canSaveSettings}
                style={{
                  padding: '8px 18px',
                  background: '#c33329',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: canSaveSettings ? 'pointer' : 'not-allowed',
                  opacity: canSaveSettings ? 1 : 0.5,
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap'
                }}
              >
                <ThumbsUp size={16} />
                Save Changes
              </button>
            </div>
          )}
          {settingsLoadFailed && (
            <div style={{
              background: '#fdecea',
              border: '1px solid #c33329',
              color: '#8a1f17',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px'
            }}>
              <AlertTriangle size={18} />
              Some of your settings couldn't be loaded. Saving is turned off so they aren't overwritten - please refresh the page.
            </div>
          )}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0 }}>Settings</h2>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setShowAddHomeworkModal(true)}
                  style={{
                    padding: '10px 20px',
                    background: '#1b998b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Plus size={18} />
                  Add Homework
                </button>
              </div>
            </div>
            
            {/* Sub-tabs */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', borderBottom: '2px solid #ecf0f1', paddingBottom: '8px' }}>
              <button
                onClick={() => setSettingsSubView('homework')}
                style={{
                  padding: '8px 16px',
                  background: settingsSubView === 'homework' ? '#f39c12' : 'transparent',
                  color: settingsSubView === 'homework' ? 'white' : '#2c3e50',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                Homework Settings
              </button>
              <button
  onClick={() => setSettingsSubView('comments')}
  style={{
    padding: '8px 16px',
    background: settingsSubView === 'comments' ? '#f39c12' : 'transparent',
    color: settingsSubView === 'comments' ? 'white' : '#2c3e50',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    position: 'relative'
  }}
>
  <MessageSquare size={16} />
  Comment Library
  {pendingSuggestions > 0 && (
    <span style={{
      background: '#e74c3c',
      color: 'white',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '700'
    }}>
      {pendingSuggestions}
    </span>
  )}
</button>
              <button
                onClick={() => setSettingsSubView('whatsapp')}
                style={{
                  padding: '8px 16px',
                  background: settingsSubView === 'whatsapp' ? '#f39c12' : 'transparent',
                  color: settingsSubView === 'whatsapp' ? 'white' : '#2c3e50',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Send size={16} />
                WhatsApp Templates
              </button>
              <button
                onClick={() => setSettingsSubView('notifications')}
                style={{
                  padding: '8px 16px',
                  background: settingsSubView === 'notifications' ? '#f39c12' : 'transparent',
                  color: settingsSubView === 'notifications' ? 'white' : '#2c3e50',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Bell size={16} />
                Notifications
              </button>
              {isAileenOrg && (
                <button
                  onClick={() => setSettingsSubView('integrations')}
                  style={{
                    padding: '8px 16px',
                    background: settingsSubView === 'integrations' ? '#f39c12' : 'transparent',
                    color: settingsSubView === 'integrations' ? 'white' : '#2c3e50',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Table size={16} />
                  Integrations
                </button>
              )}
              <button
                onClick={() => setSettingsSubView('account')}
                style={{
                  padding: '8px 16px',
                  background: settingsSubView === 'account' ? '#f39c12' : 'transparent',
                  color: settingsSubView === 'account' ? 'white' : '#2c3e50',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Lock size={16} />
                Account
              </button>
            </div>
          </div>

          {/* Homework Settings Sub-view */}
          {settingsSubView === 'homework' && (
            <div>
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                marginBottom: '16px'
              }}>
                <p style={{ color: '#7f8c8d', margin: '0', fontSize: '14px' }}>
                  Click on any homework to configure all its settings in one place. Use the arrows to change the order - students see their homework in this order too.
                </p>
              </div>

              {/* Global Tax Tables / Help document - shared by every exam-mode homework */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                marginBottom: '16px'
              }}>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#2c3e50' }}>
                  Tax Tables / Help Document
                </h3>
                <p style={{ fontSize: '12px', color: '#7f8c8d', margin: '0 0 12px 0' }}>
                  One document, shared by the Help button in every exam-mode homework. Replacing it updates it everywhere immediately.
                </p>
                {examHelpDocument ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ flex: 1, fontSize: '13px', padding: '8px 12px', background: '#f0f4ff', borderRadius: '6px', border: '1px solid #c7d2fe' }}>
                      {examHelpDocument.title || examHelpDocument.fileName}
                    </div>
                    <label style={{ padding: '8px 14px', background: 'white', color: '#667eea', border: '1px solid #667eea', borderRadius: '6px', cursor: uploadingTaxTables ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '13px' }}>
                      {uploadingTaxTables ? 'Uploading...' : 'Replace'}
                      <input
                        type="file"
                        accept=".pdf"
                        style={{ display: 'none' }}
                        disabled={uploadingTaxTables}
                        onChange={(e) => { const file = e.target.files?.[0]; if (file) handleUploadTaxTables(file); e.target.value = ''; }}
                      />
                    </label>
                    <button
                      onClick={handleDeleteTaxTables}
                      style={{ background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ) : (
                  <label style={{ display: 'inline-block', padding: '10px 16px', background: uploadingTaxTables ? '#e2e8f0' : '#667eea', color: 'white', borderRadius: '6px', cursor: uploadingTaxTables ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '13px' }}>
                    {uploadingTaxTables ? 'Uploading...' : '+ Upload Tax Tables / Help Document'}
                    <input
                      type="file"
                      accept=".pdf"
                      style={{ display: 'none' }}
                      disabled={uploadingTaxTables}
                      onChange={(e) => { const file = e.target.files?.[0]; if (file) handleUploadTaxTables(file); e.target.value = ''; }}
                    />
                  </label>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '14px', cursor: savingHelpButtonVisible ? 'wait' : 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={examHelpButtonVisible}
                    disabled={savingHelpButtonVisible}
                    onChange={handleToggleHelpButtonVisible}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#2c3e50' }}>
                    Show the Help / Tax Tables button to students in exam mode
                  </span>
                </label>
                {!examHelpButtonVisible && (
                  <p style={{ fontSize: '12px', color: '#7f8c8d', margin: '6px 0 0 24px' }}>
                    Hidden in every exam-mode homework. Saved straight away.
                  </p>
                )}
              </div>

              {/* Practice / Demo Run - record yourself completing a timed
                  homework without any of it touching real student/marker
                  data. See createPracticeExamSession. */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                marginBottom: '16px'
              }}>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#2c3e50' }}>
                  Practice / Demo Run
                </h3>
                <p style={{ fontSize: '12px', color: '#7f8c8d', margin: '0 0 12px 0' }}>
                  Opens the exact exam experience a student gets - for recording a walkthrough with your own
                  screen recorder. Always untimed, tied to your own account, and never creates a real submission -
                  when you finish, the completed PDF just downloads to your computer.
                </p>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <select
                    value={practiceHomeworkType}
                    onChange={(e) => setPracticeHomeworkType(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '14px',
                      background: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">
                      {homeworkTypes.filter(type => submissionModes[type] === 'exam').length === 0
                        ? 'No homework is set to Exam Simulation mode yet'
                        : '-- Select homework --'}
                    </option>
                    {homeworkTypes.filter(type => submissionModes[type] === 'exam').map(type => (
                      <option key={type} value={type}>{homeworkNames[type] || type}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    disabled={!practiceHomeworkType}
                    title="Carries on from where you left off if you have an unfinished practice run of this homework"
                    onClick={() => openPracticeRun(false)}
                    style={{
                      padding: '10px 20px',
                      background: practiceHomeworkType ? '#667eea' : '#cbd5e0',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: practiceHomeworkType ? 'pointer' : 'not-allowed',
                      fontWeight: '600',
                      fontSize: '14px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Continue / Start
                  </button>
                  <button
                    type="button"
                    disabled={!practiceHomeworkType}
                    title="Discards any unfinished practice run of this homework and starts again with its latest content"
                    onClick={() => openPracticeRun(true)}
                    style={{
                      padding: '10px 20px',
                      background: 'white',
                      color: practiceHomeworkType ? '#667eea' : '#a0aec0',
                      border: `2px solid ${practiceHomeworkType ? '#667eea' : '#cbd5e0'}`,
                      borderRadius: '6px',
                      cursor: practiceHomeworkType ? 'pointer' : 'not-allowed',
                      fontWeight: '600',
                      fontSize: '14px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Start Fresh
                  </button>
                </div>
                <p style={{ fontSize: '12px', color: '#7f8c8d', margin: '8px 0 0 0' }}>
                  Continue picks up an unfinished practice run where you left it. Start Fresh discards it and loads
                  the homework's latest content - use it after editing a homework.
                </p>
              </div>

              {/* AI-generated overall summary toggle */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                marginBottom: '16px'
              }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#2c3e50' }}>
                  AI-Generated Overview Summaries
                </h3>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  <button
                    onClick={handleToggleAiSummary}
                    disabled={savingAiSummary}
                    style={{
                      width: '52px',
                      height: '28px',
                      borderRadius: '14px',
                      border: 'none',
                      background: aiSummaryEnabled ? '#1b998b' : '#bdc3c7',
                      cursor: 'pointer',
                      position: 'relative',
                      flexShrink: 0,
                      transition: 'background 0.2s'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '3px',
                      left: aiSummaryEnabled ? '27px' : '3px',
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      background: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      transition: 'left 0.2s'
                    }} />
                  </button>
                  <div style={{ fontSize: '13px', color: '#7f8c8d', lineHeight: '1.5' }}>
                    {aiSummaryEnabled
                      ? 'ON — after a marker finishes marking, an AI summary of their comments is generated in the background for you to review/edit before approving.'
                      : 'OFF — no AI summary is generated.'}
                    <div style={{ marginTop: '4px', fontSize: '12px', color: '#a0aec0' }}>
                      {savingAiSummary ? 'Saving...' : 'Saved as soon as you change it.'}
                    </div>
                  </div>
                </div>
              </div>

          {/* Accordion: Each Homework Type */}
          <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
            {homeworkTypes.map(homeworkType => {
              const isExpanded = expandedHomework === homeworkType;
              const structure = sectionStructures[homeworkType] || { sections: [] };
              const totalMarks = maxMarks[homeworkType] || 20;
              const commentBank = commentBanks[homeworkType] || {};
              const template = whatsappTemplates[homeworkType] || '';
              const panel = (id) => settingsPanelProps(`${homeworkType}:${id}`);
              const commentCountFor = (key) => {
                const raw = commentBank[key] || [];
                return (Array.isArray(raw) ? raw : (raw.comments || [])).length;
              };
              // Reasons only show for sections with professional marks; hidden ones are
              // kept, and reappear if that section's marks go back above 0
              const sectionsWithProfMarks = structure.sections.filter(s => (s.maxProfessional ?? 1) > 0);

              return (
                <div
                  key={homeworkType}
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    border: isExpanded ? '3px solid #667eea' : '2px solid #e2e8f0',
                    overflow: 'hidden',
                    boxShadow: isExpanded ? '0 4px 12px rgba(102,126,234,0.2)' : '0 2px 8px rgba(0,0,0,0.05)',
                    transition: 'all 0.2s'
                  }}
                >
                  {/* Header - Always Visible, Clickable */}
                  <div
                    onClick={() => setExpandedHomework(isExpanded ? null : homeworkType)}
                    style={{
                      padding: '20px 24px',
                      cursor: 'pointer',
                      background: isExpanded ? '#f7f9ff' : 'white',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      userSelect: 'none'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#2c3e50', marginBottom: '4px' }}>
                        {homeworkNames[homeworkType] || `Homework ${homeworkType}`}
                      </div>
                      <div style={{ fontSize: '13px', color: '#7f8c8d' }}>
                        {homeworkType} · {structure.sections.length} section{structure.sections.length !== 1 ? 's' : ''} · {totalMarks} marks · {reviewSettings[homeworkType] ? 'Requires review' : 'Auto-send'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {/* Reorder - this order is what students see too */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {[[-1, ChevronUp, 'Move up'], [1, ChevronDown, 'Move down']].map(([direction, Icon, label]) => {
                          const index = homeworkTypes.indexOf(homeworkType);
                          const disabled = direction < 0 ? index === 0 : index === homeworkTypes.length - 1;
                          return (
                            <button
                              key={label}
                              title={label}
                              aria-label={`${label}: ${homeworkNames[homeworkType] || homeworkType}`}
                              disabled={disabled}
                              onClick={(e) => { e.stopPropagation(); handleMoveHomework(homeworkType, direction); }}
                              style={{ width: '28px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', border: '1px solid #cbd5e0', borderRadius: '4px', color: disabled ? '#cbd5e0' : '#4a5568', cursor: disabled ? 'default' : 'pointer', padding: 0 }}
                            >
                              <Icon size={16} />
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteHomework(homeworkType);
                        }}
                        style={{
                          padding: '8px 12px',
                          background: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '13px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                      <div style={{
                        color: '#667eea',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <ChevronDown size={24} />
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div style={{ padding: '0 24px 24px 24px' }}>
                      
                      {/* Basic Settings */}
                      <SettingsPanel
                        title="Basic Settings"
                        summary={`${submissionModes[homeworkType] === 'exam' ? 'Exam mode' : 'PDF upload'}, ${reviewSettings[homeworkType] ? 'requires review' : 'auto-send'}`}
                        {...panel('basic')}
                      >
                        
                        {/* Homework Name */}
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                            Homework Name
                          </label>
                          <input
                            type="text"
                            value={homeworkNames[homeworkType] || ''}
                            onChange={(e) => updateHomeworkName(homeworkType, e.target.value)}
                            placeholder={`Enter name for ${homeworkType}`}
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              border: '2px solid #e2e8f0',
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}
                          />
                        </div>

                        {/* Grid: Review, Max Scripts, Total Marks */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                          {/* Review Required */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                              Review Required
                            </label>
                            <div
                              onClick={() => toggleReviewSetting(homeworkType)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '10px',
                                background: 'white',
                                border: '2px solid #e2e8f0',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                userSelect: 'none'
                              }}
                            >
                              <div style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '4px',
                                border: '2px solid',
                                borderColor: reviewSettings[homeworkType] ? '#1b998b' : '#bdc3c7',
                                background: reviewSettings[homeworkType] ? '#1b998b' : 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '14px',
                                flexShrink: 0
                              }}>
                                {reviewSettings[homeworkType] && <Check size={16} />}
                              </div>
                              <span style={{ fontSize: '13px', color: '#4a5568' }}>
                                {reviewSettings[homeworkType] ? 'Yes' : 'No'}
                              </span>
                            </div>
                          </div>

                          {/* Max Scripts */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                              Max per Marker
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={markerCaps[homeworkType] || ''}
                              onChange={(e) => updateMarkerCap(homeworkType, e.target.value)}
                              placeholder="Unlimited"
                              style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: '2px solid #e2e8f0',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            />
                          </div>

                          {/* Total Marks - calculated, not typed in (see calculateHomeworkTotalMarks) */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                              Total Marks
                            </label>
                            <div
                              title="Calculated from the Section Structure below (technical + professional marks, groups and additional reasons) - not editable directly."
                              style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: '2px solid #e2e8f0',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#2c3e50',
                                background: '#f7fafc',
                                boxSizing: 'border-box'
                              }}
                            >
                              {maxMarks[homeworkType] || 0}
                            </div>
                          </div>
                        </div>

                        {/* Model Solution - shown to students alongside their marked-up homework once it's released */}
                        <div style={{ marginTop: '16px' }}>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                            Model Solution
                          </label>
                          {solutions[homeworkType] ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: 'white', border: '2px solid #e2e8f0', borderRadius: '6px' }}>
                              <FileText size={16} color="#667eea" />
                              <span style={{ flex: 1, fontSize: '13px', color: '#4a5568' }}>{solutions[homeworkType].fileName}</span>
                              <label style={{ cursor: uploadingSolutionKey === homeworkType ? 'not-allowed' : 'pointer' }}>
                                <input
                                  type="file"
                                  accept=".pdf"
                                  style={{ display: 'none' }}
                                  disabled={uploadingSolutionKey === homeworkType}
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) handleUploadSolution(homeworkType, file);
                                    e.target.value = '';
                                  }}
                                />
                                <span style={{ fontSize: '13px', color: '#667eea', fontWeight: '600' }}>
                                  {uploadingSolutionKey === homeworkType ? 'Uploading…' : 'Replace'}
                                </span>
                              </label>
                              <button
                                onClick={() => handleDeleteSolution(homeworkType)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', display: 'flex', alignItems: 'center' }}
                                title="Delete model solution"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ) : (
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 16px', background: uploadingSolutionKey === homeworkType ? '#a0aec0' : '#667eea', color: 'white', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: uploadingSolutionKey === homeworkType ? 'not-allowed' : 'pointer' }}>
                              <input
                                type="file"
                                accept=".pdf"
                                style={{ display: 'none' }}
                                disabled={uploadingSolutionKey === homeworkType}
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) handleUploadSolution(homeworkType, file);
                                  e.target.value = '';
                                }}
                              />
                              <Upload size={14} />
                              {uploadingSolutionKey === homeworkType ? 'Uploading…' : 'Upload Solution'}
                            </label>
                          )}
                          <div style={{ marginTop: '10px' }}>
                            <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '6px' }}>
                              Always requires this student's own {homeworkType} to be marked and returned first. On top of that:
                            </div>
                            <div style={{ display: 'inline-flex', border: '2px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
                              <button
                                onClick={() => setSolutionWaitsForDeadline(homeworkType, false)}
                                style={{
                                  padding: '8px 14px',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  background: solutionReleaseSettings[homeworkType] === false ? '#1b998b' : 'white',
                                  color: solutionReleaseSettings[homeworkType] === false ? 'white' : '#4a5568'
                                }}
                              >
                                As soon as marked
                              </button>
                              <button
                                onClick={() => setSolutionWaitsForDeadline(homeworkType, true)}
                                style={{
                                  padding: '8px 14px',
                                  border: 'none',
                                  borderLeft: '2px solid #e2e8f0',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  background: solutionReleaseSettings[homeworkType] !== false ? '#1b998b' : 'white',
                                  color: solutionReleaseSettings[homeworkType] !== false ? 'white' : '#4a5568'
                                }}
                              >
                                Only after the deadline passes
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Question Paper - shown to a student who uploads a completed PDF instead of using the exam simulator, so they can see what's being asked */}
                        <div style={{ marginTop: '16px' }}>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                            Question Paper
                          </label>
                          {questionPapers[homeworkType] ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: 'white', border: '2px solid #e2e8f0', borderRadius: '6px' }}>
                              <FileText size={16} color="#667eea" />
                              <span style={{ flex: 1, fontSize: '13px', color: '#4a5568' }}>{questionPapers[homeworkType].fileName}</span>
                              <label style={{ cursor: uploadingQuestionPaperKey === homeworkType ? 'not-allowed' : 'pointer' }}>
                                <input
                                  type="file"
                                  accept=".pdf"
                                  style={{ display: 'none' }}
                                  disabled={uploadingQuestionPaperKey === homeworkType}
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) handleUploadQuestionPaper(homeworkType, file);
                                    e.target.value = '';
                                  }}
                                />
                                <span style={{ fontSize: '13px', color: '#667eea', fontWeight: '600' }}>
                                  {uploadingQuestionPaperKey === homeworkType ? 'Uploading…' : 'Replace'}
                                </span>
                              </label>
                              <button
                                onClick={() => handleDeleteQuestionPaper(homeworkType)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', display: 'flex', alignItems: 'center' }}
                                title="Delete question paper"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ) : (
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 16px', background: uploadingQuestionPaperKey === homeworkType ? '#a0aec0' : '#667eea', color: 'white', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: uploadingQuestionPaperKey === homeworkType ? 'not-allowed' : 'pointer' }}>
                              <input
                                type="file"
                                accept=".pdf"
                                style={{ display: 'none' }}
                                disabled={uploadingQuestionPaperKey === homeworkType}
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) handleUploadQuestionPaper(homeworkType, file);
                                  e.target.value = '';
                                }}
                              />
                              <Upload size={14} />
                              {uploadingQuestionPaperKey === homeworkType ? 'Uploading…' : 'Upload Question Paper'}
                            </label>
                          )}
                          <div style={{ marginTop: '6px', fontSize: '12px', color: '#7f8c8d' }}>
                            Shown to students on the "Start or Upload Homework" panel when they upload a completed PDF for this homework type (not shown for exam-simulation submissions, since they already see the questions in the app). Same paper for every cohort.
                          </div>
                        </div>

                        {/* Grid: Submission Mode, Exam Duration, Unlimited Time, Breakdown Screen, Interactive Spreadsheet Marking, Spreadsheet Attachment */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '16px', marginTop: '16px' }}>
                          {/* Submission Mode */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                              Submission Mode
                            </label>
                            <select
                              value={submissionModes[homeworkType] || 'pdf'}
                              onChange={(e) => updateSubmissionMode(homeworkType, e.target.value)}
                              style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: '2px solid #e2e8f0',
                                borderRadius: '6px',
                                fontSize: '14px',
                                background: 'white',
                                cursor: 'pointer'
                              }}
                            >
                              <option value="pdf">PDF Upload</option>
                              <option value="exam">Exam Simulation (Timed)</option>
                              <option value="spreadsheet">Spreadsheet Upload (.xlsx)</option>
                            </select>
                            <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '4px' }}>
                              Default for every cohort. Individual cohorts can override this in Deadlines.
                            </div>
                          </div>

                          {/* Exam Duration */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                              Exam Duration (minutes)
                            </label>
                            <input
                              type="number"
                              min="1"
                              disabled={!!examUnlimitedDurations[homeworkType]}
                              value={examDurations[homeworkType] || 90}
                              onChange={(e) => updateExamDuration(homeworkType, e.target.value)}
                              style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: '2px solid #e2e8f0',
                                borderRadius: '6px',
                                fontSize: '14px',
                                background: examUnlimitedDurations[homeworkType] ? '#f1f3f5' : 'white',
                                color: examUnlimitedDurations[homeworkType] ? '#a0aec0' : 'inherit'
                              }}
                            />
                            <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '4px' }}>
                              Only used when this homework is in Exam Simulation mode.
                            </div>
                          </div>

                          {/* Unlimited Time */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                              Unlimited Time
                            </label>
                            <div
                              onClick={() => toggleExamUnlimited(homeworkType)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '10px',
                                background: 'white',
                                border: '2px solid #e2e8f0',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                userSelect: 'none'
                              }}
                            >
                              <div style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '4px',
                                border: '2px solid',
                                borderColor: examUnlimitedDurations[homeworkType] ? '#1b998b' : '#bdc3c7',
                                background: examUnlimitedDurations[homeworkType] ? '#1b998b' : 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '14px',
                                flexShrink: 0
                              }}>
                                {examUnlimitedDurations[homeworkType] && <Check size={16} />}
                              </div>
                              <span style={{ fontSize: '13px', color: '#4a5568' }}>
                                {examUnlimitedDurations[homeworkType] ? 'No time limit' : 'Off'}
                              </span>
                            </div>
                            <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '4px' }}>
                              Students can take as long as they need; only a manual submit ends the exam.
                            </div>
                          </div>

                          {/* Breakdown Screen */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                              Breakdown Screen
                            </label>
                            <div
                              onClick={() => toggleExamShowBreakdownScreen(homeworkType)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '10px',
                                background: 'white',
                                border: '2px solid #e2e8f0',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                userSelect: 'none'
                              }}
                            >
                              <div style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '4px',
                                border: '2px solid',
                                borderColor: examShowBreakdownScreen[homeworkType] ? '#1b998b' : '#bdc3c7',
                                background: examShowBreakdownScreen[homeworkType] ? '#1b998b' : 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '14px',
                                flexShrink: 0
                              }}>
                                {examShowBreakdownScreen[homeworkType] && <Check size={16} />}
                              </div>
                              <span style={{ fontSize: '13px', color: '#4a5568' }}>
                                {examShowBreakdownScreen[homeworkType] ? 'On' : 'Off'}
                              </span>
                            </div>
                            <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '4px' }}>
                              Shows a standalone "what's expected" screen before each question, in addition to the same info in the split screen.
                            </div>
                          </div>

                          {/* Interactive Spreadsheet Marking */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                              Interactive Spreadsheet Marking
                            </label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              {[
                                { mode: 'pdf', title: 'PDF only', text: 'Spreadsheets are marked on the PDF like any other page.' },
                                { mode: 'spreadsheet', title: 'Spreadsheet only', text: 'Marked on the spreadsheet itself. The PDF says "See Spreadsheet 1" where the spreadsheet would be, and the marked spreadsheet goes in that place in the student\'s marked script.' },
                                { mode: 'both', title: 'PDF and spreadsheet', text: 'Marked on the spreadsheet itself, and the spreadsheet stays in the PDF too. The marked spreadsheets go after the student\'s pages.' }
                              ].map(option => {
                                const selected = spreadsheetMarkingMode(examInteractiveSpreadsheetMarking[homeworkType]) === option.mode;
                                return (
                                  <div
                                    key={option.mode}
                                    onClick={() => setExamSpreadsheetMarkingMode(homeworkType, option.mode)}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: '10px',
                                      padding: '10px',
                                      background: 'white',
                                      border: `2px solid ${selected ? '#1b998b' : '#e2e8f0'}`,
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      userSelect: 'none'
                                    }}
                                  >
                                    <div style={{
                                      width: '18px',
                                      height: '18px',
                                      borderRadius: '50%',
                                      border: `2px solid ${selected ? '#1b998b' : '#bdc3c7'}`,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0,
                                      marginTop: '1px'
                                    }}>
                                      {selected && <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#1b998b' }} />}
                                    </div>
                                    <div>
                                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#2c3e50' }}>{option.title}</div>
                                      <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '2px' }}>{option.text}</div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '4px' }}>
                              For the exam spreadsheet, and for Excel files students upload (those are always marked on the spreadsheet, so "PDF only" works like "PDF and spreadsheet" for them). Applies to work handed in after you change it.
                            </div>
                          </div>

                          {/* Spreadsheet Attachment */}
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                              Spreadsheet Attachment
                            </label>
                            <div
                              onClick={() => togglePdfSpreadsheetAttachmentEnabled(homeworkType)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '10px',
                                background: 'white',
                                border: '2px solid #e2e8f0',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                userSelect: 'none'
                              }}
                            >
                              <div style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '4px',
                                border: '2px solid',
                                borderColor: pdfSpreadsheetAttachmentEnabled[homeworkType] ? '#1b998b' : '#bdc3c7',
                                background: pdfSpreadsheetAttachmentEnabled[homeworkType] ? '#1b998b' : 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '14px',
                                flexShrink: 0
                              }}>
                                {pdfSpreadsheetAttachmentEnabled[homeworkType] && <Check size={16} />}
                              </div>
                              <span style={{ fontSize: '13px', color: '#4a5568' }}>
                                {pdfSpreadsheetAttachmentEnabled[homeworkType] ? 'On' : 'Off'}
                              </span>
                            </div>
                            <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '4px' }}>
                              Adds an optional spreadsheet upload alongside the PDF, so a student can submit either, or both together. Only used when this homework is in PDF Upload mode.
                            </div>
                          </div>
                        </div>
                      </SettingsPanel>

                      {/* Section Structure */}
                      <SettingsPanel
                        title="Section Structure"
                        summary={`${countLabel(structure.sections, 'section')}, ${totalMarks} marks`}
                        {...panel('sections')}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                          <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                            ID = button label for markers | Name = displayed in sidebar
                          </div>
                          <button
                            onClick={() => resetToDefaultSections(homeworkType)}
                            style={{
                              padding: '6px 12px',
                              background: '#95a5a6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '12px'
                            }}
                          >
                            Reset to A/B/C
                          </button>
                        </div>

                        <div style={{ display: 'grid', gap: '10px', marginBottom: '12px' }}>
                          {structure.sections.map((section, index) => {
                            const sectionGroupIndex = (structure.professionalMarkGroups || []).findIndex(g => g.sections.includes(section.id));
                            const isInGroup = sectionGroupIndex !== -1;
                            const technicalGroupIndex = (structure.technicalMarkGroups || []).findIndex(g => g.sections.includes(section.id));
                            const isInTechnicalGroup = technicalGroupIndex !== -1;
                            return (
                            <div
                              key={index}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '80px 1fr 120px 40px',
                                gap: '10px',
                                alignItems: 'center',
                                padding: '10px',
                                background: 'white',
                                borderRadius: '6px'
                              }}
                            >
                              <input
                                type="text"
                                value={section.id}
                                onChange={(e) => updateSectionId(homeworkType, index, e.target.value)}
                                placeholder="ID"
                                maxLength={5}
                                style={{
                                  padding: '8px 10px',
                                  border: '2px solid #667eea',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  textAlign: 'center',
                                  color: '#667eea'
                                }}
                              />
                              
                              <input
                                type="text"
                                value={section.name}
                                onChange={(e) => updateSectionName(homeworkType, index, e.target.value)}
                                placeholder="Section name"
                                style={{
                                  padding: '8px 10px',
                                  border: '2px solid #e2e8f0',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  fontWeight: '500'
                                }}
                              />
                              
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input
                                      type="number"
                                      min="0"
                                      max="50"
                                      step="0.5"
                                      value={section.maxTechnical ?? section.maxMark ?? 0}
                                      onChange={(e) => updateSectionMaxTechnical(homeworkType, index, e.target.value)}
                                      style={{
                                        width: '50px',
                                        padding: '6px',
                                        border: '2px solid #445e93',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        textAlign: 'center'
                                      }}
                                    />
                                    <span style={{ fontSize: '11px', color: '#445e93', fontWeight: '600' }}>T</span>
                                    {isInTechnicalGroup && (
                                      <span style={{ fontSize: '10px', color: '#445e93', fontWeight: '700', background: '#eef1fa', border: '1px dashed #445e93', borderRadius: '4px', padding: '2px 5px' }}>
                                        Grp {technicalGroupIndex + 1}
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    {isInGroup ? (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <div style={{ width: '50px', padding: '6px', border: '2px dashed #f39c12', borderRadius: '6px', fontSize: '11px', fontWeight: '600', textAlign: 'center', color: '#f39c12', background: '#fef9f0' }}>
                                          Grp {sectionGroupIndex + 1}
                                        </div>
                                        <span style={{ fontSize: '11px', color: '#f39c12', fontWeight: '600' }}>P</span>
                                      </div>
                                    ) : (
                                      <>
                                        <input
                                          type="number"
                                          min="0"
                                          max="10"
                                          value={section.maxProfessional ?? 1}
                                          onChange={(e) => updateSectionMaxProfessional(homeworkType, index, e.target.value)}
                                          style={{
                                            width: '50px',
                                            padding: '6px',
                                            border: '2px solid #f39c12',
                                            borderRadius: '6px',
                                            fontSize: '13px',
                                            fontWeight: '600',
                                            textAlign: 'center'
                                          }}
                                        />
                                        <span style={{ fontSize: '11px', color: '#f39c12', fontWeight: '600' }}>P</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              <button
                                onClick={() => removeSection(homeworkType, index)}
                                disabled={structure.sections.length <= 1}
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  background: structure.sections.length <= 1 ? '#e2e8f0' : '#e74c3c',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: structure.sections.length <= 1 ? 'not-allowed' : 'pointer',
                                  fontSize: '16px',
                                  fontWeight: 'bold'
                                }}
                              >
                                -
                              </button>
                            </div>
                            );
                          })}
                        </div>

                        {structure.sections.length < 26 && (
                          <button
                            onClick={() => addSection(homeworkType)}
                            style={{
                              width: '100%',
                              padding: '10px',
                              background: '#667eea',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '14px'
                            }}
                          >
                            + Add Section
                          </button>
                        )}

                      </SettingsPanel>

                      {/* Technical Mark Groups */}
                      <SettingsPanel
                        title="Technical Mark Groups"
                        summary={countLabel(structure.technicalMarkGroups || [], 'group')}
                        {...panel('tech-groups')}
                      >
                      <p style={{ fontSize: '12px', color: '#7f8c8d', margin: '0 0 12px 0' }}>
                        Use when a question's parts have technical marks that don't sum to the question total (e.g. part (i) up to 5, part (ii) up to 4, but the question is capped at 7 overall). Each section's own T limit above still applies — the group caps their combined total on top of that.
                      </p>

                          {(structure.technicalMarkGroups || []).length === 0 ? (
                            <p style={{ fontSize: '13px', color: '#95a5a6', fontStyle: 'italic', marginBottom: '12px' }}>
                              No groups defined — technical marks use per-section T limits above.
                            </p>
                          ) : (
                            <div style={{ display: 'grid', gap: '10px', marginBottom: '12px' }}>
                              {(structure.technicalMarkGroups || []).map((group, groupIndex) => (
                                <div key={groupIndex} style={{ background: 'white', border: '2px solid #445e93', borderRadius: '8px', padding: '14px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                      <span style={{ fontSize: '13px', fontWeight: '700', color: '#2c3e50' }}>Group {groupIndex + 1}</span>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <input
                                          type="number"
                                          min="0.5"
                                          max="20"
                                          step="0.5"
                                          value={group.totalMarks}
                                          onChange={(e) => updateTechnicalGroupTotalMarks(homeworkType, groupIndex, e.target.value)}
                                          style={{ width: '52px', padding: '5px 6px', border: '2px solid #445e93', borderRadius: '6px', fontSize: '14px', fontWeight: '700', textAlign: 'center', color: '#445e93' }}
                                        />
                                        <span style={{ fontSize: '12px', color: '#7f8c8d', fontWeight: '600' }}>marks total</span>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => removeTechnicalMarkGroup(homeworkType, groupIndex)}
                                      style={{ background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  <div style={{ marginBottom: '12px' }}>
                                    <div style={{ fontSize: '12px', color: '#7f8c8d', fontWeight: '600', marginBottom: '5px' }}>Display name (shown to students and markers):</div>
                                    <input
                                      type="text"
                                      value={group.name || ''}
                                      onChange={(e) => updateTechnicalGroupName(homeworkType, groupIndex, e.target.value)}
                                      placeholder={`e.g. Question A`}
                                      style={{ width: '100%', padding: '7px 10px', border: '2px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                                    />
                                  </div>
                                  <div>
                                    <div style={{ fontSize: '12px', color: '#7f8c8d', fontWeight: '600', marginBottom: '8px' }}>Sections covered:</div>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                      {structure.sections.map(section => {
                                        const inThisGroup = group.sections.includes(section.id);
                                        const inOtherGroup = !inThisGroup && (structure.technicalMarkGroups || []).some((g, gi) => gi !== groupIndex && g.sections.includes(section.id));
                                        return (
                                          <label
                                            key={section.id}
                                            style={{
                                              display: 'flex', alignItems: 'center', gap: '5px', cursor: inOtherGroup ? 'not-allowed' : 'pointer',
                                              padding: '5px 12px', borderRadius: '6px', userSelect: 'none',
                                              background: inThisGroup ? '#eef1fa' : inOtherGroup ? '#f8f9fa' : 'white',
                                              border: `2px solid ${inThisGroup ? '#445e93' : '#e2e8f0'}`,
                                              opacity: inOtherGroup ? 0.5 : 1
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={inThisGroup}
                                              disabled={inOtherGroup}
                                              onChange={() => !inOtherGroup && toggleSectionInTechnicalGroup(homeworkType, groupIndex, section.id)}
                                              style={{ cursor: inOtherGroup ? 'not-allowed' : 'pointer' }}
                                            />
                                            <span style={{ fontSize: '13px', fontWeight: '700', color: inThisGroup ? '#445e93' : '#7f8c8d' }}>
                                              {section.id}
                                            </span>
                                            <span style={{ fontSize: '11px', color: '#95a5a6' }}>
                                              {section.name}
                                            </span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          <button
                            onClick={() => addTechnicalMarkGroup(homeworkType)}
                            style={{ padding: '8px 16px', background: '#445e93', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
                          >
                            + Add Technical Mark Group
                          </button>
                      </SettingsPanel>

                      {/* Professional Mark Groups */}
                      <SettingsPanel
                        title="Professional Mark Groups"
                        summary={countLabel(structure.professionalMarkGroups || [], 'group')}
                        {...panel('prof-groups')}
                      >
                      <p style={{ fontSize: '12px', color: '#7f8c8d', margin: '0 0 12px 0' }}>
                        Use when professional marks are shared across multiple sections. Groups override per-section P values for included sections.
                      </p>

                          {(structure.professionalMarkGroups || []).length === 0 ? (
                            <p style={{ fontSize: '13px', color: '#95a5a6', fontStyle: 'italic', marginBottom: '12px' }}>
                              No groups defined — professional marks use per-section P limits above.
                            </p>
                          ) : (
                            <div style={{ display: 'grid', gap: '10px', marginBottom: '12px' }}>
                              {(structure.professionalMarkGroups || []).map((group, groupIndex) => (
                                <div key={groupIndex} style={{ background: 'white', border: '2px solid #f39c12', borderRadius: '8px', padding: '14px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                      <span style={{ fontSize: '13px', fontWeight: '700', color: '#2c3e50' }}>Group {groupIndex + 1}</span>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <input
                                          type="number"
                                          min="1"
                                          max="20"
                                          value={group.totalMarks}
                                          onChange={(e) => updateGroupTotalMarks(homeworkType, groupIndex, e.target.value)}
                                          style={{ width: '52px', padding: '5px 6px', border: '2px solid #f39c12', borderRadius: '6px', fontSize: '14px', fontWeight: '700', textAlign: 'center', color: '#f39c12' }}
                                        />
                                        <span style={{ fontSize: '12px', color: '#7f8c8d', fontWeight: '600' }}>marks total</span>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => removeProfessionalMarkGroup(homeworkType, groupIndex)}
                                      style={{ background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  <div style={{ marginBottom: '12px' }}>
                                    <div style={{ fontSize: '12px', color: '#7f8c8d', fontWeight: '600', marginBottom: '5px' }}>Display name (shown to students and markers):</div>
                                    <input
                                      type="text"
                                      value={group.name || ''}
                                      onChange={(e) => updateGroupName(homeworkType, groupIndex, e.target.value)}
                                      placeholder={`e.g. Question 2`}
                                      style={{ width: '100%', padding: '7px 10px', border: '2px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                                    />
                                  </div>
                                  <div>
                                    <div style={{ fontSize: '12px', color: '#7f8c8d', fontWeight: '600', marginBottom: '8px' }}>Sections covered:</div>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                      {structure.sections.map(section => {
                                        const inThisGroup = group.sections.includes(section.id);
                                        const inOtherGroup = !inThisGroup && (structure.professionalMarkGroups || []).some((g, gi) => gi !== groupIndex && g.sections.includes(section.id));
                                        return (
                                          <label
                                            key={section.id}
                                            style={{
                                              display: 'flex', alignItems: 'center', gap: '5px', cursor: inOtherGroup ? 'not-allowed' : 'pointer',
                                              padding: '5px 12px', borderRadius: '6px', userSelect: 'none',
                                              background: inThisGroup ? '#fef9f0' : inOtherGroup ? '#f8f9fa' : 'white',
                                              border: `2px solid ${inThisGroup ? '#f39c12' : inOtherGroup ? '#e2e8f0' : '#e2e8f0'}`,
                                              opacity: inOtherGroup ? 0.5 : 1
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={inThisGroup}
                                              disabled={inOtherGroup}
                                              onChange={() => !inOtherGroup && toggleSectionInGroup(homeworkType, groupIndex, section.id)}
                                              style={{ cursor: inOtherGroup ? 'not-allowed' : 'pointer' }}
                                            />
                                            <span style={{ fontSize: '13px', fontWeight: '700', color: inThisGroup ? '#f39c12' : '#7f8c8d' }}>
                                              {section.id}
                                            </span>
                                            <span style={{ fontSize: '11px', color: '#95a5a6' }}>
                                              {section.name}
                                            </span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </div>
                                  <div style={{ marginTop: '12px' }}>
                                    <label
                                      style={{
                                        display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer',
                                        padding: '5px 12px', borderRadius: '6px', userSelect: 'none', width: 'fit-content',
                                        background: group.includeAdditional ? '#fef9f0' : 'white',
                                        border: `2px solid ${group.includeAdditional ? '#f39c12' : '#e2e8f0'}`
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={!!group.includeAdditional}
                                        onChange={() => toggleGroupIncludesAdditional(homeworkType, groupIndex)}
                                        style={{ cursor: 'pointer' }}
                                      />
                                      <span style={{ fontSize: '13px', fontWeight: '600', color: group.includeAdditional ? '#f39c12' : '#7f8c8d' }}>
                                        Include Additional Professional Marks in this group's total
                                      </span>
                                    </label>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          <button
                            onClick={() => addProfessionalMarkGroup(homeworkType)}
                            style={{ padding: '8px 16px', background: '#f39c12', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
                          >
                            + Add Professional Mark Group
                          </button>
                      </SettingsPanel>

                      {/* Professional Mark Reasons per Section */}
                      <SettingsPanel
                        title="Professional Mark Reasons"
                        summary={countLabel(sectionsWithProfMarks.flatMap(s => s.professionalReasons || []), 'reason')}
                        {...panel('prof-reasons')}
                      >
                        <p style={{ fontSize: '12px', color: '#7f8c8d', margin: '0 0 16px 0' }}>
                          Markers select from these reasons when awarding a professional mark in each section. Only sections with professional marks are shown.
                        </p>
                        {sectionsWithProfMarks.length === 0 && (
                          <p style={{ fontSize: '13px', color: '#95a5a6', fontStyle: 'italic', margin: 0 }}>
                            No sections have professional marks.
                          </p>
                        )}
                        {structure.sections.map((section, sectionIndex) => (section.maxProfessional ?? 1) > 0 && (
                            <ProfessionalReasonsEditor
                              key={section.id}
                              label={section.name}
                              reasons={section.professionalReasons || []}
                              borderColor="#f39c12"
                              bgColor="#fef9f0"
                              onAdd={(reason) => addProfessionalReason(homeworkType, sectionIndex, reason)}
                              onRemove={(rIdx) => removeProfessionalReason(homeworkType, sectionIndex, rIdx)}
                            />
                          ))}
                      </SettingsPanel>

                      {/* Additional Professional Mark Reasons */}
                      <SettingsPanel
                        title="Additional Professional Mark Reasons"
                        summary={countLabel(structure.additionalProfessionalReasons || [], 'reason')}
                        {...panel('additional-reasons')}
                      >
                          <p style={{ fontSize: '12px', color: '#7f8c8d', margin: '0 0 16px 0' }}>
                            Used for professional marks not tied to a specific section (paper-wide marks).
                          </p>
                          <ProfessionalReasonsEditor
                            label="Additional Professional Marks"
                            reasons={structure.additionalProfessionalReasons || []}
                            borderColor="#7b2d8b"
                            bgColor="#f9f0fc"
                            onAdd={(reason) => addAdditionalProfessionalReason(homeworkType, reason)}
                            onRemove={(rIdx) => removeAdditionalProfessionalReason(homeworkType, rIdx)}
                          />
                      </SettingsPanel>

                      {/* Exam Content - only relevant once this homework is in Exam mode */}
                      {submissionModes[homeworkType] === 'exam' && (
                        <SettingsPanel
                          title="Exam Content"
                          description="Shown to students taking this homework in Exam mode."
                          {...panel('exam')}
                        >
                          <SettingsPanel
                            nested
                            title="Task Summary"
                            description="Shown before the timer starts"
                            summary={hasHtmlText(structure.examSummary) ? 'Added' : 'Blank'}
                            {...panel('exam-summary')}
                          >
                            <ExamRichTextEditor
                              value={structure.examSummary || ''}
                              onChange={(html) => updateExamSummary(homeworkType, html)}
                              placeholder="Describe the overall task for students..."
                            />
                          </SettingsPanel>

                          {/* Question Groups - combine sections into one screen for the student */}
                          <SettingsPanel
                            nested
                            title="Combined Questions"
                            summary={countLabel(structure.groups || [], 'combined question')}
                            {...panel('exam-groups')}
                          >
                            <p style={{ fontSize: '12px', color: '#7f8c8d', margin: '0 0 12px 0' }}>
                              Combine parts of the same question (e.g. Section A and B as parts (i) and (ii) of Question 2) into one screen for the student, with one shared Word Processor/Spreadsheet response instead of separate ones per part. Marking is unaffected - sections are still marked separately, the student just labels which part they're answering within their own answer.
                            </p>

                            {(structure.groups || []).length === 0 ? (
                              <p style={{ fontSize: '13px', color: '#95a5a6', fontStyle: 'italic', marginBottom: '12px' }}>
                                No combined questions - each section is its own screen for the student.
                              </p>
                            ) : (
                              <div style={{ display: 'grid', gap: '10px', marginBottom: '12px' }}>
                                {structure.groups.map((group, groupIndex) => (
                                  <div key={group.id} style={{ background: 'white', border: '2px solid #16a085', borderRadius: '8px', padding: '14px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                      <span style={{ fontSize: '13px', fontWeight: '700', color: '#2c3e50' }}>Combined Question {groupIndex + 1}</span>
                                      <button
                                        onClick={() => removeQuestionGroup(homeworkType, groupIndex)}
                                        style={{ background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                                      >
                                        Remove
                                      </button>
                                    </div>
                                    <div style={{ marginBottom: '12px' }}>
                                      <div style={{ fontSize: '12px', color: '#7f8c8d', fontWeight: '600', marginBottom: '5px' }}>Display name (shown to students):</div>
                                      <input
                                        type="text"
                                        value={group.name || ''}
                                        onChange={(e) => updateQuestionGroupName(homeworkType, groupIndex, e.target.value)}
                                        placeholder="e.g. Question 2"
                                        style={{ width: '100%', padding: '7px 10px', border: '2px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                                      />
                                    </div>
                                    <div style={{ marginBottom: '12px' }}>
                                      <div style={{ fontSize: '12px', color: '#7f8c8d', fontWeight: '600', marginBottom: '5px' }}>
                                        Whole-question breakdown <span style={{ fontWeight: '400' }}>(optional - overrides the individual part breakdowns below if filled in)</span>
                                      </div>
                                      <ExamRichTextEditor
                                        value={group.breakdown || ''}
                                        onChange={(html) => updateQuestionGroupBreakdown(homeworkType, groupIndex, html)}
                                        placeholder="Leave blank to show each part's own breakdown instead..."
                                      />
                                    </div>
                                    {examShowBreakdownScreen[homeworkType] && (
                                      <div style={{ marginBottom: '12px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#7f8c8d', fontWeight: '600', cursor: 'pointer', userSelect: 'none', marginBottom: group.showBreakdownScreen !== false ? '10px' : 0 }}>
                                          <input
                                            type="checkbox"
                                            checked={group.showBreakdownScreen !== false}
                                            onChange={() => toggleQuestionGroupShowBreakdownScreen(homeworkType, groupIndex)}
                                          />
                                          Show Breakdown Screen before this question
                                        </label>
                                        {group.showBreakdownScreen !== false && (
                                          <>
                                            <div style={{ fontSize: '12px', color: '#7f8c8d', fontWeight: '600', marginBottom: '5px' }}>
                                              Whole-question Breakdown Screen text <span style={{ fontWeight: '400' }}>(optional - leave blank to reuse the whole-question breakdown above, or each part's own Breakdown Screen text)</span>
                                            </div>
                                            <ExamRichTextEditor
                                              value={group.breakdownScreenText || ''}
                                              onChange={(html) => updateQuestionGroupBreakdownScreenText(homeworkType, groupIndex, html)}
                                              placeholder="Leave blank to reuse the whole-question breakdown above..."
                                            />
                                          </>
                                        )}
                                      </div>
                                    )}
                                    <div>
                                      <div style={{ fontSize: '12px', color: '#7f8c8d', fontWeight: '600', marginBottom: '8px' }}>Sections covered:</div>
                                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        {structure.sections.map(section => {
                                          const inThisGroup = group.sectionIds.includes(section.id);
                                          const inOtherGroup = !inThisGroup && (structure.groups || []).some((g, gi) => gi !== groupIndex && g.sectionIds.includes(section.id));
                                          return (
                                            <label
                                              key={section.id}
                                              style={{
                                                display: 'flex', alignItems: 'center', gap: '5px', cursor: inOtherGroup ? 'not-allowed' : 'pointer',
                                                padding: '5px 12px', borderRadius: '6px', userSelect: 'none',
                                                background: inThisGroup ? '#eafaf5' : inOtherGroup ? '#f8f9fa' : 'white',
                                                border: `2px solid ${inThisGroup ? '#16a085' : '#e2e8f0'}`,
                                                opacity: inOtherGroup ? 0.5 : 1
                                              }}
                                            >
                                              <input
                                                type="checkbox"
                                                checked={inThisGroup}
                                                disabled={inOtherGroup}
                                                onChange={() => !inOtherGroup && toggleSectionInQuestionGroup(homeworkType, groupIndex, section.id)}
                                                style={{ cursor: inOtherGroup ? 'not-allowed' : 'pointer' }}
                                              />
                                              <span style={{ fontSize: '13px', fontWeight: '700', color: inThisGroup ? '#16a085' : '#7f8c8d' }}>
                                                {section.id}
                                              </span>
                                              <span style={{ fontSize: '11px', color: '#95a5a6' }}>
                                                {section.name}
                                              </span>
                                            </label>
                                          );
                                        })}
                                      </div>
                                    </div>
                                    {/* The whole question's own content - anything filled in here is all
                                        students see for that item; left blank, each part's own (below) is used */}
                                    <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '2px solid #e2e8f0' }}>
                                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#2c3e50', marginBottom: '4px' }}>Content for the whole question (optional)</div>
                                      <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '8px' }}>
                                        Anything added here is all students see for that item - e.g. exhibits here replace the parts' exhibits. Leave an item blank to keep using what's on the individual parts under Individual Questions.
                                      </div>
                                      <ExamMaterialList
                                        label="Exhibits"
                                        items={group.exhibits || []}
                                        uploading={uploadingMaterialKey === `${homeworkType}-${group.id}-exhibits`}
                                        onUpload={(file) => handleUploadExamMaterial(homeworkType, group.id, 'exhibits', file, 'groups')}
                                        onDelete={(itemId) => handleDeleteExamMaterial(homeworkType, group.id, 'exhibits', itemId, 'groups')}
                                        onAddText={(title, content) => handleAddExamMaterialText(homeworkType, group.id, 'exhibits', title, content, 'groups')}
                                      />
                                      <ExamMaterialList
                                        label="Requirements"
                                        items={group.requirements || []}
                                        uploading={uploadingMaterialKey === `${homeworkType}-${group.id}-requirements`}
                                        onUpload={(file) => handleUploadExamMaterial(homeworkType, group.id, 'requirements', file, 'groups')}
                                        onDelete={(itemId) => handleDeleteExamMaterial(homeworkType, group.id, 'requirements', itemId, 'groups')}
                                        onAddText={(title, content) => handleAddExamMaterialText(homeworkType, group.id, 'requirements', title, content, 'groups')}
                                      />
                                      {renderStartingContentEditors(group, `${homeworkType}-${group.id}`, groupStartingContentSetters(homeworkType, groupIndex))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            <button
                              onClick={() => addQuestionGroup(homeworkType)}
                              style={{ padding: '8px 16px', background: '#16a085', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
                            >
                              + Add Combined Question
                            </button>
                          </SettingsPanel>

                          <SettingsPanel
                            nested
                            title="Individual Questions"
                            summary={countLabel(structure.sections, 'question')}
                            {...panel('exam-questions')}
                          >
                          {structure.sections.map(section => (
                            <SettingsPanel
                              key={section.id}
                              nested
                              title={section.name}
                              summary={examQuestionSummary(section)}
                              {...panel(`exam-q-${section.id}`)}
                            >

                              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px' }}>
                                Question Breakdown
                              </label>
                              <div style={{ marginBottom: '14px' }}>
                                <ExamRichTextEditor
                                  value={section.breakdown || ''}
                                  onChange={(html) => updateSectionBreakdown(homeworkType, section.id, html)}
                                  placeholder="What's expected for this question..."
                                />
                              </div>

                              {examShowBreakdownScreen[homeworkType] && (
                                <div style={{ marginBottom: '14px' }}>
                                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px' }}>
                                    Breakdown Screen Text <span style={{ fontWeight: '400', color: '#7f8c8d' }}>(optional - shown only on the standalone Breakdown Screen before this question; leave blank to reuse the Question Breakdown text above)</span>
                                  </label>
                                  <ExamRichTextEditor
                                    value={section.breakdownScreenText || ''}
                                    onChange={(html) => updateSectionBreakdownScreenText(homeworkType, section.id, html)}
                                    placeholder="Leave blank to show the same text as Question Breakdown above..."
                                  />
                                </div>
                              )}

                              <ExamMaterialList
                                label="Exhibits"
                                items={section.exhibits || []}
                                uploading={uploadingMaterialKey === `${homeworkType}-${section.id}-exhibits`}
                                onUpload={(file) => handleUploadExamMaterial(homeworkType, section.id, 'exhibits', file)}
                                onDelete={(itemId) => handleDeleteExamMaterial(homeworkType, section.id, 'exhibits', itemId)}
                                onAddText={(title, content) => handleAddExamMaterialText(homeworkType, section.id, 'exhibits', title, content)}
                              />
                              <ExamMaterialList
                                label="Requirements"
                                items={section.requirements || []}
                                uploading={uploadingMaterialKey === `${homeworkType}-${section.id}-requirements`}
                                onUpload={(file) => handleUploadExamMaterial(homeworkType, section.id, 'requirements', file)}
                                onDelete={(itemId) => handleDeleteExamMaterial(homeworkType, section.id, 'requirements', itemId)}
                                onAddText={(title, content) => handleAddExamMaterialText(homeworkType, section.id, 'requirements', title, content)}
                              />

                              {renderStartingContentEditors(section, `${homeworkType}-${section.id}`, {
                                setGrid: (value) => updateSectionStartingGrid(homeworkType, section.id, value),
                                setHtml: (value) => updateSectionStartingHtml(homeworkType, section.id, value),
                                setIncludeBlankSpreadsheet: (value) => updateSectionIncludeBlankSpreadsheet(homeworkType, section.id, value),
                                setIncludeBlankWordProcessor: (value) => updateSectionIncludeBlankWordProcessor(homeworkType, section.id, value)
                              })}
                            </SettingsPanel>
                          ))}
                          </SettingsPanel>
                        </SettingsPanel>
                      )}

                      {/* Comment Library */}
                      <SettingsPanel
                        title="Comment Library"
                        summary={countLabel({ length: structure.sections.reduce((n, sec) => n + commentCountFor(sec.id === 'final' ? 'final' : `section${sec.id}`), 0) + commentCountFor('final') }, 'comment')}
                        {...panel('comments')}
                      >
                        
                        {/* Section Comments */}
                        {structure.sections.map((section, idx) => {
                          const sectionKey = section.id === 'final' ? 'final' : `section${section.id}`;
                          const rawComments = commentBank[sectionKey] || [];
const comments = Array.isArray(rawComments) ? rawComments : (rawComments.comments || []);
                          
                          return (
                            <SettingsPanel
                              key={idx}
                              nested
                              title={section.name}
                              summary={countLabel(comments, 'comment')}
                              {...panel(`comments-${section.id}`)}
                            >
                              {comments.length === 0 ? (
                                <div style={{
                                  padding: '12px',
                                  background: 'white',
                                  border: '2px dashed #e2e8f0',
                                  borderRadius: '6px',
                                  color: '#95a5a6',
                                  fontSize: '13px',
                                  textAlign: 'center'
                                }}>
                                  No comments yet
                                </div>
                              ) : (
                                <div style={{
                                  padding: '12px',
                                  background: 'white',
                                  border: '2px solid #e2e8f0',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  maxHeight: '120px',
                                  overflowY: 'auto'
                                }}>
                                  {comments.slice(0, 3).map((comment, cidx) => (
                                    <div key={cidx} style={{
                                      padding: '6px 0',
                                      borderBottom: cidx < Math.min(2, comments.length - 1) ? '1px solid #f0f0f0' : 'none'
                                    }}>
                                      - {comment.text.substring(0, 60)}{comment.text.length > 60 ? '...' : ''}

                                    </div>
                                  ))}
                                  {comments.length > 3 && (
                                    <div style={{ padding: '6px 0', color: '#7f8c8d', fontStyle: 'italic' }}>
                                      +{comments.length - 3} more...
                                    </div>
                                  )}
                                </div>
                              )}
                            </SettingsPanel>
                          );
                        })}

                    

                        {/* Final Comments */}
                        <div>
                          {(() => {
                            const rawFinal = commentBank.final || [];
                            const finalComments = Array.isArray(rawFinal) ? rawFinal : (rawFinal.comments || []);
                            return (
                              <SettingsPanel
                                nested
                                title="Final Comments"
                                summary={countLabel(finalComments, 'comment')}
                                {...panel('comments-final')}
                              >
                                {finalComments.length === 0 ? (
                                  <div style={{
                                    padding: '12px',
                                    background: 'white',
                                    border: '2px dashed #e2e8f0',
                                    borderRadius: '6px',
                                    color: '#95a5a6',
                                    fontSize: '13px',
                                    textAlign: 'center'
                                  }}>
                                    No final comments yet
                                  </div>
                                ) : (
                                  <div style={{
                                    padding: '12px',
                                    background: 'white',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    maxHeight: '120px',
                                    overflowY: 'auto'
                                  }}>
                                    {finalComments.slice(0, 3).map((comment, cidx) => (
                                      <div key={cidx} style={{
                                        padding: '6px 0',
                                        borderBottom: cidx < Math.min(2, finalComments.length - 1) ? '1px solid #f0f0f0' : 'none'
                                      }}>
                                        - {comment.text.substring(0, 60)}{comment.text.length > 60 ? '...' : ''}
                                      </div>
                                    ))}
                                    {finalComments.length > 3 && (
                                      <div style={{ padding: '6px 0', color: '#7f8c8d', fontStyle: 'italic' }}>
                                        +{finalComments.length - 3} more...
                                      </div>
                                    )}
                                  </div>
                                )}
                              </SettingsPanel>
                            );
                          })()}
                       </div>

                        <button
                          onClick={() => setSettingsSubView('comments')}
                          style={{
                            width: '100%',
                            marginTop: '12px',
                            padding: '10px',
                            background: '#667eea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          <Edit3 size={16} />
                          Edit Comments in Library
                        </button>
                      </SettingsPanel>

                      {/* WhatsApp Template */}
                      <SettingsPanel
                        title="WhatsApp Message Template"
                        summary={template.trim() ? 'Set' : 'Blank'}
                        {...panel('whatsapp')}
                      >
                        <textarea
                          value={template}
                          onChange={(e) => updateWhatsAppTemplate(homeworkType, e.target.value)}
                          placeholder="Enter WhatsApp message template..."
                          rows={6}
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '2px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontFamily: 'inherit',
                            resize: 'vertical'
                          }}
                        />
                        <div style={{
                          marginTop: '10px',
                          padding: '10px',
                          background: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          fontSize: '11px',
                          color: '#7f8c8d'
                        }}>
                          <strong>Scores:</strong> {'{{total}}'}, {'{{technical}}'}, {'{{professional}}'}, {'{{maxMark}}'}, {'{{percentage}}'}<br/>
                          <strong>Per section totals:</strong> {'{{sectionBreakdown}}'}, {'{{sectionA}}'}, {'{{sectionB}}'}, {'{{sectionC}}'}<br/>
                          <strong>Per section/group technical:</strong> {'{{technicalBreakdown}}'}, {'{{technical_A}}'}, {'{{technical_B}}'}, {'{{technical_C}}'}, {'{{technicalGroup1}}'}, {'{{technicalGroup2}}'}<br/>
                          <strong>Per section/group professional:</strong> {'{{professionalBreakdown}}'}, {'{{professional_A}}'}, {'{{professional_B}}'}, {'{{professionalGroup1}}'}, {'{{professionalGroup2}}'}<br/>
                          <strong>Other:</strong> {'{{studentName}}'}, {'{{finalComment}}'}, {'{{homeworkType}}'}, {'{{homeworkName}}'}
                        </div>
                      </SettingsPanel>

                      {/* Questions for Final Comment Bank */}
                      <SettingsPanel
                        title="Questions - For final comments"
                        summary={countLabel(questionStructures[homeworkType] || [], 'question')}
                        {...panel('questions')}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                          <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                            Configure questions for the per-question final comment bank
                          </div>
                          <button
                            onClick={() => addQuestion(homeworkType)}
                            style={{
                              padding: '8px 16px',
                              background: '#667eea',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '13px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            <Plus size={14} />
                            Add Question
                          </button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {(questionStructures[homeworkType] || []).map((question, index) => (
                            <div key={question.id} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <span style={{
                                minWidth: '24px',
                                height: '24px',
                                background: '#667eea',
                                color: 'white',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                fontWeight: '700',
                                flexShrink: 0
                              }}>
                                {index + 1}
                              </span>
                              <input
                                type="text"
                                value={question.label}
                                onChange={(e) => updateQuestionLabel(homeworkType, question.id, e.target.value)}
                                placeholder={`e.g. Question ${index + 1}`}
                                style={{
                                  flex: 1,
                                  padding: '8px 12px',
                                  border: '2px solid #e2e8f0',
                                  borderRadius: '6px',
                                  fontSize: '14px'
                                }}
                              />
                              <button
                                onClick={() => removeQuestion(homeworkType, question.id)}
                                style={{
                                  padding: '8px',
                                  background: '#e74c3c',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ))}
                          {(questionStructures[homeworkType] || []).length === 0 && (
                            <div style={{
                              fontSize: '13px',
                              color: '#7f8c8d',
                              textAlign: 'center',
                              padding: '16px',
                              background: 'white',
                              borderRadius: '6px',
                              border: '1px dashed #e2e8f0'
                            }}>
                              No questions configured. Add questions to enable per-question final comments during marking.
                            </div>
                          )}
                        </div>
                      </SettingsPanel>

                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Save Button */}
          <button
            onClick={handleUpdateSettings}
            disabled={!canSaveSettings}
            style={{
              opacity: canSaveSettings ? 1 : 0.5,
              padding: '16px 32px',
              background: '#c33329',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '16px',
              boxShadow: '0 4px 12px rgba(195,51,41,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <ThumbsUp size={18} />
            Save All Settings
          </button>
            </div>
          )}

          {/* Comment Library Sub-view */}
          {settingsSubView === 'comments' && (
            <CommentBankManager user={user} />
          )}

          {/* Registration Settings Sub-view */}
          {/* Notifications */}
          {settingsSubView === 'notifications' && (
            <NotificationSettings />
          )}

          {/* Integrations - Google Sheets webhooks (Aileen only). Each URL
              saves when you click away from its box, as it did on the old
              Sync tab. The Sync button itself is on the Cohorts tab. */}
          {settingsSubView === 'integrations' && isAileenOrg && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <h2 style={{ margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Table size={22} />
                Integrations
              </h2>
              <p style={{ color: '#7f8c8d', marginBottom: '16px', fontSize: '14px' }}>
                Google Sheets connections. To send data, use Sync to Sheets on the Cohorts tab.
              </p>
              <div style={{ display: 'inline-block', padding: '3px 10px', background: '#f1f5f9', color: '#475569', borderRadius: '6px', fontSize: '12px', fontWeight: '600', marginBottom: '16px' }}>
                Each address saves when you click out of its box
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>
                  Student Data Sheet Webhook URL:
                </label>
                <input
                  type="text"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  onBlur={() => updateStudentDataWebhookUrl(webhookUrl).catch(err => console.error('Error saving webhook URL:', err))}
                  placeholder="https://script.google.com/macros/s/..."
                  style={{ width: '100%', padding: '12px', border: '2px solid #ecf0f1', borderRadius: '8px', fontSize: '14px', fontFamily: 'monospace', boxSizing: 'border-box' }}
                />
                <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '6px', fontStyle: 'italic' }}>
                  This is the Apps Script URL from your Student Data spreadsheet
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>
                  Per-Submission Webhook URL (optional):
                </label>
                <input
                  type="text"
                  value={submissionWebhookUrl}
                  onChange={(e) => setSubmissionWebhookUrl(e.target.value)}
                  onBlur={() => updateSubmissionWebhookUrl(submissionWebhookUrl).catch(err => console.error('Error saving submission webhook URL:', err))}
                  placeholder="https://script.google.com/macros/s/..."
                  style={{ width: '100%', padding: '12px', border: '2px solid #ecf0f1', borderRadius: '8px', fontSize: '14px', fontFamily: 'monospace', boxSizing: 'border-box' }}
                />
                <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '6px', fontStyle: 'italic' }}>
                  If set, fires a lightweight ping (student name + homework type) every time a student submits - separate from the sync. Leave blank to disable.
                </div>
              </div>
            </div>
          )}

          {settingsSubView === 'account' && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: '0 0 8px 0' }}>Registration Settings</h2>
              <div style={{ display: 'inline-block', padding: '3px 10px', background: '#f1f5f9', color: '#475569', borderRadius: '6px', fontSize: '12px', fontWeight: '600', marginBottom: '16px' }}>
                Saved with the Save Settings button below
              </div>
              <p style={{ color: '#7f8c8d', marginBottom: '24px', fontSize: '14px' }}>
                Control how new students sign up for the platform.
              </p>

              <div style={{
                padding: '20px',
                background: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  {/* Toggle switch */}
                  <button
                    onClick={() => setEmailVerificationRequired(!emailVerificationRequired)}
                    style={{
                      width: '52px',
                      height: '28px',
                      borderRadius: '14px',
                      border: 'none',
                      background: emailVerificationRequired ? '#1b998b' : '#bdc3c7',
                      cursor: 'pointer',
                      position: 'relative',
                      flexShrink: 0,
                      transition: 'background 0.2s'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '3px',
                      left: emailVerificationRequired ? '27px' : '3px',
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      background: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      transition: 'left 0.2s'
                    }} />
                  </button>
                  <div>
                    <div style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '4px' }}>
                      Require email verification on sign-up
                    </div>
                    <div style={{ fontSize: '13px', color: '#7f8c8d', lineHeight: '1.5' }}>
                      {emailVerificationRequired
                        ? 'ON — New students must verify their email address before they can log in. Recommended for live use.'
                        : 'OFF — New students can log in immediately after registering. Use this during testing.'}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{
                padding: '20px',
                background: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <div style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '4px' }}>
                  Student approval
                </div>
                <div style={{ fontSize: '13px', color: '#7f8c8d', lineHeight: '1.5', marginBottom: '12px' }}>
                  A student waiting for approval sees a "waiting for your tutor to confirm you" message and can't see any homework until you press Approve in the Cohorts tab.
                </div>
                {[
                  ['all', 'All new students', 'New registrations, and students from another tutor adding you, both need approving.'],
                  ['new', 'New registrations only', 'Students registering with your cohort code need approving. Students from another tutor adding you get straight in.'],
                  ['added', 'Added tutor only', 'Students from another tutor adding you need approving. New registrations get straight in.'],
                  ['none', 'No approval', 'Everyone gets straight in with your cohort code.']
                ].map(([value, label, help]) => (
                  <label key={value} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '8px 0', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="studentApprovalMode"
                      value={value}
                      checked={studentApprovalMode === value}
                      onChange={() => setStudentApprovalMode(value)}
                      style={{ marginTop: '3px' }}
                    />
                    <span>
                      <span style={{ fontWeight: 600, color: '#2c3e50', fontSize: '14px' }}>{label}</span>
                      <span style={{ display: 'block', fontSize: '13px', color: '#7f8c8d' }}>{help}</span>
                    </span>
                  </label>
                ))}
              </div>

              <button
                onClick={handleUpdateSettings}
                disabled={!canSaveSettings}
                style={{
                  opacity: canSaveSettings ? 1 : 0.5,
                  padding: '14px 28px',
                  background: '#c33329',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: canSaveSettings ? 'pointer' : 'not-allowed',
                  fontWeight: '600',
                  fontSize: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <ThumbsUp size={18} />
                Save Settings
              </button>
            </div>
          )}

          {/* Support Access Sub-view */}
          {settingsSubView === 'account' && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <LifeBuoy size={22} />
                Support Access
              </h2>
              <div style={{ display: 'inline-block', padding: '3px 10px', background: '#f1f5f9', color: '#475569', borderRadius: '6px', fontSize: '12px', fontWeight: '600', marginBottom: '16px' }}>
                Saves as soon as you click a choice
              </div>
              <p style={{ color: '#7f8c8d', marginBottom: '24px', fontSize: '14px', lineHeight: '1.5' }}>
                Controls whether Stuart can open your dashboard directly for support (e.g. helping you set up
                your first homeworks). By default this is allowed — you can turn it off, or limit it to a
                24-hour window, at any time below. He'll always see this account exactly as you left it, and
                every visit is logged.
              </p>

              {[
                { mode: 'always', label: 'Always allow', description: 'Stuart can open your dashboard whenever he needs to.' },
                { mode: 'temporary', label: 'Grant 24-hour access', description: 'Stuart can open your dashboard for the next 24 hours, then access turns off automatically.' },
                { mode: 'off', label: 'Off', description: 'Stuart cannot open your dashboard.' }
              ].map(({ mode, label, description }) => (
                <div
                  key={mode}
                  onClick={() => !savingSupportAccess && handleSetSupportAccess(mode)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '16px',
                    marginBottom: '12px',
                    background: supportAccess.mode === mode ? '#fff8f0' : '#f8f9fa',
                    border: supportAccess.mode === mode ? '2px solid #f39c12' : '2px solid transparent',
                    borderRadius: '8px',
                    cursor: savingSupportAccess ? 'default' : 'pointer',
                    opacity: savingSupportAccess ? 0.6 : 1
                  }}
                >
                  <div style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    border: supportAccess.mode === mode ? '5px solid #f39c12' : '2px solid #bdc3c7',
                    flexShrink: 0,
                    marginTop: '2px'
                  }} />
                  <div>
                    <div style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '2px' }}>{label}</div>
                    <div style={{ fontSize: '13px', color: '#7f8c8d' }}>{description}</div>
                  </div>
                </div>
              ))}

              <div style={{ fontSize: '13px', color: '#a0aec0', marginTop: '8px' }}>
                Current status: {
                  supportAccess.mode === 'always' ? 'Always allowed'
                  : supportAccess.mode === 'off' ? 'Off'
                  : supportAccess.expiresAt
                    ? `Temporary — expires ${(supportAccess.expiresAt.toDate ? supportAccess.expiresAt.toDate() : new Date(supportAccess.expiresAt)).toLocaleString('en-GB')}`
                    : 'Temporary'
                }
              </div>
            </div>
          )}

          {/* Branding Sub-view */}
          {settingsSubView === 'account' && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ImageIcon size={22} />
                Branding
              </h2>
              <div style={{ display: 'inline-block', padding: '3px 10px', background: '#f1f5f9', color: '#475569', borderRadius: '6px', fontSize: '12px', fontWeight: '600', marginBottom: '16px' }}>
                Logo and colour changes save straight away
              </div>
              <p style={{ color: '#7f8c8d', marginBottom: '24px', fontSize: '14px', lineHeight: '1.5' }}>
                Set a logo and header colour for each dashboard - student, marker, and head marker can each
                look different. A role without its own logo/colour below uses the shared default at the
                bottom of this page instead. Logos work best as a wide image with a transparent background
                (roughly the same proportions as a banner, e.g. 800×200px). Max 2MB.
                {isAileenOrg && ' Note: your own dashboards keep their existing logos/colours regardless of what\'s set here.'}
              </p>

              {[
                { key: 'student', label: 'Student Dashboard', defaultColor: '#ffffff' },
                { key: 'marker', label: 'Marker Dashboard', defaultColor: '#2c3e50' },
                { key: 'headMarker', label: 'Head Marker Dashboard', defaultColor: '#c33329' }
              ].map(({ key, label, defaultColor }) => {
                const roleLogoUrl = branding.logos?.[key]?.url || null;
                const effectiveLogoUrl = roleLogoUrl || branding.logoUrl || null;
                const roleColor = branding.headerColors?.[key] || defaultColor;
                return (
                  <div key={key} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                    padding: '20px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ minWidth: '160px' }}>
                      <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '2px' }}>{label}</div>
                      <div style={{ fontSize: '12px', color: '#a0aec0' }}>
                        {roleLogoUrl ? 'Own logo set' : effectiveLogoUrl ? 'Using shared default logo' : 'No logo set'}
                      </div>
                    </div>

                    <div style={{
                      width: '160px',
                      height: '80px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'white',
                      border: '2px dashed #e2e8f0',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      flexShrink: 0
                    }}>
                      {effectiveLogoUrl ? (
                        <img src={effectiveLogoUrl} alt={`${label} logo`} style={{ maxWidth: '100%', maxHeight: '100%' }} />
                      ) : (
                        <span style={{ fontSize: '12px', color: '#a0aec0' }}>No logo</span>
                      )}
                    </div>

                    <div>
                      <label style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 14px',
                        background: uploadingLogo ? '#a0aec0' : '#c33329',
                        color: 'white',
                        borderRadius: '8px',
                        cursor: uploadingLogo ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '13px'
                      }}>
                        <Upload size={14} />
                        {uploadingLogo ? 'Working...' : 'Upload'}
                        <input
                          type="file"
                          accept="image/*"
                          disabled={uploadingLogo}
                          onChange={(e) => handleUploadLogo(e.target.files?.[0], key)}
                          style={{ display: 'none' }}
                        />
                      </label>

                      {roleLogoUrl && (
                        <button
                          onClick={() => handleRemoveLogo(key)}
                          disabled={uploadingLogo}
                          style={{
                            marginLeft: '8px',
                            padding: '8px 12px',
                            background: 'none',
                            border: '2px solid #e2e8f0',
                            borderRadius: '8px',
                            cursor: uploadingLogo ? 'not-allowed' : 'pointer',
                            fontWeight: '600',
                            fontSize: '13px',
                            color: '#c53030',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <Trash2 size={13} /> Remove
                        </button>
                      )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                      <label style={{ fontSize: '13px', color: '#4a5568', fontWeight: '600' }}>
                        Header colour
                        <input
                          type="color"
                          value={roleColor}
                          onChange={(e) => handleUpdateHeaderColor(key, e.target.value)}
                          style={{ display: 'block', marginTop: '4px', width: '60px', height: '32px', border: '2px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', padding: '2px' }}
                        />
                      </label>
                      {branding.headerColors?.[key] && (
                        <button
                          onClick={() => handleUpdateHeaderColor(key, null)}
                          style={{
                            padding: '6px 10px', background: 'none', border: '2px solid #e2e8f0', borderRadius: '6px',
                            cursor: 'pointer', fontWeight: '600', fontSize: '12px', color: '#718096'
                          }}
                        >
                          Reset
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
                padding: '16px 20px',
                border: '2px dashed #e2e8f0',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <div style={{ minWidth: '160px' }}>
                  <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '2px' }}>Shared default logo</div>
                  <div style={{ fontSize: '12px', color: '#a0aec0' }}>Used by any role above with no logo of its own</div>
                </div>

                <div style={{
                  width: '160px',
                  height: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'white',
                  border: '2px dashed #e2e8f0',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  flexShrink: 0
                }}>
                  {branding.logoUrl ? (
                    <img src={branding.logoUrl} alt="Default logo" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                  ) : (
                    <span style={{ fontSize: '12px', color: '#a0aec0' }}>No custom logo</span>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 14px',
                    background: uploadingLogo ? '#a0aec0' : '#c33329',
                    color: 'white',
                    borderRadius: '8px',
                    cursor: uploadingLogo ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '13px'
                  }}>
                    <Upload size={14} />
                    {uploadingLogo ? 'Working...' : 'Upload'}
                    <input
                      type="file"
                      accept="image/*"
                      disabled={uploadingLogo}
                      onChange={(e) => handleUploadLogo(e.target.files?.[0], 'default')}
                      style={{ display: 'none' }}
                    />
                  </label>

                  {branding.logoUrl && (
                    <button
                      onClick={() => handleRemoveLogo('default')}
                      disabled={uploadingLogo}
                      style={{
                        marginLeft: '8px',
                        padding: '8px 12px',
                        background: 'none',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: uploadingLogo ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '13px',
                        color: '#c53030',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Trash2 size={13} /> Remove
                    </button>
                  )}
                </div>
              </div>

              {logoError && (
                <div style={{ padding: '10px', background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: '6px', color: '#c53030', fontSize: '13px' }}>
                  {logoError}
                </div>
              )}
            </div>
          )}

          {/* Account Sub-view - change your own password */}
          {settingsSubView === 'account' && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              maxWidth: '420px'
            }}>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', color: '#2c3e50' }}>Change Password</h3>
              <p style={{ fontSize: '13px', color: '#7f8c8d', margin: '0 0 20px 0' }}>
                Applies only to your own login ({user?.email}).
              </p>

              <form onSubmit={handleChangePassword} style={{ display: 'grid', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={passwordForm.current}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
                    New Password
                  </label>
                  <input
                    type="password"
                    value={passwordForm.next}
                    onChange={(e) => setPasswordForm({ ...passwordForm, next: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={passwordForm.confirm}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>

                {passwordMessage && (
                  <div style={{
                    padding: '10px 12px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    background: passwordMessage.type === 'error' ? '#fff5f5' : '#f0faf4',
                    border: `1px solid ${passwordMessage.type === 'error' ? '#feb2b2' : '#9ae6b4'}`,
                    color: passwordMessage.type === 'error' ? '#c53030' : '#27ae60'
                  }}>
                    {passwordMessage.text}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={changingPassword}
                  style={{
                    padding: '12px 20px',
                    background: changingPassword ? '#a0aec0' : '#c33329',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: changingPassword ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  {changingPassword ? 'Changing…' : 'Change Password'}
                </button>
              </form>
            </div>
          )}

          {/* WhatsApp Templates Sub-view */}
          {settingsSubView === 'whatsapp' && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <h2 style={{ margin: '0 0 16px 0' }}>WhatsApp Message Templates</h2>
              <p style={{ color: '#7f8c8d', marginBottom: '24px' }}>
                Customize the WhatsApp message for each homework type. Use variables like {'{{studentName}}'}, {'{{total}}'}, {'{{maxMark}}'}, etc.
              </p>

              {/* Available Variables Guide */}
              <div style={{
                background: '#e8f8f5',
                border: '2px solid #1b998b',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#1b998b' }}>
                  Available Variables:
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '8px',
                  fontSize: '13px',
                  fontFamily: 'monospace'
                }}>
                  <div style={{ gridColumn: '1/-1', fontWeight: '600', color: '#2c3e50', marginBottom: '4px' }}>Totals</div>
                  <div>{'{{total}}'} — overall total</div>
                  <div>{'{{technical}}'} — total technical</div>
                  <div>{'{{professional}}'} — total professional</div>
                  <div>{'{{maxMark}}'} — max possible</div>
                  <div style={{ gridColumn: '1/-1', fontWeight: '600', color: '#2c3e50', marginTop: '8px', marginBottom: '4px' }}>Per section (total)</div>
                  <div>{'{{sectionBreakdown}}'} — all sections</div>
                  <div>{'{{sectionA}}'}, {'{{sectionB}}'}, {'{{sectionC}}'}</div>
                  <div style={{ gridColumn: '1/-1', fontWeight: '600', color: '#2c3e50', marginTop: '8px', marginBottom: '4px' }}>Per section/group (technical)</div>
                  <div>{'{{technicalBreakdown}}'} — all sections</div>
                  <div>{'{{technical_A}}'}, {'{{technical_B}}'}, {'{{technical_C}}'}</div>
                  <div>{'{{technicalGroup1}}'}, {'{{technicalGroup2}}'}</div>
                  <div style={{ gridColumn: '1/-1', fontWeight: '600', color: '#2c3e50', marginTop: '8px', marginBottom: '4px' }}>Per section/group (professional)</div>
                  <div>{'{{professionalBreakdown}}'} — all sections or groups</div>
                  <div>{'{{professional_A}}'}, {'{{professional_B}}'}</div>
                  <div>{'{{professionalGroup1}}'}, {'{{professionalGroup2}}'}</div>
                  <div style={{ gridColumn: '1/-1', fontWeight: '600', color: '#2c3e50', marginTop: '8px', marginBottom: '4px' }}>Other</div>
                  <div>{'{{studentName}}'}</div>
                  <div>{'{{finalComment}}'}</div>
                  <div>{'{{homeworkType}}'}</div>
                  <div>{'{{homeworkName}}'}</div>
                </div>
              </div>

              {/* Templates Grid */}
              <div style={{ display: 'grid', gap: '24px', marginBottom: '24px' }}>
                {homeworkTypes.map(homeworkType => (
                  <div
                    key={homeworkType}
                    style={{
                      padding: '20px',
                      border: '2px solid #ecf0f1',
                      borderRadius: '8px',
                      background: 'white'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '12px'
                    }}>
                      <div>
                        <div style={{ fontSize: '18px', fontWeight: '700', color: '#2c3e50' }}>
                          {homeworkNames[homeworkType] || homeworkType}
                        </div>
                        <div style={{ fontSize: '13px', color: '#7f8c8d' }}>
                          {homeworkType}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          // Reset to default template
                          const defaultTemplate = `Hi {{studentName}}, your homework has been marked.

{{sectionBreakdown}}

Total: {{total}}/{{maxMark}}

{{finalComment}}`;
                          updateWhatsAppTemplate(homeworkType, defaultTemplate);
                        }}
                        style={{
                          padding: '8px 16px',
                          background: '#f39c12',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '13px'
                        }}
                      >
                        Reset to Default
                      </button>
                    </div>

                    <textarea
                      value={whatsappTemplates[homeworkType] || ''}
                      onChange={(e) => updateWhatsAppTemplate(homeworkType, e.target.value)}
                      placeholder="Enter WhatsApp message template..."
                      rows={10}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #ecf0f1',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontFamily: 'monospace',
                        resize: 'vertical',
                        lineHeight: '1.6'
                      }}
                    />

                    {/* Character Count */}
                    <div style={{
                      marginTop: '8px',
                      fontSize: '12px',
                      color: '#7f8c8d',
                      textAlign: 'right'
                    }}>
                      {(whatsappTemplates[homeworkType] || '').length} characters
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={handleUpdateSettings}
                disabled={!canSaveSettings}
                style={{
                  opacity: canSaveSettings ? 1 : 0.5,
                  padding: '16px 32px',
                  background: '#c33329',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <ThumbsUp size={18} />
                Save All Templates
              </button>
            </div>
          )}

        </div>
      )}
      {/* Manage Markers View - Item 6 */}
      {currentView === 'markers' && markersSubView === 'manage' && (
        <div>
          {/* Stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            marginBottom: '24px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              borderLeft: '4px solid #1b998b'
            }}>
              <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>
                Total Markers
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#2c3e50' }}>
                {markerStats.total || 0}
              </div>
            </div>

            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              borderLeft: '4px solid #667eea'
            }}>
              <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>
                Active Markers
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#2c3e50' }}>
                {markerStats.active || 0}
              </div>
            </div>

            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              borderLeft: '4px solid #c33329'
            }}>
              <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>
                Head Markers
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#2c3e50' }}>
                {markerStats.headMarkers || 0}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <div>
                <h2 style={{ margin: '0 0 8px 0' }}>Marker Accounts</h2>
                <p style={{ margin: 0, color: '#7f8c8d', fontSize: '14px' }}>
                  Manage marker accounts and permissions
                </p>
              </div>
              {!showAddMarkerForm && (
                <button
                  onClick={() => {
                    setShowAddMarkerForm(true);
                    loadMarkers();
                  }}
                  style={{
                    padding: '12px 24px',
                    background: '#c33329',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  + Add New Marker
                </button>
              )}
            </div>

            {/* Add Marker Form */}
            {showAddMarkerForm && (
              <form onSubmit={handleAddMarker} style={{
                marginBottom: '32px',
                padding: '24px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '2px solid #c33329'
              }}>
                <h3 style={{ margin: '0 0 16px 0' }}>Create New Marker Account</h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={newMarkerData.name}
                      onChange={(e) => setNewMarkerData({ ...newMarkerData, name: e.target.value })}
                      placeholder="John Smith"
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={newMarkerData.email}
                      onChange={(e) => setNewMarkerData({ ...newMarkerData, email: e.target.value })}
                      placeholder="john@example.com"
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Role *
                  </label>
                  <select
                    value={newMarkerData.role}
                    onChange={(e) => setNewMarkerData({ ...newMarkerData, role: e.target.value })}
                    style={{
                      width: '100%',
                      maxWidth: '50%',
                      padding: '10px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="marker">Regular Marker</option>
                    <option value="head_marker">Head Marker</option>
                  </select>
                </div>

                <div style={{
                  padding: '12px',
                  background: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#856404',
                  marginBottom: '16px'
                }}>
                  <strong>Note:</strong> An email will be sent automatically to the new marker with a secure link to set their own password.
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="submit"
                    style={{
                      padding: '12px 24px',
                      background: '#c33329',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Create Account
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddMarkerForm(false);
                      setNewMarkerData({ name: '', email: '', role: 'marker' });
                    }}
                    style={{
                      padding: '12px 24px',
                      background: '#e2e8f0',
                      color: '#4a5568',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {/* Markers List */}
            {markers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px', color: '#7f8c8d' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', display: 'flex', justifyContent: 'center' }}><Users size={48} color="#bdc3c7" /></div>
                <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                  No markers yet
                </div>
                <div>Click "Add New Marker" to create your first marker account</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '12px' }}>
                {markers.map(marker => (
                  <div
                    key={marker.id}
                    style={{
                      padding: '20px',
                      border: selectedMarker?.id === marker.id ? '3px solid #667eea' : '2px solid #e2e8f0',
                      borderRadius: '8px',
                      background: marker.active === false ? '#f8f9fa' : 'white',
                      opacity: marker.active === false ? 0.7 : 1,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => handleMarkerClick(marker)}
                    onMouseEnter={(e) => {
                      if (selectedMarker?.id !== marker.id) {
                        e.currentTarget.style.borderColor = '#667eea';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedMarker?.id !== marker.id) {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                      }
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '12px',
                          marginBottom: '8px'
                        }}>
                          <div style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>
                            {marker.name}
                          </div>
                          
                          {marker.role === 'head_marker' && (
                            <span style={{
                              padding: '4px 12px',
                              background: '#c33329',
                              color: 'white',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}>
                              HEAD MARKER
                            </span>
                          )}
                          
                          {marker.active === false && (
                            <span style={{
                              padding: '4px 12px',
                              background: '#95a5a6',
                              color: 'white',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}>
                              INACTIVE
                            </span>
                          )}

                          {selectedMarker?.id === marker.id && (
                            <span style={{
                              padding: '4px 12px',
                              background: '#667eea',
                              color: 'white',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}>
                              VIEWING STATS
                            </span>
                          )}
                        </div>
                        
                        <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '4px' }}>
                          {marker.email}
                        </div>
                        
                        <div style={{ fontSize: '13px', color: '#95a5a6' }}>
                          Created: {marker.createdAt?.toDate?.().toLocaleDateString('en-GB') || 'N/A'}
                        </div>

                        {/* Show homework counts when marker is selected */}
                        {selectedMarker?.id === marker.id && Object.keys(individualMarkerStats).length > 0 && (
                          <div style={{
                            marginTop: '16px',
                            padding: '16px',
                            background: '#f0f4ff',
                            borderRadius: '8px',
                            border: '2px solid #667eea'
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '12px'
                            }}>
                              <h4 style={{ margin: 0, color: '#667eea', fontSize: '16px' }}>
                                Homework Counts (Head Marker View)
                              </h4>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRefreshMarkerStats();
                                  }}
                                  style={{
                                    padding: '6px 12px',
                                    background: '#667eea',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '12px'
                                  }}
                                >
                                  Refresh
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleResetHeadMarkerView();
                                  }}
                                  style={{
                                    padding: '6px 12px',
                                    background: '#e74c3c',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '12px'
                                  }}
                                >
                                  Reset My View
                                </button>
                              </div>
                            </div>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(4, 1fr)',
                              gap: '8px'
                            }}>
                              {(homeworkTypes.length ? homeworkTypes : ['TH1', 'TH2', 'TH3', 'TH4', 'RH1', 'RH2', 'RH3', 'RH4']).map(type => (
                                <div
                                  key={type}
                                  style={{
                                    padding: '8px',
                                    background: 'white',
                                    borderRadius: '6px',
                                    textAlign: 'center',
                                    border: '1px solid #e2e8f0'
                                  }}
                                >
                                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '4px' }}>
                                    {homeworkNames[type] || type}
                                  </div>
                                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#667eea' }}>
                                    {individualMarkerStats[type] || 0}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div style={{
                              marginTop: '12px',
                              padding: '8px',
                              background: 'white',
                              borderRadius: '6px',
                              textAlign: 'center',
                              border: '2px solid #667eea'
                            }}>
                              <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '4px' }}>
                                TOTAL MARKED
                              </div>
                              <div style={{ fontSize: '24px', fontWeight: '700', color: '#667eea' }}>
                                {Object.values(individualMarkerStats).reduce((sum, count) => {
                                  return sum + (typeof count === 'number' ? count : 0);
                                }, 0)}
                              </div>
                            </div>

                            {/* Marked scripts per cohort - counted from the submissions, so resets don't affect it */}
                            <div style={{ marginTop: '16px' }}>
                              <h4 style={{ margin: '0 0 8px 0', color: '#667eea', fontSize: '16px' }}>
                                Marked by Cohort
                              </h4>
                              {markerCohortCounts === null ? (
                                <div style={{ fontSize: '13px', color: '#7f8c8d' }}>Loading...</div>
                              ) : markerCohortCounts.length === 0 ? (
                                <div style={{ fontSize: '13px', color: '#7f8c8d' }}>No marked scripts yet.</div>
                              ) : (() => {
                                const usedTypes = new Set(markerCohortCounts.flatMap(row => Object.keys(row.counts)));
                                const columns = [
                                  ...homeworkTypes.filter(type => usedTypes.has(type)),
                                  ...[...usedTypes].filter(type => !homeworkTypes.includes(type))
                                ];
                                const cellStyle = { padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
                                return (
                                  <div style={{ overflowX: 'auto', background: 'white', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                      <thead>
                                        <tr style={{ background: '#f8f9fa', color: '#7f8c8d', fontSize: '12px' }}>
                                          <th style={{ ...cellStyle, textAlign: 'left', fontWeight: '600' }}>Cohort</th>
                                          {columns.map(type => (
                                            <th key={type} style={{ ...cellStyle, fontWeight: '600' }}>{homeworkNames[type] || type}</th>
                                          ))}
                                          <th style={{ ...cellStyle, fontWeight: '700', color: '#667eea' }}>Total</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {markerCohortCounts.map(row => (
                                          <tr key={row.cohortId}>
                                            <td style={{ ...cellStyle, textAlign: 'left', fontWeight: '600', color: '#2c3e50' }}>
                                              {cohorts.find(c => c.id === row.cohortId)?.name || row.cohortName || 'Unknown cohort'}
                                            </td>
                                            {columns.map(type => (
                                              <td key={type} style={{ ...cellStyle, color: row.counts[type] ? '#2c3e50' : '#cbd5e0' }}>
                                                {row.counts[type] || 0}
                                              </td>
                                            ))}
                                            <td style={{ ...cellStyle, fontWeight: '700', color: '#667eea' }}>{row.total}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                );
                              })()}
                              <div style={{ fontSize: '11px', color: '#7f8c8d', marginTop: '6px' }}>
                                Counted from the scripts themselves, so resetting the counts above doesn't change this table.
                              </div>
                            </div>

                            {/* Audit Log - Shows all resets */}
                            {individualMarkerStats.auditLog && individualMarkerStats.auditLog.length > 0 && (
                              <div style={{
                                marginTop: '16px',
                                padding: '12px',
                                background: '#fff9e6',
                                borderRadius: '6px',
                                border: '2px solid #f39c12'
                              }}>
                                <h5 style={{ margin: '0 0 12px 0', color: '#f39c12', fontSize: '14px', fontWeight: '600' }}>
                                  Reset History (Audit Log)
                                </h5>
                                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                  {individualMarkerStats.auditLog.map((entry, idx) => {
                                    const total = Object.values(entry).reduce((sum, value) => sum + (typeof value === 'number' ? value : 0), 0);
                                    const resetDate = entry.resetAt?.toDate?.();
                                    const resetBy = entry.type === 'marker_reset' ? 'Marker' : 'Head Marker';
                                    
                                    return (
                                      <div
                                        key={idx}
                                        style={{
                                          padding: '8px',
                                          background: 'white',
                                          borderRadius: '4px',
                                          marginBottom: '8px',
                                          fontSize: '12px',
                                          border: '1px solid #e2e8f0'
                                        }}
                                      >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                          <span style={{ fontWeight: '600', color: entry.type === 'marker_reset' ? '#1b998b' : '#c33329' }}>
                                            {resetBy} Reset
                                          </span>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ color: '#7f8c8d' }}>
                                              {resetDate ? resetDate.toLocaleDateString('en-GB') : 'N/A'}
                                            </span>
                                            <button
                                              onClick={() => handleDeleteResetLogEntry(entry.id)}
                                              title="Delete this entry"
                                              style={{
                                                background: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: '#e74c3c',
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '2px'
                                              }}
                                            >
                                              <Trash2 size={13} />
                                            </button>
                                          </div>
                                        </div>
                                        <div style={{ color: '#4a5568' }}>
                                          <strong>Total at reset:</strong> {total} homeworks
                                        </div>
                                        <div style={{ color: '#7f8c8d', fontSize: '11px', marginTop: '4px' }}>
                                          {(homeworkTypes.length ? homeworkTypes : ['TH1', 'TH2', 'TH3', 'TH4', 'RH1', 'RH2', 'RH3', 'RH4'])
                                            .map(type => `${homeworkNames[type] || type}:${entry[type] || 0}`)
                                            .join('  ')}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                <div style={{
                                  marginTop: '8px',
                                  padding: '8px',
                                  background: 'white',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  color: '#7f8c8d',
                                  border: '1px solid #e2e8f0'
                                }}>
                                  <strong>Note:</strong> This permanent log shows all resets (both marker and head marker). 
                                  When markers reset their own view, your view remains unchanged.
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSendPasswordReset(marker);
                          }}
                          style={{
                            padding: '8px 16px',
                            background: '#667eea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '14px'
                          }}
                          title="Send password reset email"
                        >
                          Reset Password
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleMarkerStatus(marker);
                          }}
                          style={{
                            padding: '8px 16px',
                            background: marker.active === false ? '#1b998b' : '#f39c12',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '14px'
                          }}
                        >
                          {marker.active === false ? 'Activate' : 'Deactivate'}
                        </button>

                        {marker.active === false && marker.role === 'marker' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteMarker(marker);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '8px 16px',
                              background: '#e74c3c',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '14px'
                            }}
                            title="Permanently delete this marker"
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sync View */}


      {/* Add Homework Modal */}
      <AddHomeworkModal
        show={showAddHomeworkModal}
        onClose={() => {
          setShowAddHomeworkModal(false);
          setNewHomeworkCode('');
        }}
        newHomeworkCode={newHomeworkCode}
        setNewHomeworkCode={setNewHomeworkCode}
        onAdd={handleAddHomework}
      />

      {/* Flag for Review Modal (the head marker flagging their own claimed submission) */}
      <FlagForReviewModal
        submissionId={showFlagModal}
        onClose={() => { setShowFlagModal(null); setFlagComment(''); }}
        flagComment={flagComment}
        setFlagComment={setFlagComment}
        onFlag={handleFlagFromClaimed}
        headMarkerName={user?.name || 'the head marker'}
      />

      {/* Change Homework Type Modal */}
      <ChangeHomeworkTypeModal
        submission={showChangeTypeModal}
        homeworkNames={homeworkNames}
        onClose={() => setShowChangeTypeModal(null)}
        onConfirm={handleChangeHomeworkType}
      />

      {/* Reject Submission Modal */}
      <RejectSubmissionModal
        submissionId={showRejectModal}
        onClose={() => setShowRejectModal(null)}
        rejectionNotes={rejectionNotes}
        setRejectionNotes={setRejectionNotes}
        onReject={handleReject}
      />

      {/* Upload Submission on Behalf of Student Modal */}
      <UploadOnBehalfModal
        show={showUploadOnBehalfModal}
        onClose={() => setShowUploadOnBehalfModal(false)}
        cohorts={cohorts}
        homeworkTypes={homeworkTypes}
        homeworkNames={homeworkNames}
        onUploaded={loadData}
      />

    </div>
  );
}

export default HeadMarkerDashboard;